import {
  Alumni,
  AlumniWithoutVideo,
  EventTypeEnum,
  FAQType,
} from "@/interface/types";
import { type ClassValue, clsx } from "clsx";
import qs from "qs";
import { twMerge } from "tailwind-merge";
// import { boolean } from "zod";
import { parseISO, format, isValid, parse } from "date-fns";
import { LevelSchedule } from "@/types/caira";
import {
  getEventCpeContentFromCms,
  getNasbaRows,
} from "@/lib/eventsCpeContent";
import { parseDisplayDateTime } from "@/data/webinarSchedule";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/* eslint-disable no-unused-vars */

export function debounceHandler<T extends (...args: any[]) => void>(
  func: T,
  delay: number,
) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

/* eslint-enable no-unused-vars */

export function getSessionData(callback: any) {
  // Use your own ID here ↓
  const pattern = /_ga_4NS9SVSY3Z=GS\d\.\d\.(.+?)(?:;|$)/;
  const match = document.cookie.match(pattern);
  const parts = match?.[1].split(".");

  if (!parts) {
    // Cookie not yet available; wait a bit and try again.
    window.setTimeout(() => getSessionData(callback), 200);
    return;
  }

  callback({
    ga_session_id: parts.shift(),
    ga_session_number: parts.shift(),
  });
}

export function get_ga_clientid() {
  try {
    const cookies = document.cookie
      .split(";")
      .map((cookie) => cookie.trim().split("="))
      .reduce((acc: any, [key, value]) => {
        acc[key] = decodeURIComponent(value || "");
        return acc;
      }, {});
    const gaCookie = cookies["_ga"];

    if (!gaCookie || !gaCookie.startsWith("GA")) {
      console.warn("The _ga cookie is missing or malformed.");
      return null;
    }

    return gaCookie.substring(6);
  } catch (error) {
    console.error("Error retrieving _ga Client ID:", error);
    return null;
  }
}

export async function insertLeadsDumpData(
  data: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch(
      "https://one.mileseducation.us/functions/v1/create-lead",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*", // Allow CORS
          mode: "no-cors",
        },
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      // If the response is not ok, throw an error with the status text
      throw new Error(response.statusText);
    }

    const responseData = await response.json();
    return { data: responseData }; // Return data if successful
  } catch (error) {
    return { error: error instanceof Error ? error : new Error(String(error)) }; // Return error if there's an issue
  }
}

export async function insertConclaveData(
  data: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch(
      "https://mfx-prod-154625965269.asia-south1.run.app/website-forms/large-event-registration",
      // "http://localhost:3000/website-forms/large-event-registration",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          mode: "no-cors",
        },
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    const responseData = await response.json();
    return { data: responseData };
  } catch (error) {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }
}

export async function selectLeadsDumpData(uuid: string) {
  const response = await fetch(
    `https://webhook.miles-api.com/webhook/7d21526b-8f3e-4515-ac7b-f650b8c7f3e4/?uuid=${uuid}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}
type RouteMessages = {
  [key: string]: string;
};

export const padWithZero = (num: number): string => {
  return num.toString().padStart(2, "0");
};

export const parseTimeString = (timeString: string): [number, number] => {
  const timeRegex = /^(\d+)(?::(\d{2}))?\s*(AM|PM)$/i;
  const match = timeString.match(timeRegex);

  if (!match) {
    throw new Error(`Invalid time format: "${timeString}"`);
  }

  let [, hour, minute = "0", period] = match;
  let hours = parseInt(hour, 10);
  const minutes = parseInt(minute, 10);

  if (period.toUpperCase() === "PM" && hours < 12) {
    hours += 12;
  } else if (period.toUpperCase() === "AM" && hours === 12) {
    hours = 0;
  }

  return [hours, minutes];
};

export const GREEN_TICK_STATEMENTS = [
  "Building the CPA/CMA talent pool in India",
  "Creating pathways for Indian Accountants to work in the US",
  "Bridging the 150 credit barrier for CPA candidates in the US",
];

export const ACCOUNTING_NUMERIC_DATA_WITH_CONTENT = [
  {
    contentTitle: "70,000+",
    contentSubTitle: "Global Alumni and Counting",
  },
  {
    contentTitle: "600+",
    contentSubTitle: "MNCS & Corporate Partnerships",
  },
  {
    contentTitle: "100+",
    contentSubTitle: "Universities in the US & India",
  },
];

export const EXPANDING_CARD_CONTENT_LIST_DATA_ACCOUNTING_PAGE = [
  {
    cardUrl: "/us-jobs-for-accountants",
    cardImgUrl: "/images/accounting/sample2.png",
    cardImgAlt: "miles us pathway- make your american dream reality",
    cardTextTitle: "Miles US Pathway",
    cardListContent: [
      "Study STEM-designated MS accounting from top US schools",
      "Secure 3-year work opportunity of work in the US",
      "Secure placement at top public accounting firms in the US",
    ],
  },
  {
    cardUrl: "/cpa",
    cardImgUrl: "/images/accounting/sample3.png",
    cardImgAlt: "80% of us cpa's in india are miles alumni",
    cardTextTitle: "Miles US CPA",
    cardListContent: [
      "Earn your CPA License",
      "Get placement opportunities in the leading Big 4 and MNCs in India",
      "Explore pathway to work in the US",
    ],
  },
  {
    cardUrl: "/cma",
    cardImgUrl: "/images/accounting/sample1.png",
    cardImgAlt: "miles us cma- earn globally recognizied certifications",
    cardTextTitle: "Miles US CMA",
    cardListContent: [
      "Get placed at one of the leading MNCs in India",
      "Explore pathways to work in the US",
      "Only 2-4 exams, pass in 12-18 months",
    ],
  },
];

export const CARD_DETAILS_ACCOUNTING_PAGE_PATHWAY_TO_SUCCESS = [
  {
    sectionId: "section1",
    sectionHrefUrl: "/us-jobs-for-accountants",
    sectionTitleName: "Miles US Pathway",
    sectionCardImgUrl: "/images/accounting/miles_us_pathway.png",
    sectionCardImgAlt: "solving the accounting shortage in us through miles",
    sectionSubTitleContent: "Solving the Accountant Shortage in the US",
    sectionListContent: [
      "Study STEM-designated MS accounting from top US schools",
      "Secure 3 years of work opportunity in the US",
      "Secure placement at top public accounting firms in the US",
    ],
  },
  {
    sectionId: "section2",
    sectionHrefUrl: "/cpa",
    sectionTitleName: "US CPA",
    sectionCardImgUrl: "/images/accounting/us_cpa_card.png",
    sectionCardImgAlt: "us cpa",
    sectionSubTitleContent:
      "Did you know that 80% of CPAs in India are Miles alumni?",
    sectionListContent: [
      "Earn your CPA License",
      "Get placement opportunities in the leading MNCs in India",
      "Explore pathway to work in the US",
    ],
  },
  {
    sectionId: "section3",
    sectionHrefUrl: "/cma",
    sectionTitleName: "US CMA",
    sectionCardImgUrl: "/images/accounting/us_cma.png",
    sectionCardImgAlt: "calculator and accounting notes",
    sectionSubTitleContent:
      "Your career, your way - practice accounting in India or the US",
    sectionListContent: [
      "Earn your US CMA + AICPA Tax Certificate",
      "Get placed at one of the Big 4s & MNCs in India",
      "Explore pathways to work at the top public accounting firms in the US",
    ],
  },
];

const ACCOUNTING_MESSAGES: RouteMessages = {
  "us-pathway": "I am interested in the Miles US Pathway.",
  "us-cpa": "I'm interested in the US CPA program.",
  "us-cma": "I'm interested in the US CMA program.",
  "us-cpa/credits": "I'm interested in the Miles CPA Credits program.",
  "enrolled-agent": "I'm interested in the US EA program.",
  masterclass: "I'm interested in the MasterClass program.",
};

const BUSINESS_MESSAGES: RouteMessages = {
  "executive-program-in-strategic-finance":
    "I'm interested in the IIM Lucknow - Strategic Finance program.",
  "post-graduate-certificate-programme-in-branding-and-advertising":
    "I'm interested in the IIM Lucknow - Investing Banking",
};

const FINANCE_MESSAGES: RouteMessages = {
  cfa: "I'm interested in the CFA charter.",
  frm: "I'm interested in the FRM program.",
};

const DEFAULT_MESSAGE = "I am interested in the Miles US Pathway.";

export const generateMessageFromRoute = (route: string): string => {
  const pathParts = route.split("/");
  const isLandingPageVisitOfAccountingSubCategory =
    route.includes("lp") && route.includes("visit");
  const isVisitOnlyOfAccountingSubCategory =
    !route.includes("lp") && route.includes("visit");
  const category = isLandingPageVisitOfAccountingSubCategory
    ? pathParts[2]
    : pathParts[1]; // accounting, business, etc.

  // Join remaining parts as mainRoute
  const mainRoute = isLandingPageVisitOfAccountingSubCategory
    ? pathParts.slice(3, 4).join("")
    : isVisitOnlyOfAccountingSubCategory
      ? pathParts.slice(2, 3).join("/")
      : pathParts.slice(2).join("/");

  let msg = "Hi Miles Team! ";

  if (category === "accounting") {
    msg += ACCOUNTING_MESSAGES[mainRoute] || DEFAULT_MESSAGE;
  } else if (category === "business") {
    msg += BUSINESS_MESSAGES[mainRoute] || DEFAULT_MESSAGE;
  } else if (category === "finance") {
    msg += FINANCE_MESSAGES[mainRoute] || DEFAULT_MESSAGE;
  } else {
    msg += DEFAULT_MESSAGE;
  }

  return msg;
};

export const cities = [
  {
    id: 1,
    name: "Choose City",
    avatar: "",
  },
  {
    id: 2,
    name: "Ahmedabad",
    avatar: "/newImages/MilesEducationWebsite-2024/cities/Ahmedabad.webp",
    meeting_event: "ahmedabad",
    address:
      "704 Satyaone Complex Near Helmet Cross Road Memnagar Ahmedabad - 380052",
  },
  {
    id: 3,
    name: "Bangalore",
    avatar: "/newImages/MilesEducationWebsite-2024/cities/Bangalore.webp",
    meeting_event: "bangalore",
    address: "2nd Floor Mantri Avenue Koramangala 8th Block Bangalore - 560047",
  },
  {
    id: 4,
    name: "Delhi",
    avatar: "/newImages/MilesEducationWebsite-2024/cities/Delhi.webp",
    meeting_event: "delhi",
    address:
      "3rd and 4th Floor Old Fort Building Near DLF Mall Main Road Saket New Delhi, Delhi 110017",
  },
  {
    id: 5,
    name: "Hyderabad",
    avatar: "/newImages/MilesEducationWebsite-2024/cities/Hyderabad.webp",
    meeting_event: "hyderabad",
    address:
      "Level - 1 Jayabheri Silicon Towers, Hitech City Road Kothaguda Hyderabad - 500084",
  },
  {
    id: 6,
    name: "Kochi",
    avatar: "/newImages/MilesEducationWebsite-2024/cities/Kochi.webp",
    meeting_event: "kochi",
    address:
      "11th Floor Muthoottu Royal tower 36 Banerji Road Kaloor Ernakulam  Kerala 682017",
  },
  {
    id: 7,
    name: "Kolkata",
    avatar: "/newImages/MilesEducationWebsite-2024/cities/Kolkata.webp",
    meeting_event: "kolkata",
    address:
      "Apeejay House Unit No. C/4/1  4th Floor Block C 15 Park Street Kolkata  West Bengal 700016",
  },
  {
    id: 11,
    name: "Chennai",
    avatar: "/newImages/MilesEducationWebsite-2024/cities/Kolkata.webp",
    meeting_event: "chennai",
    address: "4 Rajiv Gandhi Salai India TIDEL PARK Chennai Tamil Nadu 600113",
  },
  {
    id: 8,
    name: "Mumbai",
    avatar: "/newImages/MilesEducationWebsite-2024/cities/Mumbai.webp",
    meeting_event: "mumbai",
    address:
      "911/912  Wing A Kanakia Wall Street Andheri Kurla Road Mumbai - 400093",
  },
  {
    id: 9,
    name: "Pune",
    avatar: "/newImages/MilesEducationWebsite-2024/cities/Pune.webp",
    meeting_event: "pune",
    address:
      "Manikchand Ikon A Wing  behind Sheraton Grand opposite to Wadiya college Sangamvadi Pune Maharashtra 411001",
  },
  {
    id: 10,
    name: "Virtual Meeting",
    avatar: "/newImages/MilesEducationWebsite-2024/cities/Virtual_Meeting.webp",
    meeting_event: "virtual-meeting",
  },
];

export const initialAttend = {
  loader: true,
  eligible: false,
  show: false,
  value: null,
  data: [],
};

// Function to get today's date in the format YYYY-MM-DD
export const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// TailwindCSS Styling for cl1
export const stylingForCL1QuickConnect = {
  mainDiv: "test",
  title: "text-lg font-semibold text-white",
  childDiv: "lg:w-full",
  name: "appearance-none block text-sm w-full bg-slate-200 text-gray-700 h-9 border-red-600 rounded-full py-2 px-3 mb-3 leading-tight focus:outline-none",
  iconP: "h-5 w-5 flex-shrink-0 rounded-full mr-3",
  phone:
    "appearance-none block text-sm w-full bg-slate-200 text-gray-700 h-9 border border-red-600 rounded-full py-2 px-3 mb-3 leading-tight focus:outline-none",
  location:
    "appearance-none block text-sm w-full bg-slate-200 text-gray-700 h-9 border border-red-600 rounded-full py-2 px-3 mb-3 leading-tight focus:outline-none",
  course:
    "appearance-none block text-sm w-full bg-slate-200 text-gray-700 h-9 border border-red-600 rounded-full py-2 px-3 mb-3 leading-tight focus:outline-none select_box",
  email:
    "appearance-none block text-sm w-full bg-slate-200 text-gray-700 h-9 border border-red-600 rounded-full py-2 px-3 mb-3 leading-tight focus:outline-none",
  submitBtn:
    "block mx-auto shadow bg-[#B41416] hover:bg-[#b41417ee] focus:shadow-outline focus:outline-none text-white font-semibold py-2.5 px-8 rounded-full text-sm border border-white-600 mt-4 w-2/3",
  choosePath: {
    titleTop: "mb-2 text-sm text-white text-left",
    titleMiddle: "mb-2 text-white text-left",
    options: "pl-0",
  },
  whatsapp: {
    input:
      "relative float-left mr-[6px] mt-[0rem] h-[1.5rem] w-[1.5rem] appearance-none rounded-full border-[1px] border-solid border-white before:pointer-events-none before:absolute before:h-[1.4rem] before:w-[1.4rem] before:scale-0 before:rounded-full before:bg-transparent before:opacity-0 before:shadow-[0px_0px_0px_13px_transparent] before:content-[''] checked:border-white checked:border-[1px] checked:bg-transparent checked:before:opacity-[0.16] checked:after:absolute checked:after:mt-[2.6px] checked:after:ml-[0.52rem] checked:after:block checked:after:h-[0.8125rem] checked:after:w-[0.35rem] checked:after:rotate-45 checked:after:border-[0.1rem] checked:after:border-l-0 checked:after:border-t-0 checked:after:border-solid checked:after:border-white checked:after:bg-transparent checked:after:content-[''] hover:cursor-pointer hover:before:opacity-[0.04] hover:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)] focus:shadow-none focus:transition-[border-color_0.2s] focus:before:scale-100 focus:before:opacity-[0.12] focus:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)] focus:before:transition-[box-shadow_0.2s,transform_0.2s] focus:after:absolute focus:after:block focus:after:h-[0.875rem] focus:after:w-[0.875rem] focus:after:rounded-[0.125rem] focus:after:content-[''] checked:focus:before:scale-100 checked:focus:before:shadow-[0px_0px_0px_13px_#3b71ca] checked:focus:before:transition-[box-shadow_0.2s,transform_0.2s] checked:focus:after:mt-[2.6px] checked:focus:after:ml-[0.52rem] checked:focus:after:h-[0.8125rem] checked:focus:after:w-[0.36rem] checked:focus:after:rotate-45 checked:focus:after:border-[0.1rem] checked:focus:after:border-l-0 checked:focus:after:border-t-0 checked:focus:after:border-solid checked:focus:after:border-white checked:focus:after:bg-transparent dark:border-neutral-600 dark:checked:border-primary dark:checked:bg-primary dark:focus:before:shadow-[0px_0px_0px_13px_rgba(255,255,255,0.4)] dark:checked:focus:before:shadow-[0px_0px_0px_13px_#3b71ca]",
    icon: "dark:invert duration-300 mx-1",
    label:
      "hover:cursor-pointer text-white lg:text-xs text-[12px] flex items-center",
    all: "mt-5 flex justify-left items-center",
  },
  newsletter: {
    text: "text-sm text-white mt-5",
  },
  success: {
    conatiner: "w-full p-4 m-3 bg-green-800",
    msg: "text-white text-sm",
    greet: "indent-0",
  },
};

export const GlobalRankingsCategoryItems = {
  categoryItem1: "Top 100",
  categoryItem2: "",
  categoryItem3: "300+",
};

export const USRankingsCategoryItems = {
  categoryItem1: "-",
  categoryItem2: "-",
  categoryItem3: "-",
};

export const FeesCategoryItems = {
  categoryItem1: "~$40,000 (₹34,65,618)",
  categoryItem2: "~$28,000 (₹24,25,933)",
  categoryItem3: "$28,000 (₹24,25,933)",
};

export const scheduleData: LevelSchedule[] = [
  {
    level: "I",
    title: "CAIRA Level 1",
    sessions: [
      // {
      //   date: "Jun 21",
      //   time: "7:00 PM - 9:00 PM",
      //   title: "AI in Accounting 101",
      //   credits: "2 CPE Credits",
      //   icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/badges/ai-in-accounting.webp",
      // },
      // {
      //   date: "Jul 11",
      //   time: "7:00 PM - 9:00 PM",
      //   title: "Copilot for Excel",
      //   credits: "2 CPE Credits",
      //   icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/badges/copilot-for-accountants.webp",
      // },
      // {
      //   date: "Jul 18",
      //   time: "7:00 PM - 9:00 PM",
      //   title: "Copilot for Word, PowerPoint, Outlook and Teams",
      //   credits: "2 CPE Credits",
      // },
      // {
      //   date: "Jul 25",
      //   time: "7:00 PM - 9:00 PM",
      //   title: "Copilot Studio: Building AI Agents",
      //   credits: "2 CPE Credits",
      //   icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/badges/agentic-ai.webp",
      // },
      // {
      //   date: "Aug 1",
      //   time: "7:00 PM - 9:00 PM",
      //   title: "Copilot Studio: Advanced Agent Design",
      //   credits: "2 CPE Credits",
      // },
      {
        date: "31st Oct",
        time: "",
        title: "AI in Accounting 101: Build your first AI Agent",
        credits: "2 CPE Credits",
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/badges/ai-in-accounting.webp",
      },
      {
        date: "7th Nov",
        time: "",
        title: "Copilot for Excel: Data Analysis and Insights",
        credits: "2 CPE Credits",
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/badges/copilot-for-accountants.webp",
      },
      {
        date: "14th Nov",
        time: "",
        title: "Copilot for Word, PowerPoint, Outlook and Teams",
        credits: "2 CPE Credits",
      },
      {
        date: "21st Nov",
        time: "",
        title: "Copilot Studio: Build & Deploy AI Agents",
        credits: "2 CPE Credits",
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/badges/agentic-ai.webp",
      },
      {
        date: "28th Nov",
        time: "",
        title: "Copilot Studio: Advanced Agent Design",
        credits: "2 CPE Credits",
      },
      {
        date: "5th Dec",
        time: "",
        title: "Getting Started with Microsoft Power Automate",
        credits: "2 CPE Credits",
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/badges/Workflow%20Automation.webp",
      },
      {
        date: "12th Dec",
        time: "",
        title: "Power Automate for Accounting Workflows",
        credits: "2 CPE Credits",
      },
      {
        date: "19th Dec",
        time: "",
        title: "Business Intelligence & Dashboards with Power BI",
        credits: "2 CPE Credits",
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/badges/power-bi-for-accountants.webp",
      },
      {
        date: "26th Dec",
        time: "",
        title: "Visual Analytics & Insights with Power BI",
        credits: "2 CPE Credits",
      },
    ],
  },
  {
    level: "II",
    title: "CAIRA Level 2",
    sessions: [
      {
        date: "9th Jan",
        time: "",
        title: "Applied AI in Audit",
        credits: "2 CPE Credits",
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/badges/ai-enabled-auditing.webp",
      },
      {
        date: "16th Jan",
        time: "",
        title: "Applied AI in Tax",
        credits: "2 CPE Credits",
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/badges/ai-powered-tax.webp",
      },
      {
        date: "23rd Jan",
        time: "",
        title: "Applied AI for CFO Teams",
        credits: "2 CPE Credits",
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/badges/ai-for-cfo-team.webp",
      },
    ],
  },
  {
    level: "III",
    title: "CAIRA Level 3",
    sessions: [
      {
        date: "6th Feb",
        time: "",
        title: "Digital Transformation for CPA Firms",
        credits: "2 CPE Credits",
      },
      {
        date: "13th Feb",
        time: "",
        title: "Digital Transformation for CFO Teams",
        credits: "2 CPE Credits",
      },
    ],
  },
];
export const sanitizeBlogBannerImageUrl = (
  url?: string | null,
): string | null => {
  if (!url) return null;

  const cleaned = url.trim().replace(/^['"]+|['"]+$/g, "");
  const storagePrefix = "https://storage.googleapis.com/miles-blog/";

  if (cleaned.startsWith(storagePrefix)) {
    const stripped = cleaned.slice(storagePrefix.length);
    if (stripped.startsWith("http://") || stripped.startsWith("https://")) {
      return stripped;
    }
  }

  return cleaned;
};
export const level1 = [
  {
    name: "Microsoft Copilot | Dawn of the Intelligent System",
    youtubeVideo: "kRB9TMP9RBc",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/COPILOT_CHRIS_STEGH_vertical.webp",
  },
  {
    name: "Power Automation | Engineering the Invisible workforce",
    youtubeVideo: "KgrAqIQfzAU",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/POWER%20AUTOMATE%20-%20GRIFFIN%20LICKFELDT%20vertical%20(1).webp",
  },
  {
    name: "Building a Future Ready AI Toolkit",
    youtubeVideo: "EItrhAmOTLE",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/BUILDING%20A%20FUTURE%20READY%20AI%20TOOLKIT%20vertical.webp",
  },
  {
    name: "The Accountant as Architect of Intelligence",
    youtubeVideo: "gJpGwyEhqxQ",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/The%20Accountant%20as%20Architect%20of%20Intelligence%20-%20Vertical%20.webp",
  },
  {
    name: "AI: It's All about the prompt",
    youtubeVideo: "5uJPB2wo1S0",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/AI-The%20Art%20of%20The%20Prompt%20with%20Scott%20Dell-%20vertical%20(1).webp",
  },
  {
    name: "Power BI for Finance",
    youtubeVideo: "XtXNnlnRkZA",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/POWER%20BI%20FOR%20FINANCE%20CRISTIANO%20GALVAO%20vertical%20v2.webp",
  },
  {
    name: "AI for Accountants Friend or Foe",
    youtubeVideo: "DtIb56Mdp_g",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/AI%20for%20Accountants_%20Friend%20or%20Foe%20with%20Dr.%20Scott%20Dell%20vertical.webp",
  },
  {
    name: "Next-Gen Accounting",
    youtubeVideo: "kvslxB6leBk",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Next-Gen%20Accounting-%20AI%20Workflows%20&%20Agents%20with%20Alex%20LEE%20Square-Vertical%20(4).webp",
  },
  {
    name: "Advanced Excel Masterclass",
    youtubeVideo: "S1mUTIFUH24",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/ADVANCED%20EXCEL%20MASTERCLASS%20-%20CRISTIANO%20GALVAO%20vertical.webp",
  },
  {
    name: "Microsoft copilot : A beginner's Pathway",
    youtubeVideo: "Yo_LM1CeLyg",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/COPILOT%20-%20Teresa%20Cyrus%20Vertical%20(2).webp",
  },
  {
    name: "Reclaim 10 Hours a Week using ChatGPT",
    youtubeVideo: "LgKROuUo1oE",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/reclaim%2010%20hours%20a%20week%20using%20Chatgpt%20Vertical%20(1).webp",
  },
  {
    name: "Transform Data Into Action with Power BI",
    youtubeVideo: "SfYVt8orr3M",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Transform%20Data%20into%20Action%20with%20Power%20BI%20with%20Mfon%20Akpan%20vertical.webp",
  },
  {
    name: "Data Analytics for Better Decisions",
    youtubeVideo: "ZA6lsejwcAo",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Data%20Analytics%20for%20Better%20Decisions%20with%20Joe%20Oringel-vertical.webp",
  },
  {
    name: "AI Agents the New Electricity",
    youtubeVideo: "DUBp-QU-0Oc",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/AI%20Agents%20in%20Accounting%20_%20The%20New%20Electricity%20with%20Octavio%20_OJ_%20Laos-Vertical.webp",
  },
];
export const level2 = [
  {
    name: "Power Automate Endgame for manual finance",
    youtubeVideo: "KgrAqIQfzAU",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/POWER%20AUTOMATE%20ENDGAME%20-%20GRIFFIN%20LICKFELDT%20vertical%20(1).webp",
  },
  {
    name: "Audit Breakthrough with Copilot",
    youtubeVideo: "3fc1n0Y3BU4",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Vertical%20-%20The%20Audit%20Breakthrough%20with%20Copilot.webp",
  },
  {
    name: "Build to Digitize",
    youtubeVideo: "tKt5Kqxo2VM",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Built%20to%20Digitize%20with%20Jeff%20Seibert-Vertical%20(1)%20(2).webp",
  },
  {
    name: "Supercharge your practice",
    youtubeVideo: "_k5PeOuORxY",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/SUPERCHARGE%20PRACTICE%20ian%20vacine%20vertical.webp",
  },
  {
    name: "Audit Beyond Spreadsheets",
    youtubeVideo: "tSBj6Ipuhns",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/AUDIT%20BEYOND%20SPREADSHEET%20vertical.webp",
  },
  {
    name: "Automated the Close",
    youtubeVideo: "L9jfm-Sn_zY",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/AUTOMATE%20THE%20CLOSE%20-%20ALEX%20LEE%20vertical.webp",
  },
  {
    name: "AI Meets ERP",
    youtubeVideo: "3MTQIRfFen0",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/AI%20MEETS%20ERP%20vertical.webp",
  },
  {
    name: "AI-Empowered auditing",
    youtubeVideo: "DLuVIO1sc_E",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/AI-Empowered%20Auditing%20with%20Raymond-Vertical-720%20x%201280%20(1).webp",
  },
  {
    name: "Investigating Fraud",
    youtubeVideo: "NRQS8s9uwns",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Investigating%20Fraud%20with%20Data%20Models%20with%20Robert%20Persichitte-1080x1080-Vertical-720%20x%201280.webp",
  },
  {
    name: "File to win: AI's Power Boost for Tax",
    youtubeVideo: "tivuVuOXmko",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/FILED%20TO%20WIN-%20LK%20and%20RD%20vertical.webp",
  },
  {
    name: "AI Tax Specialist  part1",
    youtubeVideo: "O8TAy4jirF0",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/AI%20TAX%20SPECIALIST%20V1%20vertical%20(1).webp",
  },
  {
    name: "AI Tax Specialist  part2",
    youtubeVideo: "O8TAy4jirF0",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/AI%20TAX%20SPECIALIST%20V1%20part%202%20vertical%20(1).webp",
  },
  {
    name: "Behind the Build Aiwyn",
    youtubeVideo: "Z3-0_B9I5xk",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Behind%20the%20Build-Case%20Study%20on%20Aiwyn%20with%20Ellen%20Choi-Vertical-720%20x%201280%20(1).webp",
  },
  {
    name: "Securing your firm in the age of AI",
    youtubeVideo: "AZX8k8UcvUk",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/SECURING%20YOUR%20FIRM%202%20vertical.webp",
  },
  {
    name: "From Financials to advisory Insights",
    youtubeVideo: "4747LjSqt5Q",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/From%20Financials%20to%20Advisory%20Insight%20Square%20(1).webp",
  },
  {
    name: "The science behind Stanfordtax",
    youtubeVideo: "g89ZWIqq86s",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/THE%20SCIENCE%20BEHIND%20STANFORDTAX%20-%20DAN%20BERENHOLTS%20vertical.webp",
  },
  {
    name: "The Firm leader's quest",
    youtubeVideo: "OjVEpO5f-7o",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/The%20Firm%20Leader_s%20Quest%20with%20Jetpack%20Workflow%20vertical.webp",
  },
  {
    name: "Role of Technology in Testing Controls",
    youtubeVideo: "augBJEahjpQ",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Role%20of%20Technology%20in%20Testing%20Controls%20with%20Roger%20Mayer-720%20x%201280%20(1).webp",
  },
  {
    name: "Document Intelligence",
    youtubeVideo: "PZ8LvTWq7xk",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Document%20Intelligence%20for%20Finance%20Leaders%20Vertical.webp",
  },
  {
    name: "AI-Powered transformation in tax firms",
    youtubeVideo: "uzMIBYOxtNM",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/AI%20POWERED%20TRANSFORMATIONS%20IN%20TAX%20FIRMS%20-%20SOLON%20ANGEL%20vertical%20(1).webp",
  },
  {
    name: "Agentive audit automation",
    youtubeVideo: "xhlChgfA4OM",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Agentive%20Audit%20Automation%20Vertical.webp",
  },
];
export const level3 = [
  {
    name: "Transforming client services",
    youtubeVideo: "hTeo-TTKgjU",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/TRANSFORMING%20CLIENT%20SERVICES%20v2%20vertical.webp",
  },
  {
    name: "Navigating Accounting with AI",
    youtubeVideo: "E2BYNo4wqNI",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/Amel_Edmond.webp",
  },
  {
    name: "Convergence of AI & IP",
    youtubeVideo: "s59py8XA68o",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/The%20Convergence%20of%20AI%20and%20IP%20with%20Gary%20Boomer-vertical%20(2).webp",
  },
  {
    name: "The AI Accountant",
    youtubeVideo: "_-2O1z393RU",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/The%20AI%20Accountant%20-%20Adapt,%20Automate,%20Achieve-Vertical-%20vertical%20(1).webp",
  },
  {
    name: "Navigating Cybersecurity Incident",
    youtubeVideo: "bfBo1ZQ-fVo",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/NAVIGATING%20A%20CYBERSECURITY%20INCIDENT%20-%20TAMMIE%20HOLLIS%20vertical.webp",
  },
  {
    name: "Building Responsible AI in CPA Firms",
    youtubeVideo: "xChGkYRpjm0",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Building%20Responsible%20AI%20in%20CPA%20Firms%20with%20Raymond-Vertical.webp",
  },
  {
    name: "The State of AI in Accounting",
    youtubeVideo: "3fi2T-idmxc",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/The%20State%20of%20AI%20in%20Accounting%20Vertical.webp",
  },
  {
    name: "Build a 10x Firm: The AI + ExO System",
    youtubeVideo: "e1epFGC4yz4",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Build%20a%2010X%20Firm%20-%20The%20AI%20+%20ExO%20System%20Gary%20Boomer-Vertical%20(1)%20(1).webp",
  },
  {
    name: "AI Enablement for accounting firms",
    youtubeVideo: "dXF5SzbWti8",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/AI%20Enablement%20for%20Accounting%20Firms%20with%20Ellen%20Choi-Vertical-720%20x%201280%20(1).webp",
  },
  {
    name: "A Strategic Blueprint for AI Adoption in Accounting",
    youtubeVideo: "meQr4Z10aSA",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/A%20Strategic%20Blueprint%20for%20AI%20Adoption%20in%20Accounting%20with%20Octavio%20OJ%20Laos-Vertical%20(1).webp",
  },
  {
    name: "Beyond the AI Hype",
    youtubeVideo: "MJYmTiwjSsM",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Beyond%20the%20AI%20Hype%20Vertical.webp",
  },
  {
    name: "The genius Equation thriving in the age of AI",
    youtubeVideo: "O3QZdeUop68",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/THE%20GENIUS%20EQUATION%20VERTICAL.webp",
  },
  {
    name: "The CFO's AI Playbook",
    youtubeVideo: "Ge_TF-RZOsY",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/The%20CFO_s%20AI%20Playbook%20with%20Joe%20Oringel-vertical%20(2).webp",
  },
  {
    name: "The CPA's AI Playbook",
    youtubeVideo: "-8seXI_FmKU",
    imagekitUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/The%20CPAs%20AI%20Playbook%20vertical.webp",
  },
];
export const LearnAI_from_global_data = [
  {
    author: "By Scott Landis",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/Scott_landis.webp",
    youTubeLink: "LgKROuUo1oE",
  },
  {
    author: "By Chris Stegh",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/COPILOT_CHRIS_STEGH.webp",
    youTubeLink: "kRB9TMP9RBc",
  },
  {
    author: "By Dr. Mfon Akpan ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/Transform_Data_into_Action_with_Power_BI_with_Mfon_Akpan_.webp ",
    youTubeLink: "SfYVt8orr3M",
  },
  {
    author: "By Dr. Scott Dell",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/AI-The_Art_of_The_Prompt.webp",
    youTubeLink: "5uJPB2wo1S0",
  },
  {
    author: "By Joe Oringel",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/Joe_Oringel.webp",
    youTubeLink: "A78iFozFkVU",
  },
];
export const Applied_AI_across_data = [
  {
    author: "By Jeff Seibert ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/Built_to_Digitize_with_Jeff_Seibert-Vertical_Z1LNaa1.webp ",
    youTubeLink: "tKt5Kqxo2VM",
  },
  {
    author: "By Raymond Cheng ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/AI-Empowered_Auditing.webp ",
    youTubeLink: "9Pfh8VEpjA8",
  },
  {
    author: "Ian Vacin",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/Ian_Vacin.webp",
    // youTubeLink: "9Pfh8VEpjA8",
  },
  // {
  //   author: "By Alex Lee",
  //   img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/Automate_the_Close_Elevate_the_Team_with_Alex_Lee-Vertical.webp ",
  //   youTubeLink: "zSUMgNGE3UU",
  // },
  {
    author: "By Loroy Kerry",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/Loroy_kerry.webp",
    //youTubeLink: "NRQS8s9uwns",
  },
];
export const AI_leadership_strategy_data = [
  {
    author: "By Jordan Flasdell",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/The_AI_Accountant_Adapt_Automate_Achieve.webp",
    youTubeLink: "t-HRYoEJILU",
  },
  {
    author: "By Tammie Hollis",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/TAMMIE_HOLLIS.webp",
    youTubeLink: "bfBo1ZQ-fVo",
  },
  {
    author: 'By Octavio "OJ" Laos',
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/Octavio_OJ_Laos.webp",
    youTubeLink: "DUBp-QU-0Oc",
  },
  {
    author: "By Gary Boomer",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/The_Convergence_of_AI_and_IP_with_Gary_Boomer_DcLiH7C.webp",
    youTubeLink: "s59py8XA68o",
  },
  {
    author: "By Michelle Voyer",
    description:
      "Learn how firms can package services, monetize IP, harness AI, and attract talent to build sustainable, future-ready practices. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/mic.webp",
    // youTubeLink: "s59py8XA68o",
  },
];

export const AI_Leadership_and_strategy = [
  {
    title: "Information Technology",
    description:
      "From automation to strategy—how CPAs can harness AI for efficiency, insight, and leadership in the new accounting era.",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/The_AI_Accountant_Adapt_Automate_Achieve.webp",
    youTubeLink: "t-HRYoEJILU",
    cpeCredits: " 2 Credits",
  },
  {
    title: "Information Technology",
    description:
      " A practical guide for accounting firms to adopt AI, boost efficiency, manage risks, and transform client service with smart automation ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/AI_Enablement_for_Accounting_Firms.webp",
    youTubeLink: "PLi4LeGjcUo",
    cpeCredits: "1.5 Credits",
  },
  {
    title: "Information Technology",
    description:
      " A practical guide for accounting firms to adopt AI, boost efficiency, manage risks, and transform client service with smart automation ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/AI_Enablement_for_Accounting_Firms.webp",
    youTubeLink: "PLi4LeGjcUo",
    cpeCredits: "1.5 Credits",
  },
  {
    title: "Behavioral Ethics",
    description:
      " Robert explores fraud psychology, using case studies and ethics to teach professionals how to identify and prevent unethical actions. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/Robert_Premiere_Banner_02_A_1.webp",
    youTubeLink: "GDuu6u5NgdA",
    cpeCredits: " 2 Credits",
  },
  {
    title: "Information Technology",
    description:
      "Learn how firms can package services, monetize IP, harness AI, and attract talent to build sustainable, future-ready practices. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/The_Convergence_of_AI_and_IP_with_Gary_Boomer_DcLiH7C.webp",
    youTubeLink: "s59py8XA68o",
    cpeCredits: "2 Credits",
  },
  {
    title: "Information Technology",
    description:
      " A CFO’s guide to using AI strategically, managing risks, and setting governance to ensure ethical and effective business adoption. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/The_CFOs_AI_Playbook_with_Joe_Oringel.webp",
    youTubeLink: "klJXMCwkYO8",
    cpeCredits: "2 Credits",
  },
  // {
  //   title: "Information Technology",
  //   description:
  //     "Use GPT as your everyday thought partner to streamline tasks, improve focus, and reclaim hours for high-value work. ",
  //   img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/Reclaim_10_Hours_a_Week_Using_ChatGPT_with_Scott_Landis.webp",
  //   youTubeLink: "LgKROuUo1oE",
  //   cpeCredits: "3 Credits",
  // },
  {
    title: "Finance",
    description:
      " Tackle business valuation complexities with Jamar Cobb, applying advanced methods to provide precise, market-driven financial insights. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/How_to_Value_a_Business_with_Jamar-Cobb_Dennard.webp",
    youTubeLink: "P8Wt2z7GXeQ",
    cpeCredits: " 2 Credits ",
  },
  {
    title: " Business Management & Organisation ",
    description:
      " Learn to safeguard your business with Erika Adresen’s masterclass on continuity, risk mitigation, and operational resilience. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/Building_Resilience_for_Business_Growth_with_Erika_Andresen.webp",
    youTubeLink: "5yWGTR0Z5eQ",
    cpeCredits: "2 Credits",
  },
  // {
  //   title: "Information Technology",
  //   description:
  //     " From IT evolution to cyber defense, this course equips you with essential strategies to tackle modern threats and ensure business resilience. ",
  //   img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/NAVIGATING_A_CYBERSECURITY_INCIDENT_WITH_TAMMIE_HOLLIS.webp",
  //   youTubeLink: "bfBo1ZQ-fVo",
  //   cpeCredits: " 2.5 Credits",
  // },
  {
    title: " Management Services ",
    description:
      " Business risk isn’t optional - managing it well is. Erika Andresen’s Masterclass arms you with tools to lead smart & stay prepared. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/Adulting_in_Business_with_Erika_Andresen.webp",
    youTubeLink: "uRVqC0imoAQ",
    cpeCredits: " 2 Credits",
  },
  // {
  //   title: " Management Services ",
  //   description:
  //     "  Learn from Gary Cokins, founder and author, on how Corporate Performance Management bridges strategy and execution for success. ",
  //   img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/Strategy_Management_with_a_Strategy_Map_with_Gary_Cokins.webp",
  //   youTubeLink: "t3x7uUMlxQM",
  //   cpeCredits: " 2 Credits",
  // },
  {
    title: " Management Services ",
    description:
      "  Tackle burnout & client chaos in accounting. Learn to retain talent, offboard wisely, and build a firm where people thrive. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/Improving_Capacity_by_Firing_Clients_with_Bonnie_Buol-Horizontal.webp",
    youTubeLink: "ztHrWoD9_f0",
    cpeCredits: " 2 Credits",
  },
  {
    title: "Information Technology",
    description:
      " AI is here - now it’s about making it work. From strategy to training, tools, use cases, and ROI, learn how to turn AI into everyday results.  ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira/A_Strategic_Blueprint_for_AI_Adoption_in_Accounting.webp",
    youTubeLink: "9K0JfyXwIOQ",
    cpeCredits: " 1.5 Credits",
  },
];

export const LearnAI_from_global_data_auditiors = [
  {
    author: "By Chris Stegh",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/microsoft-copilot.webp",
    youTubeLink: "kRB9TMP9RBc",
  },
  {
    author: "By Griffin Lickfeldt",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/power-automate.webp",
    youTubeLink: "KgrAqIQfzAU",
  },
  {
    author: "By Cristiano Galvao",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/power-bi.webp",
    youTubeLink: "hqPeNYGwp_M",
  },
  {
    author: "By Dr. Scott Dell",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/ai.webp",
    youTubeLink: "5uJPB2wo1S0",
  },
  {
    author: "By Joe Oringel",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/data-analytics.webp",
    youTubeLink: "8GWwMFaN0XA",
  },
];

export const Applied_AI_across_auditors = [
  {
    author: "By Chris Stegh",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/audit-breakthrough.webp",
    youTubeLink: "s-lQfnZULqY",
  },
  {
    author: "By Kelly Mann",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/audit-beyond.webp",
    youTubeLink: "KMGhVyp5ibI",
  },
  {
    author: "By Raymond Cheng",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/ai-empowered.webp",
    youTubeLink: "YMo2g-jw3rA",
  },
  {
    author: "By Daniel Alberson",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/agentive-audit.webp",
    youTubeLink: "Uy5ShzGGW5k",
  },
  {
    author: "By Robert Persichitte",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/investigating-fraud.webp",
    youTubeLink: "GDuu6u5NgdA",
  },
];

export const AI_leadership_strategy_auditors = [
  {
    author: "By Gary Boomer",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/convergence-ai.webp",
    youTubeLink: "e1epFGC4yz4",
  },
  {
    author: "By Michelle Voyer ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/transforming-client.webp",
    youTubeLink: "hTeo-TTKgjU",
  },
  {
    author: "By Tammie Hollis",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/cybersecurity.webp",
    youTubeLink: "bfBo1ZQ-fVo",
  },
  {
    author: "By John Higgins",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/ai-hype.webp",
    youTubeLink: "JvQ0zI8MBRo",
  },
  {
    author: "By Callie Carlton",
    // description:
    //   "Learn how firms can package services, monetize IP, harness AI, and attract talent to build sustainable, future-ready practices. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/caira-for-auditors/genius-equation.webp",
    youTubeLink: "rCKVX-C5FVk",
  },
];

// CAIRA Testimonials - 38 images with mobile and desktop versions
export const caira_testimonials_data = [
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-1.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-1.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-2.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-2.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-3.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-3.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-4.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-4.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-5.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-5.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-6.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-6.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-7.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-7.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-8.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-8.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-9.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-9.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-10.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-10.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-11.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-11.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-12.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-12.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-13.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-13.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-14.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-14.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-15.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-15.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-16.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-16.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-17.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-17.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-18.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-18.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-19.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-19.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-20.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-20.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-21.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-21.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-22.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-22.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-23.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-23.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-24.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-24.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-25.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-25.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-26.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-26.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-27.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-27.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-28.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-28.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-29.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-29.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-30.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-30.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-31.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-31.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-32.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-32.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-33.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-33.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-34.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-34.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-35.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-35.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-36.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-36.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-37.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-37.webp",
  },
  {
    mobile:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkedin-post-mob-38.webp",
    desktop:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/linkdin-post-desk-38.webp",
  },
];

export const Showcase_Badge_data = [
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.08.54%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.18.25%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.08.11%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.19.49%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.18.54%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.39.42%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.21.20%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.39.26%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.22.48%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.20.18%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.01.30%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.14.16%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.06.51%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.19.21%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.38.57%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.09.27%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Cpa%204.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.23.21%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.07.26%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.17.20%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.14.44%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.12.34%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/cpa%206.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Cpa%205.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/cpa.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.22.07%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Capture%206.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Screenshot%202026-06-08%20at%204.22.32%E2%80%AFPM.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/cpa%203.webp",
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/testimonials/Cpa%202.webp",
];

export const ai_and_analytics_for_accounting = [
  {
    courseType: "Information Technology",
    title: " Built to Digitize ",
    author: " By Jeff Seibert ",
    description:
      "Reimagine accounting with AI - automate the grind, close books in real time, and turn financial data into instant business insight.  ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/Built_to_Digitize_with_Jeff_Seibert-Vertical_Z1LNaa1.webp ",
    youTubeLink: "tKt5Kqxo2VM",
  },

  {
    courseType: "Information Technology ",
    title: "Next-Gen Accounting: AI Workflows & Agents",
    author: "By Alex Lee",
    description:
      "Workflows vs agents for finance: build n8n automations, then deploy agents for month-end QA and toward continuous close. Layered roadmap.",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/Next-Gen_Accounting-_AI_Workflows__Agents_with_Alex_LEE_Square-Vertical.webp",
  },
  {
    courseType: "Information Technology",
    title: "AI Agents in Accounting: The New Electricity",
    author: "By Octavio (OJ) Laos",
    description:
      "Learn how Agentic AI transforms business by moving from chatbots to autonomous agents, with real use cases, risks, and future trends.",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/AI_Agents_the_New_Electricity__with_Octavio_OJ_Laos-Vertical_1.webp ",
    youTubeLink: "DUBp-QU-0Oc",
  },
  {
    courseType: "Information Technology",
    title: "AI: It's All About the Prompt",
    author: "By Dr. Scott Dell",
    description:
      "Master prompt engineering with Dr. Scott Dell, exploring AI-driven tools, advanced techniques, and practical applications for success.",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/AI-The_Art_of_The_Prompt.webp",
    youTubeLink: "5uJPB2wo1S0",
  },

  {
    courseType: " Computer software & Applications ",
    title: "Behind the Build: Aiwyn ",
    author: "By Ellen Choi ",
    description:
      "Ellen Choi’s Masterclass shows how Aiwyn modernized billing, payments, and practice of cutting manual work and scaling with AI. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/Behind_the_Build-Case_Study_on_Aiwyn_with_Ellen_Choi-Vertical-720_x_1280.webp",
    youTubeLink: "7RyOUSu4w1E",
  },
  {
    courseType: "Information Technology",
    title: "Automate the Close, Elevate the Team ",
    author: "By Alex Lee",
    description:
      "Harness AI to simplify workflows, cut errors, and elevate your accounting career from execution to leadership. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/Automate_the_Close_Elevate_the_Team_with_Alex_Lee-Vertical.webp ",
  },
  {
    courseType: " Auditing ",
    title: "Investigating Fraud with Data Models",
    author: "By Robert Persichitte ",
    description:
      "Uncover fraud with data analytics. Learn to design and refine detection models using tools like Benford’s Law, AI, and statistical analysis.",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/Investigating_Fraud_with_Data_Models_with_Robert_Persichitte-1080x1080-Ver_DEXC5qn.webp?updatedAt=1758710114266 ",
    youTubeLink: "NRQS8s9uwns",
  },
  {
    courseType: "Information Technology",
    title: " AI for Accountants: Friend or Foe ",
    author: " By Dr. Scott Dell ",
    description:
      "Master AI in accounting with Dr. Scott Dell. Explore tools, risks, and strategies to thrive in an AI-driven finance world. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/AI_for_Accountants__Friend_or_Foe_with_Dr_XWbcBch._Scott_Dell.webp ",
    youTubeLink: "DtIb56Mdp_g",
  },
  {
    courseType: "Information Technology",
    title: " Building Responsible AI in CPA Firms ",
    author: "By Raymond Cheng",
    description:
      "Masterclass on embedding AI into CPA firms through responsible frameworks, risk safeguards, and high-impact, real-world applications.",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/Building_Responsible_AI_in_CPA_Firms.webp",
    youTubeLink: "9Pfh8VEpjA8",
  },
  {
    courseType: " Finance ",
    title: " In-Depth Guide to Financial Valuation ",
    author: " By Dr. Jason Cherubini ",
    description:
      "Dr. Jason Cherubini, finance expert & entrepreneur, guides you through DCF & market multiples for smarter financial choices. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/In-Depth_Guide_to_Financial_Valuation.webp",
    youTubeLink: "zVh3PoAtHBA",
  },
  {
    courseType: "Computer software & Applications ",
    title: " Transform Data into Action with Power BI ",
    author: " By Dr. Mfon Akpan ",
    description:
      "Take control of your data with Power BI Dr.Mfon Akpan reveals how to transform raw data into insights and create reports and dashboards.",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/Transform_Data_into_Action_with_Power_BI_with_Mfon_Akpan_.webp ",
    youTubeLink: "SfYVt8orr3M",
  },
];

export const human_skills_in_the_ai_era = [
  {
    title: "Communications & Marketing",
    description:
      "Dr. Schiefelbein elevates public speaking with cutting-edge strategies and tech-enhanced frameworks for impactful, persuasive communication.",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/skills-1.webp",
    youTubeLink: "uEgjtZ5cicY",
    cpeCredits: "3 Credits",
  },
  {
    title: "Communications & Marketing",
    description:
      "Lead with Sherrika, mastering AI communication, emotional intelligence, and modern leadership strategies to excel in today’s fast-paced world. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/skills-2.webp",
    youTubeLink: "m7N-SuH-hP0",
    cpeCredits: "2 Credits",
  },
  {
    title: " Business Management & Organisation ",
    description:
      "Pioneer workforce change with Mark James, combining next-gen management techniques and tech advancements to drive growth and agility. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/skills-3.webp",
    youTubeLink: "zvo_saWdo1s",
    cpeCredits: "2.5 Credits",
  },
  {
    title: " Personal Development ",
    description:
      "Edi Hayes guides participants in balancing corporate careers and entrepreneurship through strategic planning, mindset growth, and key tools. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/skills-4.webp",
    youTubeLink: "xdWNvKNReP4",
    cpeCredits: "2 Credits",
  },
  {
    title: " Finance ",
    description:
      "From NFL cheerleader to finance leader, Danetha Doe shows CPAs how to blend purpose, planning, and passion to thrive as entrepreneurs. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/skills-5.webp",
    youTubeLink: "W93Ih7zsook",
    cpeCredits: "2 Credits",
  },
  {
    title: " Management Services ",
    description:
      "Future-proof your career with Jamar Cobb-Dennard by mastering digital branding, content creation, and networking strategies in the CPA field. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/skills-6.webp",
    youTubeLink: "4nztGrX1ypg",
    cpeCredits: "2 Credits",
  },
  {
    title: "Communications & Marketing",
    description:
      "Dr.Jill Schiffelbein’s Masterclass shows how to build trust through clear, human-centered CX across all four stages of the customer journey. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/skills-7.webp",
    youTubeLink: "x0lWBnFrWWQ",
    cpeCredits: "2 Credits",
  },
  {
    title: " Personnel/Human Resource ",
    description:
      "Bonnie Buol Ruszczyk tackles gender equity in accounting with strategies to attract, retain, and elevate women in the profession. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/skills-8.webp",
    youTubeLink: "IKUNGpLvDzk",
    cpeCredits: "2.5 Credits",
  },
  {
    title: " Personal Development ",
    description:
      "Support mental health at work with Edi Hayes. Learn rights, strategies, and tools to foster well-being and create inclusive workplaces. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/skills-9.webp",
    youTubeLink: "9BiRdiS8gqQ",
    cpeCredits: "2 Credits",
  },
  {
    title: " Personal Development ",
    description:
      "Reimagine leadership with Lisa Dy, blending innovative EQ tools and self-leadership techniques to shape the next generation of leaders. ",
    img: "https://ik.imagekit.io/mileseducation/miles_website/masterclass/skill-10.webp",
    youTubeLink: "doPrTyvYaEk",
    cpeCredits: "2 Credits",
  },
];

export const CMA_PAGE_ALUMNI_DATA: Alumni[] = [
  {
    name: "Vijay Reddy",
    img: "/newImages/MilesEducationWebsite-2024/cma/cma-home/alumni-vijay.png",
    description:
      "The Miles US Pathway program was a game-changer for my career growth. Massive thanks to the Miles Placement team for their invaluable support and guidance throughout!",
    des: "The Miles US Pathway program was a game-changer for my career growth. Massive thanks to the Miles Placement team for their invaluable support and guidance throughout!",
    compLogo:
      "/newImages/MilesEducationWebsite-2024/cma/cma-home/companylogo-smithmarion.webp",
    alliance: "",
    location: "Redlands, CA",
    linkdin: "https://www.linkedin.com/in/vijaynrkonduru/",
    storyYoutubeVideoLink: "https://youtube.com/shorts/He2cwbYxp44",
  },
  {
    name: "Hyder Syed",
    img: "/newImages/MilesEducationWebsite-2024/cma/cma-home/alumni-hyder.jpeg",
    description:
      "Choosing the Miles US Pathway was a smart move. Thanks to Miles Placement Team for guiding me through it. I've embraced, conquered, and now I'm ready to fly!",
    des: "Choosing the Miles US Pathway was a smart move. Thanks to Miles Placement Team for guiding me through it. I've embraced, conquered, and now I'm ready to fly!",
    compLogo:
      "/newImages/MilesEducationWebsite-2024/cma/cma-home/companylogo-mbe.png",
    alliance: "",
    location: "Chandler, AZ",
    linkdin: "http://linkedin.com/in/hyder-syed/",
    storyYoutubeVideoLink: "https://youtube.com/shorts/myS4Ic_FfA0",
  },
  {
    name: "Manisha Kulkarni",
    img: "/newImages/MilesEducationWebsite-2024/cma/cma-home/alumni-manisha.jpg",
    description:
      "Miles helped me master my field and turn my American Dream into reality by securing my placement in the US. Grateful for their expertise and support.",
    des: "Miles helped me master my field and turn my American Dream into reality by securing my placement in the US. Grateful for their expertise and support.",
    compLogo:
      "/newImages/MilesEducationWebsite-2024/cma/cma-home/companylogo-rsm.png",
    alliance: "",
    location: "Philadelphia, PA",
    linkdin: "www.linkedin.com/in/ manisha-kulkarni-060634187/",
    storyYoutubeVideoLink: "https://youtube.com/shorts/vcNGtncehYY",
  },
  {
    name: "Anand Sharma",
    img: "/newImages/MilesEducationWebsite-2024/cma/cma-home/alumni-anand.jpeg",
    description:
      "The Miles US Pathway program was a game-changer for my career growth. Massive thanks to the Miles Placement team for their invaluable support and guidance throughout!",
    des: "The Miles US Pathway program was a game-changer for my career growth. Massive thanks to the Miles Placement team for their invaluable support and guidance throughout!",
    compLogo:
      "/newImages/MilesEducationWebsite-2024/cma/cma-home/companylogo-ksdt.png",
    alliance: "",
    location: "Miami, FL",
    linkdin: "https://in.linkedin.com/in/anand-sharma-080b0314a/",
    storyYoutubeVideoLink: "https://youtube.com/shorts/NbKbS-2Nv1o",
  },
  {
    name: "Mahesh Subramanian",
    img: "/newImages/MilesEducationWebsite-2024/cma/cma-home/alumni-mahesh.JPG",
    description:
      "Never thought a US accounting career was possible for me until Miles made it happen. Forever grateful for their support",
    des: "Never thought a US accounting career was possible for me until Miles made it happen. Forever grateful for their support",
    compLogo:
      "/newImages/MilesEducationWebsite-2024/cma/cma-home/companylogo-rsm.png",
    alliance: "",
    location: "New York City, NY",
    linkdin: "https://www.linkedin.com/in/mahesh-s-5a50b1127/",
    storyYoutubeVideoLink: "https://youtube.com/shorts/7an8N6hHclc",
  },
  {
    name: "Lavika Sharma",
    img: "/newImages/MilesEducationWebsite-2024/cma/cma-home/alumni-lavika.jpg",
    description:
      "Thanks to Miles, my US work dream is now a reality! Ready for the next career chapter with a bag full of wins waiting. ",
    des: "Thanks to Miles, my US work dream is now a reality! Ready for the next career chapter with a bag full of wins waiting. ",
    compLogo:
      "/newImages/MilesEducationWebsite-2024/cma/cma-home/companylogo-sdk.jpg",
    alliance: "",
    location: "Minneapolis, MN",
    linkdin: "https://www.linkedin.com/in/lavika-sharma-6b9703165/",
    storyYoutubeVideoLink: "https://youtube.com/shorts/_Fz6_qI0IK8",
  },
  {
    name: "Disha Rungta",
    img: "/newImages/MilesEducationWebsite-2024/cma/cma-home/alumni-disha.jpg",
    description:
      "Thanks to Miles, I'm diving into a thrilling new chapter in the US. This transition is huge for me, and I'm excited for what's next!",
    des: "Thanks to Miles, I'm diving into a thrilling new chapter in the US. This transition is huge for me, and I'm excited for what's next!",
    compLogo:
      "/newImages/MilesEducationWebsite-2024/cma/cma-home/companylogo-rsm.png",
    alliance: "",
    location: "New York City, NY",
    linkdin: "https://www.linkedin.com/in/disha-rungta/",
    storyYoutubeVideoLink: "https://youtube.com/shorts/UTdmgdGxGug",
  },
  {
    name: "Vinay Kumar",
    img: "/newImages/MilesEducationWebsite-2024/cma/cma-home/alumni-vinay.jpg",
    description:
      "With Miles it was a smooth journey of not just all round support and improvement but also elevating my confidence in myself in achieving my dream.",
    des: "With Miles it was a smooth journey of not just all round support and improvement but also elevating my confidence in myself in achieving my dream.",
    compLogo:
      "/newImages/MilesEducationWebsite-2024/cma/cma-home/companylogo-blackmansloop.webp",
    alliance: "",
    location: "Chapel Hill, NC",
    linkdin: "http://linkedin.com/in/402581189/",
    storyYoutubeVideoLink: "https://www.youtube.com/shorts/z-cwdKrwqN4",
  },
  {
    name: "Vamshi Teja",
    img: "/newImages/MilesEducationWebsite-2024/cma/cma-home/alumni-vamshi.jpeg",
    description:
      "Moving to the U.S. has been a transformative journey. Thanks to Miles, I’ve had an incredible year and am thrilled to begin my new job!",
    des: "Moving to the U.S. has been a transformative journey. Thanks to Miles, I’ve had an incredible year and am thrilled to begin my new job!",
    compLogo:
      "/newImages/MilesEducationWebsite-2024/cma/cma-home/companylogo-smithmarion.webp",
    alliance: "",
    location: "Redlands, CA",
    linkdin: "https://www.linkedin.com/in/vamshi-teja-vangala-604081141/",
    storyYoutubeVideoLink: "https://youtube.com/shorts/e9UwNPzHG2Q",
  },
];
// TailwindCSS Styling for cl2
export const stylingForCL2QuickConnect = {
  mainDiv: "test",
  title: "text-lg font-semibold text-white",
  childDiv: "lg:w-full",
  name: "appearance-none block text-sm w-full bg-slate-200 text-gray-700 h-9 border-red-600 rounded-full py-2 px-3 mb-3 leading-tight focus:outline-none",
  iconP: "h-5 w-5 flex-shrink-0 rounded-full mr-3",
  phone:
    "appearance-none block text-sm w-full bg-slate-200 text-gray-700 h-9 border border-red-600 rounded-full py-2 px-3 mb-3 leading-tight focus:outline-none",
  location:
    "appearance-none block text-sm w-full bg-slate-200 text-gray-700 h-9 border border-red-600 rounded-full py-2 px-3 mb-3 leading-tight focus:outline-none",
  course:
    "appearance-none block text-sm w-full bg-slate-200 text-gray-700 h-9 border border-red-600 rounded-full py-2 px-3 mb-3 leading-tight focus:outline-none select_box",
  email:
    "appearance-none block text-sm w-full bg-slate-200 text-gray-700 h-9 border border-red-600 rounded-full py-2 px-3 mb-3 leading-tight focus:outline-none",
  submitBtn:
    "block mx-auto shadow bg-[#B41416] hover:bg-[#b41417ee] focus:shadow-outline focus:outline-none text-white font-semibold py-2.5 px-8 rounded-full text-sm border border-white-600 mt-4 w-2/3",
  choosePath: {
    titleTop: "mb-2 text-sm text-white text-left",
    titleMiddle: "mb-2 text-white text-left",
    options: "pl-0",
  },
  whatsapp: {
    input:
      "relative float-left mr-[6px] mt-[0rem] h-[1.5rem] w-[1.5rem] appearance-none rounded-full border-[1px] border-solid border-white before:pointer-events-none before:absolute before:h-[1.4rem] before:w-[1.4rem] before:scale-0 before:rounded-full before:bg-transparent before:opacity-0 before:shadow-[0px_0px_0px_13px_transparent] before:content-[''] checked:border-white checked:border-[1px] checked:bg-transparent checked:before:opacity-[0.16] checked:after:absolute checked:after:mt-[2.6px] checked:after:ml-[0.52rem] checked:after:block checked:after:h-[0.8125rem] checked:after:w-[0.35rem] checked:after:rotate-45 checked:after:border-[0.1rem] checked:after:border-l-0 checked:after:border-t-0 checked:after:border-solid checked:after:border-white checked:after:bg-transparent checked:after:content-[''] hover:cursor-pointer hover:before:opacity-[0.04] hover:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)] focus:shadow-none focus:transition-[border-color_0.2s] focus:before:scale-100 focus:before:opacity-[0.12] focus:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)] focus:before:transition-[box-shadow_0.2s,transform_0.2s] focus:after:absolute focus:after:block focus:after:h-[0.875rem] focus:after:w-[0.875rem] focus:after:rounded-[0.125rem] focus:after:content-[''] checked:focus:before:scale-100 checked:focus:before:shadow-[0px_0px_0px_13px_#3b71ca] checked:focus:before:transition-[box-shadow_0.2s,transform_0.2s] checked:focus:after:mt-[2.6px] checked:focus:after:ml-[0.52rem] checked:focus:after:h-[0.8125rem] checked:focus:after:w-[0.36rem] checked:focus:after:rotate-45 checked:focus:after:border-[0.1rem] checked:focus:after:border-l-0 checked:focus:after:border-t-0 checked:focus:after:border-solid checked:focus:after:border-white checked:focus:after:bg-transparent dark:border-neutral-600 dark:checked:border-primary dark:checked:bg-primary dark:focus:before:shadow-[0px_0px_0px_13px_rgba(255,255,255,0.4)] dark:checked:focus:before:shadow-[0px_0px_0px_13px_#3b71ca]",
    icon: "dark:invert duration-300 mx-1",
    label:
      "hover:cursor-pointer text-white lg:text-xs !text-[12px] flex items-center",
    all: "mt-5 flex justify-left items-center",
  },
  newsletter: {
    text: "text-sm text-white mt-5",
  },
  success: {
    conatiner: "w-full p-4 m-3 bg-green-800",
    msg: "text-white text-sm",
    greet: "indent-0",
  },
};

export const CollegeCategoryItems = {
  categoryItem1:
    "National University of Singapore (NUS)IIM Ahmedabad, IIM Bangalore, IIM",
  categoryItem2:
    "IIM Lucknow, IIM Indore - India Asian Institute of Management - Philippines Cape Town GSB - South Africa",
  categoryItem3:
    "IIM Mumbai, IIM Trichy - India University of the Philippines, De La Salle University - Philippines National Cheng Kung University - Taiwan Shanghai University - China Narxoz University - Kazakhstan Doshisha University - Japan International University of Japan SolBridge International School of Business - South Korea AASTMT - Egypt Lagos Business School - Nigeria American University in Dubai - UAE",
  categoryItem4: "",
  categoryItem5: "",
};

export const POSITION_WITH_IIMS_CATEGORY_BUTTONS = [
  {
    btnKey: "US Rankings*",
    btnTitle: "US Rankings*",
  },
  {
    btnKey: "Global Rankings*",
    btnTitle: "Global Rankings*",
  },
  {
    btnKey: "Fees",
    btnTitle: "Fees",
  },
];

export const WHY_MILES_MBA_ABROAD_REASONS = [
  {
    reasonKey: "abroad_reason1",
    reasonIcon: "/mba/cap.png",
    reasonTitle: "MBA from Clark University",
    reasonSubtitle: "Ranked top 100 US B-school",
    reasonSubtitleWidth: "96px",
  },
  {
    reasonKey: "abroad_reason3",
    reasonIcon: "/mba/perc.png",
    reasonTitle: "Test Waiver",
    reasonSubtitle: "No need for GMAT / GRE & TOEFL / IELTS",
    reasonSubtitleWidth: "135px",
  },
  {
    reasonKey: "abroad_reason2",
    reasonIcon: "/mba/lap.png",
    reasonTitle: "55% Scholarship",
    reasonSubtitle: "Clark MBA",
    reasonSubtitleWidth: "100%",
  },

  {
    reasonKey: "abroad_reason4",
    reasonIcon: "/mba/cal.png",
    reasonTitle: "STEM-designated program",
    reasonSubtitle: "with 3-year work option (OPT) in the US",
    reasonSubtitleWidth: "150px",
  },
];

export const CMA_AND_CPA_CARD_DATA: AlumniWithoutVideo[] = [
  {
    name: "Rohit Kumar",
    img: "/newImages/MilesEducationWebsite-2024/alumni11.png",
    description:
      "Starting this US chapter brings me joy, thanks to the Miles Placement Team. From visa to job, their support made it happen!",
    des: "Starting this US chapter brings me joy, thanks to the Miles Placement Team. From visa to job, their support made it happen!",
    compLogo: "/newImages/MilesEducationWebsite-2024/rsm.png",
    alliance: "BDO Alliance",
    location: "New York",
    linkdin: "https://www.linkedin.com/in/rohit-kumar-srivastava-2b7b8a161/",
  },
  {
    name: "Pooja Grover",
    img: "/newImages/MilesEducationWebsite-2024/alumni112.png",
    description:
      "I'm thrilled to take on this new chapter and savor life in the U.S. It's a significant transition, and I eagerly anticipate the adventures and opportunities that await.",
    des: "From the VISA process to touchdown in the US, Miles was with me at every step. I’m living my American dream now.",
    compLogo: "/newImages/MilesEducationWebsite-2024/sbc.png",
    alliance: "BDO Alliance",
    location: "Owing Mills, Maryland",
    linkdin: "https://www.linkedin.com/in/groverpooja",
  },
  {
    name: "Dipesh Anand",
    img: "/newImages/MilesEducationWebsite-2024/alumni13.png",
    description:
      "From getting my Visa approved to landing a job, every step has been amazing. The anticipation of starting a new chapter in the US fills me with pure happiness.",
    des: "From getting my Visa approved to landing a job, every step has been amazing. The anticipation of starting a new chapter in the US fills me with pure happiness.",

    compLogo: "/newImages/MilesEducationWebsite-2024/boos.png",
    location: "Fresno, California",
    alliance: "BDO Alliance",
    linkdin: "https://www.linkedin.com/in/dipeshanand/",
  },
  {
    name: "Reepal Patel",
    img: "/newImages/MilesEducationWebsite-2024/alumni14.png",
    description:
      "I am studying MAcc here and enjoying living my dream. I came to the US since we get exceptional opportunities here.",
    des: "I am studying MAcc here and enjoying living my dream. I came to the US since we get exceptional opportunities here.",

    compLogo: "/newImages/MilesEducationWebsite-2024/logo-bdo.png",
    alliance: "BDO Alliance",
    location: "Columbus, Ohio",
    linkdin: "https://www.linkedin.com/in/reepal-patel/",
  },
  {
    name: "Amrutha Kallarackal",
    img: "/newImages/MilesEducationWebsite-2024/alumni15.png",
    description:
      "When I look back, I will think - already 9 months gone and job in hand; I didn’t expect this at all. All thanks to miles.",
    des: "When I look back, I will think - already 9 months gone and job in hand; I didn’t expect this at all. All thanks to miles.",

    compLogo: "/newImages/MilesEducationWebsite-2024/MBE_Logo.png",
    location: "Baraboo, Wisconsin",
    alliance: "RSM Alliance",
    linkdin: "https://www.linkedin.com/in/amrutha-teressa-k-51621016a/",
  },

  {
    name: "Lavika Sharma",
    img: "/newImages/MilesEducationWebsite-2024/alumni/Lavika.png",
    description:
      "Turning my dream of working in the U.S. into reality has been incredible. Excited for the next step in my career ",
    des: "Turning my dream of working in the U.S. into reality has been incredible. Excited for the next step in my career ",

    compLogo: "/newImages/MilesEducationWebsite-2024/alumni/logo1.png",
    location: "Minneapolis, Minnesota",
    alliance: "Allinial Alliance",
    linkdin: "https://www.linkedin.com/in/lavika-sharma-6b9703165/",
  },
  {
    name: "Sahil rayani",
    img: "/newImages/MilesEducationWebsite-2024/alumni/Sahil.png",
    description:
      "Deloitte enriched my auditing, and with the Macc program, I'm set for success. Excited to continue my journey in the U.S.!",
    des: "Deloitte enriched my auditing, and with the Macc program, I'm set for success. Excited to continue my journey in the U.S.!",

    compLogo: "/newImages/MilesEducationWebsite-2024/alumni/logo2.png",
    location: "San Antonia, texas",
    alliance: "  Alliance",
    linkdin: "https://www.linkedin.com/in/sahilrayani/",
  },
  {
    name: "Preetha Ravi",
    img: "/newImages/MilesEducationWebsite-2024/alumni/preetha.png",
    description:
      "I've enjoyed the experience of meeting new friendships and making connections. The MAcc program has been a unique and fulfilling journey.",
    des: "I've enjoyed the experience of meeting new friendships and making connections. The MAcc program has been a unique and fulfilling journey.",

    compLogo: "/newImages/MilesEducationWebsite-2024/alumni/logo4.png",
    location: "Chicago, IL",
    alliance: "RSM Alliance",
    linkdin: "https://www.linkedin.com/in/preetha-ravi-2a950b186/",
  },
  {
    name: "Ambily George",
    img: "/newImages/MilesEducationWebsite-2024/alumni/ambily.png",
    description:
      "The MAcc program has been a game-changer for my career, providing me with invaluable skills and knowledge.",
    des: "The MAcc program has been a game-changer for my career, providing me with invaluable skills and knowledge.",

    compLogo: "/newImages/MilesEducationWebsite-2024/alumni/logo4.png",
    location: "San Antonio, TX",
    alliance: "RSM Alliance",
    linkdin: "https://www.linkedin.com/in/ambily-george-39715213",
  },
];

export const getCMA_AND_CPA_BIG4_DATA = (
  isCMA: boolean = true,
): AlumniWithoutVideo[] => {
  return [
    {
      name: "Taniya Kanwar",
      img: "/newImages/MilesEducationWebsite-2024/alumni6.png",
      alt: "taniya kanwar, cpa alumni - miles education",
      description: `Varun Jain's online lectures gave me a great concept base. The tricks he shared in the videos were very helpful to memorize the concepts during the exams. Also, the advantage with Miles is that you can call your mentor anytime to clear your doubts. A realistic study plan, 100% attention to study hours, and doubt-solving sessions with Miles subject Mentors before the exam helped me clear ${
        isCMA ? "CMA" : "CPA"
      }.`,
      des: `${
        isCMA ? "CMA" : "CPA"
      } with Miles gave me the wings to fly at Deloitte. The Miles team helped me at every stage of my journey.`,

      compLogo: "/newImages/MilesEducationWebsite-2024/Deloitte-logo.png",
      alliance: "delloite, big 4 accounting firm",
      location: "Delhi, India",
      linkdin: "https://www.linkedin.com/in/taniya-kanwar-6a941910a/",
    },
    {
      name: "Dhyey Jatania",
      img: "/newImages/MilesEducationWebsite-2024/alumni8.png",
      alt: "dhey jatania, cpa alumni - miles education",
      description: `When I enrolled for ${
        isCMA ? "CMA" : "CPA"
      } with Miles, I was simultaneously preparing for my CA, finals and all credits go to the game plan team of Miles tailored a personalized learning experience. Throughout my preparation, I only focused on Miles’s books for a good read as they were crisp, to the point, and excellent for the last moment revisions.`,
      des: `Doing ${
        isCMA ? "CMA" : "CPA"
      } with Miles after my CA was a game-changer since it landed me a ground-breaking role at EY.`,

      compLogo: "/newImages/MilesEducationWebsite-2024/EY.png",
      alliance: "ey, big 4 accounting firm",
      location: "Mumbai, India",
      linkdin: "https://www.linkedin.com/in/dhyey-jatania-4b85b4a4/",
    },
    {
      name: "Salam Mohammed",
      img: "/newImages/MilesEducationWebsite-2024/alumni7.png",
      alt: "salam mohammed, cpa alumni - miles education",
      description: `I joined Miles after the suggestion from a colleague. Watching the excellent videos by Varun in the demo class convinced me further. I got great support from the Miles team in all aspects, from evaluation to exam preparations. The study material provided by Miles is extremely simplified and apt, especially for Indian candidates. Videos by Varun are much required for the preparation. I have suggested Miles to everyone who inquiried about a ${
        isCMA ? "CMA" : "CPA"
      } course provider.`,
      des: "I joined Miles after watching Varun in the demo class. I got great support from the miles team in all aspects.",

      compLogo: "/newImages/MilesEducationWebsite-2024/KPMG.png",
      alliance: "kpmg, big 4 accounting firm",
      location: "Emakulum, India",
      linkdin: "https://www.linkedin.com/in/salam-mohammed-196b22134/",
    },
    {
      name: "Prathamesh Shinde",
      img: "/newImages/MilesEducationWebsite-2024/alumni5.png",
      alt: "prathamesh shinde, cpa alumni - miles education",
      description: `After I cleared my ${
        isCMA ? "CMA" : "CPA"
      } exam, it took less than a month to get a job at PwC with the help of Miles's placement team. It was a great experience with Miles, right from the registration till my placement post ${
        isCMA ? "CMA" : "CPA"
      }, everything was very well taken care of. I have even referred many of my friends to get their ${
        isCMA ? "CMA" : "CPA"
      } done through Miles.`,
      des: `I felt like I was on cloud nine after earning my US ${
        isCMA ? "CMA" : "CPA"
      } with Miles. I got so many offers, and finally chose PWC.`,
      compLogo: "/newImages/MilesEducationWebsite-2024/pwc.png",
      alliance: "pwc, big 4 accounting firm",
      location: "Ahmedabad, India ",
      linkdin: "https://www.linkedin.com/in/prathamesh-shinde-220bb785/",
    },
    {
      name: "Bhavit Sethi",
      img: "/newImages/MilesEducationWebsite-2024/alumni12.png",
      alt: "bhavit sethi, cpa alumni - miles education",
      description: `Clearing ${
        isCMA ? "CMA" : "CPA"
      } helped me learn professional accounting. It cleared my fundamental concepts and helped me in my professional career, as well. My experience with Miles was very good and satisfying. Follow Miles' guidance and courseware, and you would reach your ${
        isCMA ? "CMA" : "CPA"
      } destination soon. Miles is simply a path breaker!`,
      des: `${
        isCMA ? "CMA" : "CPA"
      } cleared fundamentals, boosting my career. Miles' guidance is key. It's a path-breaker for ${
        isCMA ? "CMA" : "CPA"
      } success!`,

      compLogo: "/newImages/MilesEducationWebsite-2024/Accenture-logo-2.png",
      alliance: "accenture, big 4 accounting firm",
      location: "Delhi, India ",
      linkdin: "https://www.linkedin.com/in/bhavit-sethi-cpa/",
    },
  ];
};

export const get_NEW_CPA_BIG4_DATA = (): AlumniWithoutVideo[] => [
  {
    name: "Dhyey Jatania",
    img: "/newImages/MilesEducationWebsite-2024/alumni/cpa/jatania.png",
    alt: "dhey jatania, cpa alumni - miles education",
    description:
      "Doing CPA with Miles after my CA was a game-changer since it landed me a ground-breaking role at EY.",
    des: "Doing CPA with Miles after my CA was a game-changer since it landed me a ground-breaking role at EY.",
    compLogo: "/newImages/MilesEducationWebsite-2024/EY.png",
    alliance: "ey, big 4 accounting firm",
    location: "Mumbai, India",
    linkdin: "https://www.linkedin.com/in/dhyey-jatania-4b85b4a4/",
  },
  {
    name: "Salam Mohammed",
    img: "/newImages/MilesEducationWebsite-2024/alumni/cpa/salam.png",
    alt: "salam mohammed, cpa alumni - miles education",
    description:
      "I joined Miles after watching Varun in the demo class. I got great support from the miles team in all aspects.",
    des: "I joined Miles after watching Varun in the demo class. I got great support from the miles team in all aspects.",
    compLogo: "/newImages/MilesEducationWebsite-2024/KPMG.png",
    alliance: "kpmg, big 4 accounting firm",
    location: "Emakulum, India",
    linkdin: "https://www.linkedin.com/in/salam-mohammed-196b22134/",
  },
  {
    name: "Prathamesh Shinde",
    img: "/newImages/MilesEducationWebsite-2024/alumni/cpa/shinde.png",
    alt: "prathamesh shinde, cpa alumni - miles education",
    description:
      "I felt like I was on cloud nine after earning my US CPA with Miles. I got so many offers, and finally chose PWC.",
    des: "I felt like I was on cloud nine after earning my US CPA with Miles. I got so many offers, and finally chose PWC.",
    compLogo: "/newImages/MilesEducationWebsite-2024/pwc.png",
    alliance: "pwc, big 4 accounting firm",
    location: "Ahmedabad, India ",
    linkdin: "https://www.linkedin.com/in/prathamesh-shinde-220bb785/",
  },
  {
    name: "Bhavit Sethi",
    img: "/newImages/MilesEducationWebsite-2024/alumni/cpa/sethi.png",
    alt: "bhavit sethi, cpa alumni - miles education",
    description:
      "CPA cleared fundamentals, boosting my career. Miles' guidance is key. It's a path-breaker for CPA success!",
    des: "CPA cleared fundamentals, boosting my career. Miles' guidance is key. It's a path-breaker for CPA success!",
    compLogo: "/newImages/MilesEducationWebsite-2024/alumni/logo6.png",
    alliance: "accenture, big 4 accounting firm",
    location: "Delhi, India ",
    linkdin: "https://www.linkedin.com/in/bhavit-sethi-cpa/",
  },
  {
    name: "Taniya Kanwar",
    img: "/newImages/MilesEducationWebsite-2024/alumni/cpa/taniya.png",
    description:
      "CPA with Miles gave me the wings to fly at Deloitte. The Miles team helped me at every stage of my journey.",
    des: "CPA with Miles gave me the wings to fly at Deloitte. The Miles team helped me at every stage of my journey.",
    compLogo: "/newImages/MilesEducationWebsite-2024/alumni/logo5.png",
    location: "San Antonio, TX",
    alliance: "RSM Alliance",
    linkdin: "https://www.linkedin.com/in/taniya-kanwar-6a941910a/",
  },
  {
    name: "Disha Rungta",
    img: "/newImages/MilesEducationWebsite-2024/alumni/cpa/disha.png",
    alt: "bhavit sethi, cpa alumni - miles education",
    description:
      "Thanks to Miles, I'm diving into a thrilling new chapter in the US. This transition is huge for me, and I'm excited for what's next!",
    des: "Thanks to Miles, I'm diving into a thrilling new chapter in the US. This transition is huge for me, and I'm excited for what's next!",
    compLogo: "/newImages/MilesEducationWebsite-2024/alumni/logo6.png",
    alliance: "accenture, big 4 accounting firm",
    location: "Delhi, India ",
    linkdin: "https://www.linkedin.com/in/disha-rungta/",
  },
  {
    name: "Vinay Kumar",
    img: "/newImages/MilesEducationWebsite-2024/alumni/cpa/vinay.png",
    alt: "bhavit sethi, cpa alumni - miles education",
    description:
      "With Miles it was a smooth journey of not just all round support and improvement but also elevating my confidence and achieving my dream.",
    des: "With Miles it was a smooth journey of not just all round support and improvement but also elevating my confidence and achieving my dream.",
    compLogo: "/newImages/MilesEducationWebsite-2024/alumni/logo7.png",
    alliance: "accenture, big 4 accounting firm",
    location: "Delhi, India ",
    linkdin: "http://linkedin.com/in/402581189",
  },
  {
    name: "Vamshi Teja",
    img: "/newImages/MilesEducationWebsite-2024/alumni/cpa/vamshi.png",
    alt: "Vamshi Teja cpa alumni - miles education",
    description:
      "Moving to the US has been a transformative journey. Thanks to Miles, I’ve had an incredible year and am thrilled to begin my new job!",
    des: "Moving to the US has been a transformative journey. Thanks to Miles, I’ve had an incredible year and am thrilled to begin my new job!",
    compLogo: "/newImages/MilesEducationWebsite-2024/alumni/logo10.png",
    alliance: "accenture, big 4 accounting firm",
    location: "Delhi, India ",
    linkdin: "https://www.linkedin.com/in/bhavit-sethi-cpa/",
  },
  {
    name: "Vijay Reddy",
    img: "/newImages/MilesEducationWebsite-2024/alumni/cpa/vijay.png",
    alt: "bhavit sethi, cpa alumni - miles education",
    description:
      "With Miles by my side, I turned my dream of a US accounting career into a thriving reality. Their expertise made all the difference in my success.",
    des: "With Miles by my side, I turned my dream of a US accounting career into a thriving reality. Their expertise made all the difference in my success.",
    compLogo: "/newImages/MilesEducationWebsite-2024/alumni/logo10.png",
    alliance: "accenture, big 4 accounting firm",
    location: "Delhi, India ",
    linkdin: "https://www.linkedin.com/in/bhavit-sethi-cpa/",
  },
  {
    name: "Hyder Syed",
    img: "/newImages/MilesEducationWebsite-2024/alumni/cpa/hyder.png",
    alt: "Hyder Syed, cpa alumni - miles education",
    description:
      "Choosing the Miles US Pathway was a smart move. Thanks to Miles Placement Team for guiding me through it. I've embraced, conquered, and now I'm ready to fly!",
    des: "Choosing the Miles US Pathway was a smart move. Thanks to Miles Placement Team for guiding me through it. I've embraced, conquered, and now I'm ready to fly!",
    compLogo:
      "/newImages/MilesEducationWebsite-2024/cma/cma-home/companylogo-mbe.png",
    alliance: "accenture, big 4 accounting firm",
    location: "Delhi, India ",
    linkdin: "http://linkedin.com/in/hyder-syed",
  },
  {
    name: "Manisha Kulkarni",
    img: "/newImages/MilesEducationWebsite-2024/alumni/cpa/mannisha.png",
    alt: "bhavit sethi, cpa alumni - miles education",
    description:
      "Miles helped me master my field and turn my American Dream into reality by securing my placement in the US. Grateful for their expertise and support.",
    des: "Miles helped me master my field and turn my American Dream into reality by securing my placement in the US. Grateful for their expertise and support.",
    compLogo: "/newImages/MilesEducationWebsite-2024/alumni/logo4.png",
    alliance: "accenture, big 4 accounting firm",
    location: "Delhi, India ",
    linkdin: "https://www.linkedin.com/in/bhavit-sethi-cpa/",
  },
  {
    name: "Anand Sharma",
    img: "/newImages/MilesEducationWebsite-2024/alumni/cpa/anand.png",
    alt: "bhavit sethi, cpa alumni - miles education",
    description:
      "The Miles US Pathway program was a game-changer for my career growth. Massive thanks to the Miles Placement team for their invaluable support.",
    des: "The Miles US Pathway program was a game-changer for my career growth. Massive thanks to the Miles Placement team for their invaluable support.",
    compLogo: "/newImages/MilesEducationWebsite-2024/alumni/logo8.png",
    alliance: "accenture, big 4 accounting firm",
    location: "Delhi, India ",
    linkdin: "https://in.linkedin.com/in/anand-sharma-080b0314a",
  },
  {
    name: "Lavika Sharma",
    img: "/newImages/MilesEducationWebsite-2024/alumni/cpa/lavika.png",
    alt: "Lavika Sharma, cpa alumni - miles education",
    description:
      "Thanks to Miles, my US work dream is now a reality! Ready for the next career chapter with a bag full of wins waiting.",
    des: "Thanks to Miles, my US work dream is now a reality! Ready for the next career chapter with a bag full of wins waiting.",
    compLogo: "/newImages/MilesEducationWebsite-2024/alumni/logo9.png",
    alliance: "accenture, big 4 accounting firm",
    location: "Delhi, India ",
    linkdin: "https://www.linkedin.com/in/lavika-sharma-6b9703165/",
  },
  {
    name: "Aakash Bahudhanam",
    img: "/newImages/MilesEducationWebsite-2024/alumni/cpa/aaakash.png",
    alt: "Aakash, cpa alumni - miles education",
    description:
      "Miles made my dream of a US accounting career a reality.Their expertise,support were invaluable & now I'm thriving in my new role,thanks to them.",
    des: "Miles made my dream of a US accounting career a reality.Their expertise,support were invaluable & now I'm thriving in my new role,thanks to them.",
    compLogo: "/newImages/MilesEducationWebsite-2024/alumni/logo4.png",
    alliance: "accenture, big 4 accounting firm",
    location: "Delhi, India ",
    linkdin: "https://www.linkedin.com/in/aakash-bahudhanam/",
  },
];

export const getPublishedDateFormat = (publish_date: string) => {
  const date = new Date(publish_date);

  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date: "${publish_date}"`);
  }

  // Format the date as MM/DD/YYYY
  const formattedDate = `${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()}`;
  return formattedDate;
};

export const removeLastSlashAndAfter = (path: string) =>
  path.slice(0, path.lastIndexOf("/"));

export const capitalizeWords = (str: string) => {
  return str
    .split(/[-_\s]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(str.match(/[-_\s]/)?.[0] || "");
};

export const CMA_PAGE_FINANCIAL_PLANNING_PART_1 = [
  {
    title: "Introduction",
    description: "Your Roadmap to the Future of Finance",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma-visit/financial_planning_intro.webp",
    planneingURLKeys: "Z09CbtTvLio?si=9CPiJjEGV0R_jSHv",
  },
  {
    title: "Section A",
    description: "External Financial Reporting Decision",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma-visit/part1_section_A.webp",
    planneingURLKeys: "B8kCCkf34OM?si=WsBbA6fq-Qvti9k5",
  },
  {
    title: "Section B",
    description: "YPlanning, Budgeting & Forecasting",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma-visit/part1_section_B.webp",
    planneingURLKeys: "TpzHtk5hXPc?si=zK4SVFUk58PSALfm",
  },
  {
    title: "Section C",
    description: "Performance Management",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma-visit/part1_section_C.webp",
    planneingURLKeys: "0LNhTI_ws8s?si=TfuTZO68S67z44LJ",
  },
  {
    title: "Section D",
    description: "Cost Management",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma-visit/part1_section_D.webp",
    planneingURLKeys: "tQBf19hHjqg?si=CBpr-mGVrwHFrxA6",
  },
  {
    title: "Section E",
    description: "Internal Controls",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma-visit/part1_section_E.webp",
    planneingURLKeys: "dClMvmmpc3k?si=_fHJ-n1pVQiqQWkE",
  },
];
export const CMA_FINANCIAL_PLANNING_PART_1 = [
  {
    title: "Introduction",
    description: "Your Roadmap to the Future of Finance",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/financial_planning_intro.webp",
    planneingURLKeys: "Z09CbtTvLio?si=9CPiJjEGV0R_jSHv",
  },
  {
    title: "Section A",
    description: "External Financial Reporting Decision",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/part1_section_A.webp",
    planneingURLKeys: "B8kCCkf34OM?si=WsBbA6fq-Qvti9k5",
  },
  {
    title: "Section B",
    description: "YPlanning, Budgeting & Forecasting",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/part1_section_B.webp",
    planneingURLKeys: "TpzHtk5hXPc?si=zK4SVFUk58PSALfm",
  },
  {
    title: "Section C",
    description: "Performance Management",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/part1_section_C.webp",
    planneingURLKeys: "0LNhTI_ws8s?si=TfuTZO68S67z44LJ",
  },
  {
    title: "Section D",
    description: "Cost Management",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/part1_section_D.webp",
    planneingURLKeys: "tQBf19hHjqg?si=CBpr-mGVrwHFrxA6",
  },
  {
    title: "Section E",
    description: "Internal Controls",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/part1_section_E.webp",
    planneingURLKeys: "dClMvmmpc3k?si=_fHJ-n1pVQiqQWkE",
  },
];

export const CMA_PAGE_FINANCIAL_PLANNING_PART_2 = [
  {
    title: "Section A",
    description: "Financial Statement Analysis",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma-visit/part2_section_A.webp",
    planneingURLKeys: "j2bTXlwBQDY?si=2qWN2Py8nSAtUk1-",
  },
  {
    title: "Section B",
    description: "Corporate Finance",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma-visit/part2_section_B.webp",
    planneingURLKeys: "HVN96VHz07M?si=XK56oHHwaT9C5j36",
  },
  {
    title: "Section C",
    description: "Decision Analysis",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma-visit/part2_section_C.webp",
    planneingURLKeys: "cDuKPAwzGFY?si=NhWGoJjTAyBD_dVg",
  },
  {
    title: "Section D",
    description: "Risk Management",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma-visit/part2_section_D.webp",
    planneingURLKeys: "6mNl9kUOpvY?si=CykGvpcGPOApLy7k",
  },
  {
    title: "Section E",
    description: "Investment Decisions",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma-visit/part2_section_E.webp",
    planneingURLKeys: "UkSy6WArCS8?si=Zxfj8gjO-l7uTOuu",
  },
  // {
  //   title: "Section F",
  //   description: "Professional Ethics",
  //   planningImage: "/cma/visit/part2_section_F.png",
  //   planneingURLKeys: "dClMvmmpc3k?si=_fHJ-n1pVQiqQWkE",
  // },
];
export const CMA_FINANCIAL_PLANNING_PART_2 = [
  {
    title: "Section A",
    description: "Financial Statement Analysis",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/part2_section_A.webp",
    planneingURLKeys: "j2bTXlwBQDY?si=2qWN2Py8nSAtUk1-",
  },
  {
    title: "Section B",
    description: "Corporate Finance",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/part2_section_B.webp",
    planneingURLKeys: "HVN96VHz07M?si=XK56oHHwaT9C5j36",
  },
  {
    title: "Section C",
    description: "Decision Analysis",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/part2_section_C.webp",
    planneingURLKeys: "cDuKPAwzGFY?si=NhWGoJjTAyBD_dVg",
  },
  {
    title: "Section D",
    description: "Risk Management",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/part2_section_D.webp",
    planneingURLKeys: "6mNl9kUOpvY?si=CykGvpcGPOApLy7k",
  },
  {
    title: "Section E",
    description: "Investment Decisions",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/part2_section_E.webp",
    planneingURLKeys: "UkSy6WArCS8?si=Zxfj8gjO-l7uTOuu",
  },
  {
    title: "Section F",
    description: "Professional Ethics",
    planningImage:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cma/part2_section_F.webp",
    planneingURLKeys: "vJ3PKl_PgUE?si=EQJM4FFTnHk6fsbx",
  },
];

export const CMA_PAGE_INSTRUCTOR_YOUTUBE_DETAILS = [
  {
    instructorURLKey: "eXnV-Eb-INA",
    instructorImageURL:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/jason_cherubini.png?tr=f-webp",
    instructorName: "Jason Cherubini",
    instructorProfession: "Academic Entrepreneur",
  },
  {
    instructorURLKey: "-V5IdZl33vw",
    instructorImageURL:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/jamar_cobb_dennard.png?tr=f-webp",
    instructorName: "Jamar Dennard",
    instructorProfession: "Attorney",
  },
  {
    instructorURLKey: "iorxsMu6pyA",
    instructorImageURL:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/gary_cokins.png?tr=f-webp",
    instructorName: "Gary Cokins",
    instructorProfession: "Author Entrepreneur",
  },
  {
    instructorURLKey: "Mb-iHAfU-uY",
    instructorImageURL:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/mfon_akpan.png?tr=f-webp",
    instructorName: "Dr. Mfon Akpan",
    instructorProfession: "Accountant",
  },
  {
    instructorURLKey: "sCnbUjLGem4",
    instructorImageURL:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/floran_syler_woods.png?tr=f-webp",
    instructorName: "Dr. Floran Woods",
    instructorProfession: "Accountant",
  },
  {
    instructorURLKey: "ePLhB9O1FdA",
    instructorImageURL:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/mick_caroll.png?tr=f-webp",
    instructorName: "Dr. Mick Carroll",
    instructorProfession: "Accountant",
  },
  {
    instructorURLKey: "uhUyZn4WuOI",
    instructorImageURL:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/roger_mayer.png?tr=f-webp",
    instructorName: "Dr. Roger Mayer",
    instructorProfession: "Accountant",
  },
  {
    instructorURLKey: "ash9cPoNV2U",
    instructorImageURL:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/sherrika_sanders.png?tr=f-webp",
    instructorName: "Sherrika Sanders",
    instructorProfession: "Career Coach",
  },
];

export const CMA_PAGE_CORPORATE_TIEUPS_CARDS = [
  {
    id: 1,
    bg: "/cpa/d-big4.png",
    cardName: "Big 4 & Public Accounting Firms",
    btnActive: "text-[#000] border-[#3174d6]",
  },
  {
    id: 2,
    bg: "/cpa/d-f&a.png",
    cardName: "F&A Consulting",
    btnActive: "text-[#000] border-[#3174d6]",
  },
  {
    id: 3,
    bg: "/cpa/d-financial.png",
    cardName: "Financial Services",
    btnActive: "text-[#000] border-[#3174d6]",
  },
  {
    id: 4,
    bg: "/cpa/d-other.png",
    cardName: "Others (Including Global Fortune 500)",
    btnActive: "text-[#000] border-[#3174d6]",
  },
];

export const USP_SLIDES_DATA = [
  {
    href: "https://www.mileseducation.com/blog/accounting/embracing-spooktacular-culture-shocks-desi-accountants-us",
    imgSrc: "/blogs/usp_blog3.webp",
    imgAlt: "Special Offer",
    title:
      "Embracing the Spooktacular: Culture Shocks for Desi Accountants in the US",
    readTime: "3 min read",
  },
  {
    href: "https://www.mileseducation.com/blog/accounting/hogwarts-global-accounting-careers",
    imgSrc: "/blogs/usp_blog1.webp",
    imgAlt: "Special Offer",
    title: "Miles Education: The Hogwarts of Global Accounting Careers",
    readTime: "3 min read",
  },
  {
    href: "https://www.mileseducation.com/blog/accounting/how-indian-accountants-tackling-us-accounting-shortage",
    imgSrc: "/blogs/usp_blog2.webp",
    imgAlt: "Special Offer",
    title:
      "The Audit Avengers: How Indian Accountants Are Tackling the US Shortage!",
    readTime: "4 min read",
  },
  {
    href: "https://www.mileseducation.com/blog/accounting/embracing-spooktacular-culture-shocks-desi-accountants-us",
    imgSrc: "/blogs/usp_blog3.webp",
    imgAlt: "Special Offer",
    title:
      "Embracing the Spooktacular: Culture Shocks for Desi Accountants in the US",
    readTime: "3 min read",
  },
  {
    href: "https://www.mileseducation.com/blog/accounting/hogwarts-global-accounting-careers",
    imgSrc: "/blogs/usp_blog1.webp",
    imgAlt: "Special Offer",
    title: "Miles Education: The Hogwarts of Global Accounting Careers",
    readTime: "3 min read",
  },
  {
    href: "https://www.mileseducation.com/blog/accounting/how-indian-accountants-tackling-us-accounting-shortage",
    imgSrc: "/blogs/usp_blog2.webp",
    imgAlt: "Special Offer",
    title:
      "The Audit Avengers: How Indian Accountants Are Tackling the US Shortage!",
    readTime: "4 min read",
  },
];

export const CMA_PAGE_CORPORATE_TIEUPS_CARDS_MOBILE = [
  {
    id: 1,
    bg: "/home/corporateTieUps/big_img.png",
    cardName: "Big 4 & ",
    cardName1: "Public Accounting Firms",
    btnActive: "text-[#000] border-[#3174d6]",
  },

  {
    id: 2,
    bg: "/home/corporateTieUps/financeconsult.png",
    cardName: "F&A",
    cardName1: "Consulting",
    btnActive: "text-[#000] border-[#3174d6]",
  },
  {
    id: 3,
    bg: "/home/corporateTieUps/finservice.png",
    cardName: "Financial",
    cardName1: "Services",
    btnActive: "text-[#000] border-[#3174d6]",
  },
  {
    id: 4,
    bg: "/home/corporateTieUps/fortune500.png",
    cardName: "Others",
    cardName1: "(Including Global Fortune 500)",
    btnActive: "text-[#000] border-[#3174d6]",
  },
];

export const CPA_PAGE_CORPORATE_TIEUPS_CARDS_MOBILE = [
  {
    id: 1,
    bg: "/cpa/mbig4.png",
    cardName: "Big 4 & ",
    cardName1: "Public Accounting Firms",
    btnActive: "text-[#000] border-[#3174d6]",
  },

  {
    id: 2,
    bg: "/cpa/mf&a.png",
    cardName: "F&A",
    cardName1: "Consulting",
    btnActive: "text-[#000] border-[#3174d6]",
  },
  {
    id: 3,
    bg: "/cpa/mfinance.png",
    cardName: "Financial",
    cardName1: "Services",
    btnActive: "text-[#000] border-[#3174d6]",
  },
  {
    id: 4,
    bg: "/cpa/d-other.png",
    cardName: "Others ",
    cardName1: "(Including Global Fortune 500)",
    btnActive: "text-[#000] border-[#3174d6]",
  },
];

export const CPA_PAGE_CORPORATE_TIEUPS_CARDS_MOB = [
  {
    id: 1,
    bg: "/CPA_New/mob/big_4.png",
    cardName: "Big 4 & ",
    cardName1: "Public Accounting Firms",
    btnActive: "text-[#000] border-[#3174d6]",
  },

  {
    id: 2,
    bg: "/CPA_New/mob/fa_consulting.png",
    cardName: "F&A",
    cardName1: "Consulting",
    btnActive: "text-[#000] border-[#3174d6]",
  },
  {
    id: 3,
    bg: "/CPA_New/mob/financial_services.png",
    cardName: "Financial",
    cardName1: "Services",
    btnActive: "text-[#000] border-[#3174d6]",
  },
  {
    id: 4,
    bg: "/CPA_New/mob/others.png",
    cardName: "Others ",
    cardName1: "(Including Global Fortune 500)",
    btnActive: "text-[#000] border-[#3174d6]",
  },
];

export const BANNER_SWIPER_COMPANY_ITEMS = [
  {
    companyImageURL:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-cma-course-visit/kpmg.webp",
    companyImageWidth: 100,
    companyImageheight: 48,
  },
  {
    companyImageURL:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-cma-course-visit/ey.webp",
    companyImageWidth: 57,
    companyImageheight: 66,
  },
  {
    companyImageURL:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-cma-course-visit/accenture.webp",
    companyImageWidth: 100,
    companyImageheight: 40,
  },
  {
    companyImageURL:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-cma-course-visit/pwc.webp",
    companyImageWidth: 63,
    companyImageheight: 48,
  },
  {
    companyImageURL: "/cpa/rsm.png",
    companyImageWidth: 86,
    companyImageheight: 48,
  },
  {
    companyImageURL: "/cpa/jp_morgan.png",
    companyImageWidth: 203,
    companyImageheight: 29,
  },
  {
    companyImageURL: "/cpa/amazon.png",
    companyImageWidth: 90,
    companyImageheight: 33,
  },
  {
    companyImageURL: "/cpa/bdo.png",
    companyImageWidth: 73,
    companyImageheight: 38,
  },
  {
    companyImageURL: "/cpa/gt.png",
    companyImageWidth: 149,
    companyImageheight: 27,
  },
  {
    companyImageURL:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-cma-course-visit/deloitte.webp",
    companyImageWidth: 100,
    companyImageheight: 24,
  },
];

export const CMA_PAGE_BLOGS_SECTION_DATA = [
  {
    blogKeyURL: "blogs/cma-course-details-full-form-eligibility-duration",
    blogImageSrc: "blog1",
    blogTitle: "Your CMA Survival Kit: All the Essentials in One Guide!",
    blogTimeToRead: "2 min read",
  },
  {
    blogKeyURL: "blogs/scope-of-us-cma-in-india-usa-2024",
    blogImageSrc: "blog2",
    blogTitle: "2024 Career Forecast: US CMA Opportunities in India & Beyond",
    blogTimeToRead: "2 min read",
  },
  {
    blogKeyURL:
      "blogs/cma-in-india-vs-usa-scope-opportunities-for-accountants-explored",
    blogImageSrc: "blog3",
    blogTitle: "India or USA? The Great CMA Career Debate!",
    blogTimeToRead: "3 min read",
  },
];

export const CPA_PAGE_BLOGS_SECTION_DATA = [
  {
    blogKeyURL: "blogs/cpa-salary-in-usa-and-india-average-to-highest-2024",
    blogImageSrc: "blog1",
    blogTitle:
      "Cha-Ching! CPA Salaries in USA vs India – What You Could Be Earning in 2024",
    blogTimeToRead: "4 min read",
  },
  {
    blogKeyURL: "blogs/dream-to-work-in-the-us-is-now-possible-through-cpa",
    blogImageSrc: "blog2",
    blogTitle: "Pack Your Bags! CPA is Your Fast-Track to a US Career",
    blogTimeToRead: "4 min read",
  },
  {
    blogKeyURL: "blogs/what-is-the-scope-for-us-cpa-in-india",
    blogImageSrc: "blog3",
    blogTitle: " US CPA in India: Is It Worth the Hype?",
    blogTimeToRead: "4 min read",
  },
];

export const ACCOUNTING_PAGE_BLOGS_SECTION_DATA = [
  {
    blogKeyURL: "blogs/cma-course-details-full-form-eligibility-duration",
    blogImageSrc: "blog1",
    blogTitle: "Your CMA Survival Kit: All the Essentials in One Guide!",
    blogTimeToRead: "2 min read",
  },
  {
    blogKeyURL: "blogs/cpa-salary-in-usa-and-india-average-to-highest-2024",
    blogImageSrc: "blog1",
    blogTitle:
      "Cha-Ching! CPA Salaries in USA vs India – What You Could Be Earning in 2024",
    blogTimeToRead: "4 min read",
  },
  {
    blogKeyURL: "blogs/scope-of-us-cma-in-india-usa-2024",
    blogImageSrc: "blog2",
    blogTitle: "2024 Career Forecast: US CMA Opportunities in India & Beyond",
    blogTimeToRead: "2 min read",
  },
];

export const HC_PAGE_BLOGS_SECTION_DATA = [
  {
    blogKeyURL: "blogs/neet-preparation-tips-by-toppers ",
    blogImageDoc: "/blogs/neet_preparation_tips.jpg",
    blogTitle:
      "NEET Preparation Tips by Toppers: How to Score 650+ in Your First Attempt",
    blogTimeToRead: "8 min read",
  },
  {
    blogKeyURL: "blogs/should-i-take-third-drop-for-neet",
    blogImageDoc: "/blogs/third_drop_for_neet.webp",
    blogTitle: "Should I Take a 3rd Drop for NEET? - Reality Check",
    blogTimeToRead: "6 min read",
  },
  {
    blogKeyURL: "blogs/no-neet-rank-pursue-medicine-us ",
    blogImageDoc:
      "/blogs/No_NEET_Rank_No_Problem__Pursuing_Medicine_in_the_US.webp",
    blogTitle: "No NEET Rank? No Problem – Pursuing Medicine in the US",
    blogTimeToRead: "5 min read",
  },
];

export const NURSE_PAGE_BLOGS_SECTION_DATA = [
  {
    blogKeyURL: "rn-nursing-positions-in-us",
    blogImageNrs: "/blogs/rn_nursing_positions_in_us.webp",
    blogTitle: "How RN Nursing Positions in the US Can Transform Your Life?",
    blogTimeToRead: "5 min read",
  },
  {
    blogKeyURL: "types-of-nurses-and-salaries",
    blogImageNrs: "/blogs/types_of_nurses_and_salaries_in_us.webp",
    blogTitle: "Types of Nurses and Salaries in the US: Which Pays the Most?",
    blogTimeToRead: "6 min read",
  },
  {
    blogKeyURL: "best-book-for-nclex-rn",
    blogImageNrs: "/blogs/book_for_nclex.webp",
    blogTitle:
      "Best Book for NCLEX-RN – Your Ultimate Guide to Acing the Exam!",
    blogTimeToRead: "6 min read",
  },
];

export const MBA_PAGE_BLOGS_SECTION_DATA = [
  {
    blogKeyURL: "blogs/career-opportunities-after-mba",
    blogImageMba: "blogs/mba_career_opportunities.webp",
    blogTitle:
      "Career Opportunities After MBA: Unlocking Your Path to the Finance Jungle!",
    blogTimeToRead: "4 min read",
  },
  {
    blogKeyURL: "blogs/miles-mba-abroad-career-for-Indian-students",
    blogImageMba: "blogs/miles_mba_career_abroad.webp",
    blogTitle: "Miles MBA Abroad: International Career for Indian Students",
    blogTimeToRead: "4 min read",
  },
  {
    blogKeyURL: "blogs/gmat-or-no-gmat-exploring-flexible-pathway-for-us-mba",
    blogImageMba: "blogs/gmat_or_no_gmat.webp",
    blogTitle: "GMAT or No GMAT? Exploring Flexible Pathway for Your US MBA",
    blogTimeToRead: "4 min read",
  },
];

export const BATCH_START_FOR_STUDENTS_ROWS_CPA = [
  {
  number: "1",
  date: "25-Oct-2026",
  day: "Sunday",
  time: "5:00 PM - 8:00 PM",
  description: "Kaun Banega CPA with Varun Jain",
},
{
  number: "2",
  date: "27-Oct-2026",
  day: "Tuesday",
  time: "7:30 AM - 9:00 AM",
  description: "Orientation Session Class #2",
},
{
  number: "3",
  date: "31-Oct-2026",
  day: "Saturday",
  time: "3:00 PM - 6:00 PM",
  description: "Income Statement and Balance Sheet",
},
{
  number: "4",
  date: "1-Nov-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Notes, Fair Value, SEC and Interim FS",
},
{
  number: "-",
  date: "1-Nov-2026",
  day: "Sunday",
  time: "10:00 AM - 2:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "5",
  date: "5-Nov-2026",
  day: "Thursday",
  time: "7:00 AM - 9:00 AM",
  description: "Matching of Revenues & Expenses, Accounting Changes, Other F/S Presentations",
},
{
  number: "6",
  date: "14-Nov-2026",
  day: "Saturday",
  time: "3:00 PM - 6:00 PM",
  description: "Current Assets & Liabilities",
},
{
  number: "7",
  date: "15-Nov-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Inventory Accounting",
},
{
  number: "-",
  date: "15-Nov-2026",
  day: "Sunday",
  time: "10:00 AM - 2:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "8",
  date: "19-Nov-2026",
  day: "Thursday",
  time: "7:00 AM - 9:00 AM",
  description: "Contingencies",
},
{
  number: "9",
  date: "21-Nov-2026",
  day: "Saturday",
  time: "3:00 PM - 6:00 PM",
  description: "Investment Accounting",
},
{
  number: "10",
  date: "22-Nov-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Fixed Assets",
},
{
  number: "-",
  date: "22-Nov-2026",
  day: "Sunday",
  time: "10:00 AM - 2:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "11",
  date: "26-Nov-2026",
  day: "Thursday",
  time: "7:00 AM - 9:00 AM",
  description: "Intangible Assets",
},
{
  number: "12",
  date: "28-Nov-2026",
  day: "Saturday",
  time: "3:00 PM - 6:00 PM",
  description: "Revenue Recognition",
},
{
  number: "13",
  date: "29-Nov-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Revenue Recognition",
},
{
  number: "-",
  date: "29-Nov-2026",
  day: "Sunday",
  time: "10:00 AM - 2:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "14",
  date: "3-Dec-2026",
  day: "Thursday",
  time: "7:00 AM - 9:00 AM",
  description: "Long-term Contruction Contracts",
},
{
  number: "15",
  date: "5-Dec-2026",
  day: "Saturday",
  time: "3:00 PM - 6:00 PM",
  description: "Present Value & Bonds",
},
{
  number: "16",
  date: "6-Dec-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Lease Accounting",
},
{
  number: "-",
  date: "6-Dec-2026",
  day: "Sunday",
  time: "10:00 AM - 2:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "17",
  date: "10-Dec-2026",
  day: "Thursday",
  time: "7:00 AM - 9:00 AM",
  description: "Troubled Debt Accounting",
},
{
  number: "18",
  date: "12-Dec-2026",
  day: "Saturday",
  time: "3:00 PM - 6:00 PM",
  description: "Deferred Tax",
},
{
  number: "19",
  date: "13-Dec-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Equity Transactions, Earnings per Share",
},
{
  number: "-",
  date: "13-Dec-2026",
  day: "Sunday",
  time: "10:00 AM - 2:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "20",
  date: "17-Dec-2026",
  day: "Thursday",
  time: "7:00 AM - 9:00 AM",
  description: "Cash Flow Statement",
},
{
  number: "21",
  date: "19-Dec-2026",
  day: "Saturday",
  time: "3:00 PM - 6:00 PM",
  description: "Consolidations",
},
{
  number: "22",
  date: "20-Dec-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Ratio Analysis, Foreign currency transactions",
},
{
  number: "-",
  date: "20-Dec-2026",
  day: "Sunday",
  time: "10:00 AM - 2:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "23",
  date: "24-Dec-2026",
  day: "Thursday",
  time: "7:00 AM - 9:00 AM",
  description: "Not-for-Profit Accounting",
},
{
  number: "24",
  date: "26-Dec-2026",
  day: "Saturday",
  time: "3:00 PM - 6:00 PM",
  description: "Government Accounting",
},
{
  number: "25",
  date: "27-Dec-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Government Accounting",
},
{
  number: "-",
  date: "27-Dec-2026",
  day: "Sunday",
  time: "10:00 AM - 2:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "26",
  date: "31-Dec-2026",
  day: "Thursday",
  time: "7:00 AM - 9:00 AM",
  description: "FAR - Additional Practice - Class #1",
},
];

export const BATCH_START_FOR_NOT_STUDENTS_ROWS_CPA = [
  {
    date: "27-Apr-2025",
    day: "Sunday",
    time: "10:00 AM - 2:00 PM",
    description: "F2F Class FAR 1",
  },
  {
    date: "4-May-2025",
    day: "Sunday",
    time: "10:00 AM - 2:00 PM",
    description: "F2F Class FAR 2",
  },
  {
    date: "11-May-2025",
    day: "Sunday",
    time: "10:00 AM - 2:00 PM",
    description: "F2F Class FAR 3",
  },
  {
    date: "25-May-2025",
    day: "Sunday",
    time: "10:00 AM - 2:00 PM",
    description: "F2F Class FAR 4",
  },
  {
    date: "1-Jun-2025",
    day: "Sunday",
    time: "10:00 AM - 2:00 PM",
    description: "F2F Class FAR 5",
  },
  {
    date: "8-Jun-2025",
    day: "Sunday",
    time: "10:00 AM - 2:00 PM",
    description: "F2F Class FAR 6",
  },
  {
    date: "15-Jun-2025",
    day: "Sunday",
    time: "10:00 AM - 2:00 PM",
    description: "F2F Class FAR 7",
  },
];

export const BATCH_START_FOR_STUDENTS_ROWS_EA = [
  {
    number: "1",
    date: "25-Apr-2026",
    day: "Saturday",
    time: "5:00 PM - 7:00 PM",
    description: "Orientation Session",
  },
  {
    number: "2",
    date: "2-May-2026",
    day: "Saturday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part I - Master Class",
  },
  {
    number: "3",
    date: "3-May-2026",
    day: "Sunday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part I - Class #2",
  },
  {
    number: "4",
    date: "9-May-2026",
    day: "Saturday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part I - Class #3",
  },
  {
    number: "5",
    date: "10-May-2026",
    day: "Sunday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part I - Class #4",
  },
  {
    number: "6",
    date: "16-May-2026",
    day: "Saturday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part I - Class #5",
  },
  {
    number: "7",
    date: "17-May-2026",
    day: "Sunday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part I - Class #6",
  },
  {
    number: "8",
    date: "23-May-2026",
    day: "Saturday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part I - Class #7",
  },
  {
    number: "9",
    date: "24-May-2026",
    day: "Sunday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part I - Class #8",
  },
  {
    number: "10",
    date: "30-May-2026",
    day: "Saturday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part I - Class #9",
  },
  {
    number: "11",
    date: "31-May-2026",
    day: "Sunday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part I - Class #10",
  },
  {
    number: "12",
    date: "6-Jun-2026",
    day: "Saturday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part I - Class #11",
  },
  {
    number: "13",
    date: "7-Jun-2026",
    day: "Sunday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part I - Class #12",
  },
  {
    number: "14",
    date: "13-Jun-2026",
    day: "Saturday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part I - Class #13",
  },
];

export const BATCH_START_FOR_NOT_STUDENTS_ROWS_EA = [
  {
    date: "27-Apr-2025",
    day: "Sunday",
    time: "10:00 AM - 2:00 PM",
    description: "F2F Class FAR 1",
  },
  {
    date: "4-May-2025",
    day: "Sunday",
    time: "10:00 AM - 2:00 PM",
    description: "F2F Class FAR 2",
  },
  {
    date: "11-May-2025",
    day: "Sunday",
    time: "10:00 AM - 2:00 PM",
    description: "F2F Class FAR 3",
  },
  {
    date: "25-May-2025",
    day: "Sunday",
    time: "10:00 AM - 2:00 PM",
    description: "F2F Class FAR 4",
  },
  {
    date: "1-Jun-2025",
    day: "Sunday",
    time: "10:00 AM - 2:00 PM",
    description: "F2F Class FAR 5",
  },
  {
    date: "8-Jun-2025",
    day: "Sunday",
    time: "10:00 AM - 2:00 PM",
    description: "F2F Class FAR 6",
  },
  {
    date: "15-Jun-2025",
    day: "Sunday",
    time: "10:00 AM - 2:00 PM",
    description: "F2F Class FAR 7",
  },
];

export const BATCH_START_FOR_PART2_ROWS_EA = [
  {
    number: "1",
    date: "08-Aug-2026",
    day: "Saturday",
    time: "9:00 AM - 11:30 AM",
    description: "EA 101 Intro Session (Varun Jain)",
  },
  {
    number: "2",
    date: "09-Aug-2026",
    day: "Sunday",
    time: "9:00 AM - 11:30 AM",
    description: "EA Kick-off & SEE Orientation",
  },
  {
    number: "3",
    date: "22-Aug-2026",
    day: "Saturday",
    time: "9:00 AM - 11:30 AM",
    description: "Tax Formula, Filing Status & Dependents",
  },
  {
    number: "4",
    date: "23-Aug-2026",
    day: "Sunday",
    time: "9:00 AM - 11:30 AM",
    description: "Filing Considerations & Specialized Returns",
  },
  {
    number: "6",
    date: "29-Aug-2026",
    day: "Saturday",
    time: "9:00 AM - 11:30 AM",
    description: "Gross Income: Inclusions & Exclusions",
  },
  {
    number: "7",
    date: "30-Aug-2026",
    day: "Sunday",
    time: "9:00 AM - 11:30 AM",
    description: "Advanced Income Items",
  },
  {
    number: "9",
    date: "05-Sep-2026",
    day: "Saturday",
    time: "9:00 AM - 11:30 AM",
    description: "Capital Gains, Basis & Retirement Income",
  },
  {
    number: "10",
    date: "06-Sep-2026",
    day: "Sunday",
    time: "9:00 AM - 11:30 AM",
    description: "Rental Real Estate, Passive Activity & Loss Limitations",
  },
  {
    number: "12",
    date: "12-Sep-2026",
    day: "Saturday",
    time: "9:00 AM - 11:30 AM",
    description: "Adjustments to Income, AGI & Itemized Deductions",
  },
  {
    number: "13",
    date: "13-Sep-2026",
    day: "Sunday",
    time: "9:00 AM - 11:30 AM",
    description: "Advanced - Adjustments to Income & Itemized Deductions",
  },
  {
    number: "15",
    date: "19-Sep-2026",
    day: "Saturday",
    time: "9:00 AM - 11:30 AM",
    description: "Credits, QBI & Special Individual Taxes",
  },
  {
    number: "16",
    date: "20-Sep-2026",
    day: "Sunday",
    time: "9:00 AM - 11:30 AM",
    description: "Advanced - Taxes and Tax Credits",
  },
  {
    number: "18",
    date: "26-Sep-2026",
    day: "Saturday",
    time: "9:00 AM - 11:30 AM",
    description: "Self-Employment, Procedures & 1040 Capstone",
  },
  {
    number: "19",
    date: "27-Sep-2026",
    day: "Sunday",
    time: "9:00 AM - 11:30 AM",
    description: "EA Part 1 Review",
  },
  {
    number: "21",
    date: "03-Oct-2026",
    day: "Saturday",
    time: "9:00 AM - 11:30 AM",
    description: "Business Income, Accounting Methods & Depreciation",
  },
  {
    number: "22",
    date: "04-Oct-2026",
    day: "Sunday",
    time: "9:00 AM - 11:30 AM",
    description: "Advanced - Business Taxation",
  },
  {
    number: "24",
    date: "10-Oct-2026",
    day: "Saturday",
    time: "9:00 AM - 11:30 AM",
    description: "Business Property Transactions",
  },
  {
    number: "25",
    date: "11-Oct-2026",
    day: "Sunday",
    time: "9:00 AM - 11:30 AM",
    description: "Advanced - Business Property Transactions",
  },
  {
    number: "27",
    date: "17-Oct-2026",
    day: "Saturday",
    time: "9:00 AM - 11:30 AM",
    description: "C Corporations Taxation",
  },
  {
    number: "28",
    date: "18-Oct-2026",
    day: "Sunday",
    time: "9:00 AM - 11:30 AM",
    description: "Advanced - C Corporations Taxation",
  },
  {
    number: "30",
    date: "24-Oct-2026",
    day: "Saturday",
    time: "9:00 AM - 11:30 AM",
    description: "S Corporations & Partnerships",
  },
  {
    number: "31",
    date: "25-Oct-2026",
    day: "Sunday",
    time: "9:00 AM - 11:30 AM",
    description: "EA Part 2 Review",
  },
  {
    number: "33",
    date: "31-Oct-2026",
    day: "Saturday",
    time: "9:00 AM - 11:30 AM",
    description: "Practice Before the IRS, Circular 230 & Examinations",
  },
  {
    number: "34",
    date: "01-Nov-2026",
    day: "Sunday",
    time: "9:00 AM - 11:30 AM",
    description: "Advanced - IRS Representations",
  },
  {
    number: "36",
    date: "07-Nov-2026",
    day: "Saturday",
    time: "9:00 AM - 11:30 AM",
    description: "Collections, Taxpayer Relief, Ethics & Capstone",
  },
  {
    number: "37",
    date: "15-Nov-2026",
    day: "Sunday",
    time: "9:00 AM - 11:30 AM",
    description: "EA Part 3 Review",
  },
];

export const BATCH_START_FOR_PART3_ROWS_EA = [
  {
    number: "1",
    date: "24-Apr-2027",
    day: "Saturday",
    time: "5:00 PM - 7:00 PM",
    description: "Orientation Session",
  },
  {
    number: "2",
    date: "1-May-2027",
    day: "Saturday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part 3 - Master Class",
  },
  {
    number: "3",
    date: "2-May-2027",
    day: "Sunday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part 3 - Class #2",
  },
  {
    number: "4",
    date: "8-May-2027",
    day: "Saturday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part 3 - Class #3",
  },
  {
    number: "5",
    date: "9-May-2027",
    day: "Sunday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part 3 - Class #4",
  },
  {
    number: "6",
    date: "15-May-2027",
    day: "Saturday",
    time: "5:00 PM - 7:00 PM",
    description: "EA Part 3 - Class #5",
  },
];

export const BATCH_START_FOR_STUDENTS_ROWS_CMA = [
  {
  number: "1",
  date: "25-Oct-2026",
  day: "Sunday",
  time: "2:00 PM - 5:00 PM",
  description: "Kaun Banega CMA, with Varun Jain",
},
{
  number: "2",
  date: "27-Oct-2026",
  day: "Tuesday",
  time: "7:30 PM - 9:00 PM",
  description: "Orientation Session",
},
{
  number: "3",
  date: "31-Oct-2026",
  day: "Saturday",
  time: "11:30 AM - 2:30 PM",
  description: "Strategic Planning",
},
{
  number: "4",
  date: "1-Nov-2026",
  day: "Sunday",
  time: "11:30 AM - 2:30 PM",
  description: "Strategic Planning",
},
{
  number: "-",
  date: "1-Nov-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "5",
  date: "3-Nov-2026",
  day: "Tuesday",
  time: "7:30 PM - 9:00 PM",
  description: "Budgeting concepts",
},
{
  number: "6",
  date: "5-Nov-2026",
  day: "Thursday",
  time: "7:30 PM - 9:00 PM",
  description: "Budgeting concepts",
},
{
  number: "7",
  date: "11-Nov-2026",
  day: "Wednesday",
  time: "7:30 PM - 9:00 PM",
  description: "Forecasting Techniques",
},
{
  number: "8",
  date: "12-Nov-2026",
  day: "Thursday",
  time: "7:30 PM - 9:00 PM",
  description: "Forecasting Techniques",
},
{
  number: "9",
  date: "14-Nov-2026",
  day: "Saturday",
  time: "11:30 AM - 2:30 PM",
  description: "Budgeting Methodologies",
},
{
  number: "10",
  date: "15-Nov-2026",
  day: "Sunday",
  time: "11:30 AM - 2:30 PM",
  description: "Annual Profit Plan & Supporting Schedules & Top level planning and Analysis",
},
{
  number: "-",
  date: "15-Nov-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "11",
  date: "17-Nov-2026",
  day: "Tuesday",
  time: "7:30 PM - 9:00 PM",
  description: "Cost & Variance Measures",
},
{
  number: "12",
  date: "19-Nov-2026",
  day: "Thursday",
  time: "7:30 PM - 9:00 PM",
  description: "Cost & Variance Measures",
},
{
  number: "13",
  date: "21-Nov-2026",
  day: "Saturday",
  time: "11:30 AM - 2:30 PM",
  description: "Resposibility Centres & Reporting Segment",
},
{
  number: "14",
  date: "22-Nov-2026",
  day: "Sunday",
  time: "11:30 AM - 2:30 PM",
  description: "Cost & Variance Measures & Resposibility Centres & Reporting Segment",
},
{
  number: "-",
  date: "22-Nov-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "15",
  date: "24-Nov-2026",
  day: "Tuesday",
  time: "7:30 PM - 9:00 PM",
  description: "Performance Measures",
},
{
  number: "16",
  date: "26-Nov-2026",
  day: "Thursday",
  time: "7:30 PM - 9:00 PM",
  description: "Performance Measures",
},
{
  number: "17",
  date: "28-Nov-2026",
  day: "Saturday",
  time: "11:30 AM - 2:30 PM",
  description: "Measurement Concepts",
},
{
  number: "18",
  date: "29-Nov-2026",
  day: "Sunday",
  time: "11:30 AM - 2:30 PM",
  description: "Measurement Concepts & Cost & Variance Measures",
},
{
  number: "-",
  date: "29-Nov-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "19",
  date: "1-Dec-2026",
  day: "Tuesday",
  time: "7:30 PM - 9:00 PM",
  description: "Measurement Concepts",
},
{
  number: "20",
  date: "3-Dec-2026",
  day: "Thursday",
  time: "7:30 PM - 9:00 PM",
  description: "Measurement Concepts",
},
{
  number: "21",
  date: "5-Dec-2026",
  day: "Saturday",
  time: "11:30 AM - 2:30 PM",
  description: "Costing Sytems",
},
{
  number: "22",
  date: "6-Dec-2026",
  day: "Sunday",
  time: "11:30 AM - 2:30 PM",
  description: "Costing Systems & Overhead Costs",
},
{
  number: "-",
  date: "6-Dec-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "23",
  date: "8-Dec-2026",
  day: "Tuesday",
  time: "7:30 PM - 9:00 PM",
  description: "Governance, Risk & Compliance - I",
},
{
  number: "24",
  date: "10-Dec-2026",
  day: "Thursday",
  time: "7:30 PM - 9:00 PM",
  description: "Governance, Risk & Compliance - I",
},
{
  number: "25",
  date: "12-Dec-2026",
  day: "Saturday",
  time: "11:30 AM - 2:30 PM",
  description: "Governance , Risk & Compliance -II",
},
{
  number: "26",
  date: "13-Dec-2026",
  day: "Sunday",
  time: "11:30 AM - 2:30 PM",
  description: "System Controls & Security Measured",
},
{
  number: "-",
  date: "13-Dec-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "27",
  date: "15-Dec-2026",
  day: "Tuesday",
  time: "7:30 PM - 9:00 PM",
  description: "System Controls & Security Measured",
},
{
  number: "28",
  date: "17-Dec-2026",
  day: "Thursday",
  time: "7:30 PM - 9:00 PM",
  description: "System Controls & Security Measured",
},
{
  number: "29",
  date: "19-Dec-2026",
  day: "Saturday",
  time: "11:30 AM - 2:30 PM",
  description: "Information Systems & Data Governance",
},
{
  number: "30",
  date: "20-Dec-2026",
  day: "Sunday",
  time: "11:30 AM - 2:30 PM",
  description: "Technology - Enabled Finance Transformation & Data Analytics",
},
{
  number: "-",
  date: "20-Dec-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "31",
  date: "22-Dec-2026",
  day: "Tuesday",
  time: "7:30 PM - 9:00 PM",
  description: "Technology - Enabled Finance Transformation & Data Analytics",
},
{
  number: "32",
  date: "24-Dec-2026",
  day: "Thursday",
  time: "7:30 PM - 9:00 PM",
  description: "Technology - Enabled Finance Transformation & Data Analytics",
},
{
  number: "33",
  date: "26-Dec-2026",
  day: "Saturday",
  time: "11:30 AM - 2:30 PM",
  description: "Income Statement, Balance Sheet, Statement of Cash Flows, Notes to F/S, Integrated Reporting",
},
{
  number: "34",
  date: "27-Dec-2026",
  day: "Sunday",
  time: "11:30 AM - 2:30 PM",
  description: "Fair Value Measurements & Disclosures, Account Receivables",
},
{
  number: "-",
  date: "27-Dec-2026",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "35",
  date: "29-Dec-2026",
  day: "Tuesday",
  time: "7:30 PM - 9:00 PM",
  description: "Inventory",
},
{
  number: "36",
  date: "30-Dec-2026",
  day: "Wednesday",
  time: "7:30 PM - 9:00 PM",
  description: "Investments in Equity & Debt Securities",
},
{
  number: "37",
  date: "2-Jan-2027",
  day: "Saturday",
  time: "11:30 AM - 2:30 PM",
  description: "Stockholder's Equity & Revenue Recognition",
},
{
  number: "38",
  date: "3-Jan-2027",
  day: "Sunday",
  time: "11:30 AM - 2:30 PM",
  description: "Deferred Taxes, Business Combinations & Consolidations",
},
{
  number: "-",
  date: "3-Jan-2027",
  day: "Sunday",
  time: "3:00 PM - 6:00 PM",
  description: "Face-2-Face Class*",
},
{
  number: "39",
  date: "5-Jan-2027",
  day: "Tuesday",
  time: "7:30 PM - 9:00 PM",
  description: "Leases & US GAAP vs IFRS",
},
{
  number: "40",
  date: "7-Jan-2027",
  day: "Thursday",
  time: "7:30 PM - 9:00 PM",
  description: "Tangible Fixed Assets & Intangible Assets",
},
{
  number: "41",
  date: "9-Jan-2027",
  day: "Saturday",
  time: "11:30 AM - 2:30 PM",
  description: "Case based questions",
},
];

export const DOCTOR_HEALTHCARE_FAQS: FAQType[] = [
  {
    id: 1,
    title: "THE WHYs",
    questions: [
      {
        id: 1,
        question:
          "1. Why should I pursue the Miles MBBS-MD U.S. Pathway for Doctors?",
        answer:
          "This program is ideal for NEET aspirants seeking abroad admission for medicine without needing a NEET UG rank. With benefits like a net-zero investment model, the chance to study alongside US students, and a fee structure that’s extremely competitive to other study abroad MBBS programs. Candidates will work/practice in the US for 6 out of 9 years, with a potential to earn ₹2 crore annually upon completion.",
      },
      {
        id: 2,
        question: "2. Why is studying medicine in the US advantageous?",
        answer:
          "Unlike typical MBBS abroad programs, studying medicine in the U.S. provides rigorous training, access to state-of-the-art facilities, and immersive clinical experience. Students benefit from a curriculum that emphasizes both academic excellence and practical skills. This leads to exceptional career opportunities, higher earning potential, and the chance to earn an MD while practicing in some of the world’s most advanced medical settings.",
      },
      {
        id: 3,
        question:
          "3. Why is the Miles MBBS-MD U.S. Pathway for Doctors a financially viable option for studying medicine in the US?",
        answer:
          "The Miles MBBS-MD U.S. Pathway for Doctors offers a net zero investment model, as students earn while they study. With a $125K tuition loan for the initial years, students can repay it with ~$250K in earnings during residency, making it a high-return investment with the potential for an annual salary of ₹2 crore post-completion.",
      },
      {
        id: 4,
        question:
          "4. Why does the job outlook and salary after completing the Miles MBBS-MD U.S. Pathway make it a worthwhile investment?",
        answer:
          "With a high demand for skilled doctors and rewarding salaries, the Miles MBBS-MD U.S. Pathway offers a clear path to a fulfilling and lucrative medical career. Graduates can expect competitive salaries, often exceeding 2 crores annually in the US, depending on their specialty.",
      },
      {
        id: 5,
        question: "5. Why is the clinical training conducted in the US?",
        answer:
          "The clinical training in the Miles MBBS-MD U.S. Pathway for Doctors is conducted in accredited US hospitals, offering students hands-on experience under expert supervision. By training in key metropolitan areas, students gain exposure to diverse patients and the latest medical practices, preparing them for successful careers.",
      },
      {
        id: 6,
        question:
          "6. Why does the Miles MBBS-MD U.S. Pathway for Doctors program span 9 years?",
        answer:
          "The Miles MBBS-MD U.S. Pathway for Doctors spans 9 years, with 6 years in the US This structure combines 3 years of foundational theory, 2 years of clinical rotations, and 4 years of residency, ensuring comprehensive medical training and hands-on experience.",
      },
      {
        id: 7,
        question:
          "7. Why does the Miles MBBS-MD U.S. Pathway for Doctors lead to strong career prospects?",
        answer:
          "Candidates of the Miles MBBS-MD U.S. Pathway earn an MD from accredited US medical schools, setting them up for a rewarding career in the US After passing required licensing exams like the USMLE and completing their residency, they become eligible to practice medicine, with access to high-paying roles and significant career growth opportunities.",
      },
    ],
  },
  {
    id: 2,
    title: "THE WHATs",
    questions: [
      {
        id: 1,
        question: "8. What is the USMLE, and how does it fit into the program?",
        answer:
          "The USMLE (United States Medical Licensing Examination) is a three-step exam required for medical licensure in the US Core Sections <br />" +
          "&nbsp;&nbsp;Step 1 is taken after Year 3 <br />" +
          "&nbsp;&nbsp;Step 2 is taken after Year 5 <br />" +
          "&nbsp;&nbsp;Step 3 is completed during the residency",
      },
      {
        id: 2,
        question:
          "9. What support does the Miles MBBS-MD U.S. Pathway offer during the residency application process?",
        answer:
          "We provide comprehensive support, including mentorship, interview preparation, and application resources.",
      },
      {
        id: 3,
        question:
          "10. What documents are required for the application process?",
        answer:
          "Applicants need: 12th class certificate, scorecard, transcripts, passport, statement of purpose, financial documents, passport-size photos, and, if needed, a resume and recommendation letters.",
      },
      {
        id: 4,
        question:
          "11. What kind of support does Miles offer throughout the program?",
        answer:
          "In comparison to most foreign MBBS abroad programs. With Miles MBBS-MD U.S. Pathway for Doctors (where students earn a prestigious MD) offers comprehensive support including academic and visa assistance, licensing exam prep, and career mentoring.",
      },
    ],
  },
  {
    id: 3,
    title: "THE WHENs",
    questions: [
      {
        id: 1,
        question:
          "12. When should I start the application process for the Miles MBBS-MD U.S. Pathway?",
        answer:
          "It’s recommended to begin your application process during/after graduating from the 12th class. Early application also allows sufficient time for visa processing and financial arrangements.",
      },
      {
        id: 2,
        question:
          "13. When do clinical rotations and residencies typically take place?",
        answer:
          "Clinical rotations begin after completing the pre-medical portion of the program, while residency timelines vary based on the chosen specialty.",
      },
    ],
  },
  {
    id: 4,
    title: "THE HOWs",
    questions: [
      {
        id: 1,
        question:
          "14. How do I get started with the Miles MBBS-MD U.S. Pathway for Doctors?",
        answer:
          "To get started, simply visit our website and fill out the application form. You’ll receive guidance on the next steps, including submitting required documents, preparing for interviews, and securing your spot in the program.",
      },
      {
        id: 2,
        question: "15. How does the US pathway compare to the Indian pathway?",
        answer:
          "The US pathway offers significant advantages over the Indian pathway. Post-MD salaries in the US range from INR 2-4 crores, compared to INR 8-16 lakhs in India." +
          "<br />" +
          "<br />While PG programs at private Indian colleges can cost around INR 1 crore, through the US pathway, you'll earn an annual salary of around $60K (INR 50 lakhs) during residency." +
          "<br />" +
          "<br />This international exposure allows you to learn from world-class medical professionals and access cutting-edge healthcare facilities." +
          "<br />" +
          "<br />Additionally, the ability to repay educational costs through residency earnings helps reduce the financial burden on both students and their families.",
      },
      {
        id: 3,
        question:
          "16. How does the Miles MBBS-MD U.S. Pathway ensure quality education?",
        answer:
          "We collaborate with US-accredited medical institutions and experienced faculty to provide a high-quality, relevant education for international students" +
          "<br />" +
          "<br />Our partner institutions are listed in the World Directory of Medical Schools with an ECFMG® Sponsor Note, making their students eligible to apply to ECFMG for ECFMG Certification and USMLE® as a step towards ECFMG Certification" +
          "<br />" +
          "<br />Partner institutions are also recognized by the Medical Council of Canada making our students eligible to sit Part 1 & Part II of the Medical Council of Canada Qualifying Examination (MCCQE) for their Licentiate of the Medical Council of Canada (LMCC)",
      },
    ],
  },
  {
    id: 5,
    title: "THE WHEREs",
    questions: [
      {
        id: 1,
        question:
          "17. Where can I find more information or speak with a representative?",
        answer:
          'Fill out the form on our website, or email us at <a href="mailto:doctorspathway@mileseducation.com" class="text-blue-500">doctorspathway@mileseducation.com</a>. Our team is ready to assist.',
      },
      {
        id: 2,
        question:
          "18. Where can I find more information or speak with a representative?",
        answer:
          "Graduates secure residencies in US hospitals and can practice medicine in the US or internationally, depending on local licensing.",
      },
      {
        id: 3,
        question: "19. Where does the program take place?",
        answer:
          "The program includes:" +
          "<br />" +
          "&nbsp;&nbsp;3 years of studies in St. Vincent Island" +
          "<br />" +
          "&nbsp;&nbsp;2 years of clinical rotations in the US" +
          "<br />" +
          "&nbsp;&nbsp;4 years of residency in the US",
      },
      {
        id: 4,
        question: "20. Can I return to India after completing the program?",
        answer:
          "Yes, you can return to India, As per MCI, graduates who obtained a license to practice medicine in the USA / UK / Australia / Canada / New Zealand can practice in India directly.",
      },
    ],
  },
];

export const DOCTOR_HEALTHCARE_FAQS_INTER: FAQType[] = [
  {
    id: 1,
    title: "THE WHYs",
    questions: [
      {
        id: 1,
        question:
          "1. Why should I pursue the Miles MBBS-MD U.S. Pathway for Doctors?",
        answer:
          "This program is ideal for aspirants seeking admission in the U.S. for medicine. With benefits like a net-zero investment model, the chance to study alongside US students, and a fee structure that’s extremely competitive to other study abroad MBBS programs. Candidates will work/practice in the US for 6 out of 9 years, with a potential to earn $250K+ annually upon completion.",
      },
      {
        id: 2,
        question: "2. Why is studying medicine in the US advantageous?",
        answer:
          "Unlike typical MBBS abroad programs, studying medicine in the U.S. provides rigorous training, access to state-of-the-art facilities, and immersive clinical experience. Students benefit from a curriculum that emphasizes both academic excellence and practical skills. This leads to exceptional career opportunities, higher earning potential, and the chance to earn an MD while practicing in some of the world’s most advanced medical settings.",
      },
      {
        id: 3,
        question:
          "3. Why is the Miles MBBS-MD U.S. Pathway for Doctors a financially viable option for studying medicine in the US?",
        answer:
          "The Miles MBBS-MD U.S. Pathway for Doctors offers a net zero investment model, as students earn while they study. With a $125K tuition loan for the initial years, students can repay it with ~$250K in earnings during residency, making it a high-return investment.",
      },
      {
        id: 4,
        question:
          "4. Why does the job outlook and salary after completing the Miles MBBS-MD U.S. Pathway make it a worthwhile investment?",
        answer:
          "With a high demand for skilled doctors and rewarding salaries, the Miles MBBS-MD U.S. Pathway offers a clear path to a fulfilling and lucrative medical career. Graduates can expect competitive salaries, often exceeding $250K+ annually in the US, depending on their specialty.",
      },
      {
        id: 5,
        question: "5. Why is the clinical training conducted in the US?",
        answer:
          "The clinical training in the Miles MBBS-MD U.S. Pathway for Doctors is conducted in accredited US hospitals, offering students hands-on experience under expert supervision. By training in key metropolitan areas, students gain exposure to diverse patients and the latest medical practices, preparing them for successful careers.",
      },
      {
        id: 6,
        question:
          "6. Why does the Miles MBBS-MD U.S. Pathway for Doctors program span 9 years?",
        answer:
          "The Miles MBBS-MD U.S. Pathway for Doctors spans 9 years, with 6 years in the US This structure combines 3 years of foundational theory, 2 years of clinical rotations, and 4 years of residency, ensuring comprehensive medical training and hands-on experience.",
      },
      {
        id: 7,
        question:
          "7. Why does the Miles MBBS-MD U.S. Pathway for Doctors lead to strong career prospects?",
        answer:
          "Candidates of the Miles MBBS-MD U.S. Pathway earn an MD from accredited US medical schools, setting them up for a rewarding career in the US After passing required licensing exams like the USMLE and completing their residency, they become eligible to practice medicine, with access to high-paying roles and significant career growth opportunities.",
      },
    ],
  },
  {
    id: 2,
    title: "THE WHATs",
    questions: [
      {
        id: 1,
        question: "8. What is the USMLE, and how does it fit into the program?",
        answer:
          "The USMLE (United States Medical Licensing Examination) is a three-step exam required for medical licensure in the US Core Sections <br />" +
          "&nbsp;&nbsp;Step 1 is taken after Year 3 <br />" +
          "&nbsp;&nbsp;Step 2 is taken after Year 5 <br />" +
          "&nbsp;&nbsp;Step 3 is completed during the residency",
      },
      {
        id: 2,
        question:
          "9. What support does the Miles MBBS-MD U.S. Pathway offer during the residency application process?",
        answer:
          "We provide comprehensive support, including mentorship, interview preparation, and application resources.",
      },
      {
        id: 3,
        question:
          "10. What documents are required for the application process?",
        answer:
          "Applicants need: 12th grade certificate, scorecard, transcripts, passport, statement of purpose, financial documents, passport-size photos, and, if needed, a resume and recommendation letters.",
      },
      {
        id: 4,
        question:
          "11. What kind of support does Miles offer throughout the program?",
        answer:
          "In comparison to most foreign MBBS abroad programs. With Miles MBBS-MD U.S. Pathway for Doctors (where students earn a prestigious MD) offers comprehensive support including academic and visa assistance, licensing exam prep, and career mentoring.",
      },
    ],
  },
  {
    id: 3,
    title: "THE WHENs",
    questions: [
      {
        id: 1,
        question:
          "12. When should I start the application process for the Miles MBBS-MD U.S. Pathway?",
        answer:
          "It’s recommended to begin your application process during/after graduating from 12th grade. Early application also allows sufficient time for visa processing and financial arrangements.",
      },
      {
        id: 2,
        question:
          "13. When do clinical rotations and residencies typically take place?",
        answer:
          "Clinical rotations begin after completing the pre-medical portion of the program, while residency timelines vary based on the chosen specialty.",
      },
    ],
  },
  {
    id: 4,
    title: "THE HOWs",
    questions: [
      {
        id: 1,
        question:
          "14. How do I get started with the Miles MBBS-MD U.S. Pathway for Doctors?",
        answer:
          "To get started, simply visit our website and fill out the application form. You’ll receive guidance on the next steps, including submitting required documents, preparing for interviews, and securing your spot in the program.",
      },
      {
        id: 2,
        question:
          "15. How does the Miles MBBS-MD U.S. Pathway ensure quality education?",
        answer:
          "We collaborate with US-accredited medical institutions and experienced faculty to provide a high-quality, relevant education for international students" +
          "<br />" +
          "<br />Our partner institutions are listed in the World Directory of Medical Schools with an ECFMG® Sponsor Note, making their students eligible to apply to ECFMG for ECFMG Certification and USMLE® as a step towards ECFMG Certification" +
          "<br />" +
          "<br />Partner institutions are also recognized by the Medical Council of Canada making our students eligible to sit Part 1 & Part II of the Medical Council of Canada Qualifying Examination (MCCQE) for their Licentiate of the Medical Council of Canada (LMCC)",
      },
      {
        id: 3,
        question:
          "16. How does the Miles MBBS-MD U.S. Pathway ensure quality education?",
        answer:
          "We collaborate with US-accredited medical institutions and experienced faculty to provide a high-quality, relevant education for international students" +
          "<br />" +
          "<br />Our partner institutions are listed in the World Directory of Medical Schools with an ECFMG® Sponsor Note, making their students eligible to apply to ECFMG for ECFMG Certification and USMLE® as a step towards ECFMG Certification" +
          "<br />" +
          "<br />Partner institutions are also recognized by the Medical Council of Canada making our students eligible to sit Part 1 & Part II of the Medical Council of Canada Qualifying Examination (MCCQE) for their Licentiate of the Medical Council of Canada (LMCC)",
      },
    ],
  },
  {
    id: 5,
    title: "THE WHEREs",
    questions: [
      {
        id: 1,
        question:
          "17. Where can I find more information or speak with a representative?",
        answer:
          "Fill out the form on our website, or email us at doctorspathway@mileseducation.com. Our team is ready to assist.",
      },
      {
        id: 2,
        question:
          "18. Where can I find more information or speak with a representative?",
        answer:
          "Graduates secure residencies in US hospitals and can practice medicine in the US or internationally, depending on local licensing.",
      },
      {
        id: 3,
        question: "19. Where does the program take place?",
        answer:
          "The program includes:" +
          "<br />" +
          "&nbsp;&nbsp;3 years of studies in St. Vincent Island" +
          "<br />" +
          "&nbsp;&nbsp;2 years of clinical rotations in the US" +
          "<br />" +
          "&nbsp;&nbsp;4 years of residency in the US",
      },
      // {
      //   id: 4,
      //   question: "20. Can I return to India after completing the program?",
      //   answer:
      //     "Yes, you can return to India, As per MCI, graduates who obtained a license to practice medicine in the USA / UK / Australia / Canada / New Zealand can practice in India directly.",
      // },
    ],
  },
];

export const MBA_AI_FAQS: FAQType[] = [
  {
    id: 1,
    title: "THE WHYs",
    questions: [
      {
        id: 1,
        question: "1. Why should I pursue this MBA?",
        answer:
          "It’s India’s first fully online MBA focused on building and deploying AI-powered business systems." +
          "<br />" +
          " It prepares you to lead business transformation, not just study theory—with 50+ AI system deployments." +
          "<br />" +
          " You graduate with real-world experience and a digital portfolio, making you industry- and boardroom-ready.",
      },
      {
        id: 2,
        question: "2. Why is AI integration important in an MBA?",
        answer:
          "AI is disrupting all business functions. This MBA teaches you how to lead with intelligent workflows and automation in real time—no coding required.",
      },
      {
        id: 3,
        question: "3. Why is this different from traditional MBA programs?",
        answer:
          "It offers Agentic AI labs, no-code platforms, real-world projects, and hands-on business tool building.",
      },
    ],
  },
  {
    id: 2,
    title: "THE WHATs",
    questions: [
      {
        id: 1,
        question: "4. What is the Online MBA in AI-Driven Business Strategy?",
        answer:
          "A 2-year, part-time online program that blends business fundamentals with hands-on AI deployments across marketing, finance, and strategy.",
      },
      {
        id: 2,
        question: "5. What will I learn during this course?",
        answer:
          "Business productivity tools, AI-led digital transformation, platform thinking, financial automation, and the ability to design, launch, and scale AI-first businesses.",
      },
      {
        id: 3,
        question: "6. What kind of AI projects will I work on?",
        answer:
          "Estimated total CPA exam cost is around INR 4.43 lakhs (evaluation fee + exam fee). Miles ensures transparency and supports you throughout.",
      },
    ],
  },
  {
    id: 3,
    title: "THE WHENs",
    questions: [
      {
        id: 1,
        question: "7. When does the program start and how long is it?",
        answer:
          "The program spans 2 years, divided into 4 semesters. Start dates vary; admissions are rolling.",
      },
      {
        id: 2,
        question: "8. When will I start using AI tools?",
        answer:
          "From Semester 1 itself. You begin with productivity tools and gradually move to advanced AI systems.",
      },
      {
        id: 3,
        question: "9. When are live sessions conducted?",
        answer:
          "Periodically during the program; recordings are also available. Plus, mentorship is available 24x7.",
      },
    ],
  },
  {
    id: 4,
    title: "THE WHEREs",
    questions: [
      {
        id: 1,
        question: "10. Where is the MBA conducted?",
        answer:
          "100% online through a robust LMS that serves as your AI lab and business simulator.",
      },
      {
        id: 2,
        question: "11. Where can I work after this MBA?",
        answer:
          "Across industries—consulting, marketing, fintech, FMCG, startups, or launch your own venture.",
      },
      {
        id: 3,
        question: "12. Where does the learning happen inside the LMS?",
        answer:
          "In integrated modules featuring real-world datasets, AI toolkits, workflow builders, and collaboration zones.",
      },
    ],
  },
  {
    id: 5,
    title: "The Whos",
    questions: [
      {
        id: 1,
        question: "13. Who can apply?",
        answer:
          "Any graduate (UGC-recognised, 3-year degree, minimum 50% marks) with the ambition to lead in the AI economy. No coding or tech background needed.",
      },
      {
        id: 2,
        question: "14. Who are the instructors and mentors?",
        answer:
          "A blend of B-school faculty and AI industry experts with deep experience in business transformation.",
      },
      {
        id: 3,
        question: "15. Who is this MBA meant for?",
        answer:
          "Early-career professionals, mid-level managers, startup founders, L&D professionals, and career switchers aiming to lead with AI.",
      },
    ],
  },
  {
    id: 6,
    title: "The Hows",
    questions: [
      {
        id: 1,
        question: "16. How do I apply?",
        answer:
          "Apply online via JAIN Online." +
          "<br />" +
          "Choose your specialization (Business Strategy / Marketing / Finance/ Human Resources)." +
          "<br />" +
          "Pay the registration fee or opt for an EMI plan.",
      },
      {
        id: 2,
        question: "17. How will I learn?",
        answer:
          "Through TAP-R Pedagogy (Think–Apply–Practice–Reflect), micro-learning modules, and AI labs. Every concept leads to action.",
      },
      {
        id: 3,
        question: "18. How does this MBA help my career?",
        answer:
          "You’ll build a portfolio of 30+ AI systems, receive personalized mentorship, and be guided into roles like:" +
          "<br />" +
          "&nbsp;&nbsp;Chief Innovation Officer" +
          "<br />" +
          "&nbsp;&nbsp;AI Strategist" +
          "<br />" +
          "&nbsp;&nbsp;FP&A Lead – Automation" +
          "<br />" +
          "&nbsp;&nbsp;Startup Founder – AI Ventures",
      },
      {
        id: 4,
        question: "19. How much does the program cost?",
        answer:
          "<strong>₹75,000 per semester</strong>, ₹3,00,000 total. EMI plans available starting at ₹18,750/month.",
      },
      {
        id: 5,
        question: "20.  How does the Capstone Project work?",
        answer:
          "In the final semester, you pitch an AI-powered business or deploy a real solution, guided by industry mentors and VC insights.",
      },
    ],
  },
];

export const SpeakerBio = [
  {
    name: "Abhinav Vijayaraghavan",
    title: "Managing Director (India),Withum",
    description: [
      "Chartered Accountant with 20+ years of leadership in global public practice",
      "Leading Withum India by driving growth, operational excellence, and top-tier client experience",
      "Known for building high-performing GCC teams and navigating complex global regulations",
      "A people-first leader who empowers teams and elevates client success with consistency",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/Abhinav_Vijayaraghavan.webp",
  },
  {
    name: "Shrenik Shah",
    title: "Managing Director (India), Armanino",
    description: [
      "20+ years of expertise in taxation, financial planning & business development",
      "Leads Armanino India’s growth across tax, assurance, consulting & advisory",
      "Former Managing Director at Citrin Cooperman, with deep US & India tax experience",
      "CPA & CA, with a Master’s in Accounting, known for blending technical excellence with strategic leadership",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/ShrenikShah.webp",
  },
  {
    name: "Vishnu Patwari",
    title: "National Managing Principal, BDO Rise",
    description: [
      "Founding force behind BDO RISE: scaled it from 0 to 3300+ professionals across India",
      "CEO-level leader driving BDO USA’s Center of Excellence across Assurance, Tax & Advisory",
      "Former Audit Partner, BDO San Francisco, with 20+ years serving global alternative investment giants",
      "Recognized for building high-performance teams, shaping audit innovation & driving enterprise-wide impact",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/VishnuPatwari.webp",
  },
  {
    name: "Balaji Iyer",
    title: "Managing Principal (India), Baker Tilly One",
    description: [
      "Leads strategic and operational growth for Baker Tilly One in India",
      "Brings 20+ years of experience building GCC strategies and new service capabilities",
      "Skilled in digital transformation, design thinking, and driving new global delivery models",
      "Has held key leadership roles at Grant Thornton, EY, and other consulting firms",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/BalajiIyer.webp",
  },
  {
    name: "Uttam Pai Umesh",
    title: "AVP, Founders Office, Miles Education",
    description: [
      "Leads 50+ college & university partnerships, including multiple IIMs",
      "Heads a 70+ member academic & industry team delivering high-impact learning",
      "Licensed US CPA guiding thousands of CPA/CMA aspirants toward global careers",
      "Sought-after speaker, panelist & moderator at top fests, conclaves & industry forums",
      "Judge at major university events, mentoring emerging talent",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/UttamPaiUmesh.webp",
  },
  {
    name: "Ishita Doshi",
    title: "Partner, PKF O'Connor Davies ",
    description: [
      "Recognized for expertise in building tax practices and delivering strategic guidance in U.S. Tax Advisory and Compliance",
      "With over 17 years of experience in the U.S. tax domain, Ishita actively trains and mentors teams to achieve excellence and drive professional growth",
      "Committed to operational excellence by designing and implementing streamlined processes that enhance efficiency and accuracy",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/Ishita_Doshi.png",
  },
  // {
  //   name: "Sumeet Aggarwal",
  //   title: "Partner-In-Charge (India) PKF O'Connor Davies",
  //   description: [
  //     "16+ years of experience in fund administration & management company accounting",
  //     "Leads PKF O’Connor Davies’ India operations",
  //     "Expertise across hedge funds, PE, VC, real estate funds & alternative vehicles",
  //     "Strong command over master-feeder structures and complex allocations",
  //     "Skilled in carried interest, management fees & investor reporting",
  //   ],
  //   imageUrl:
  //     "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/SumeetAggarwal.webp",
  // },
  {
    name: "Karthick Venkatakrishnan",
    title: "Managing Director (India), EisnerAmper ",
    description: [
      "Responsible for operations, growth, and talent maximization",
      "Previously headed EisnerAmper's Fund Services practice in Bangalore",
      "Possesses 20+ years of expertise in alternative investment funds (Hedge Funds, Private Equity, Family Offices)",
      "Former Director and Head of Operations managing service delivery for North America and the Cayman Islands",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/KarthickVenkatakrishnan.webp",
  },
  {
    name: "Ashish Jalan",
    title: "President & Head Global Operations CohnReznick",
    description: [
      "Seasoned leader (30 years) with experience in industry and Big4 firms, driving sustainable bottom-line impact",
      "Leading Strategists for 1100+ FTEs in India and the Philippines",
      "Previously a Partner at KPMG Tax, where he grew the US practice 5x and transformed India operations",
      "Established India Shared Services centers from scratch at both Deloitte (2001) and PwC (2005)",
      "Expert in organizational transformation, developing capabilities, and managing global, high-performance teams",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/AshishJalan%20.webp",
  },
  {
    name: "Manoj Pola",
    title: "Office Leader (India), Elliott Davis ",
    description: [
      "Leads Elliott Davis India with a focus on growth, capability-building, and operational excellence",
      "Experienced people-first leader driving expansion and high-performance teams in Bengaluru",
      "Known for fostering innovation, collaboration, and a strong delivery culture across the firm",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/ManojPola.webp",
  },
  {
    name: "Ronak Shah",
    title: "Country Manager (India), Plante Moran",
    description: [
      "Country Manager, Plante Moran (Mumbai), driving strategic growth and operations in India",
      "15+ Years of U.S. Tax/M&A Expertise. Advising companies and PE funds on complex tax structuring (including ASC 740) and due diligence",
      "Proven Mentor known for building high-performing teams; achieved 500%+ people growth in the Mumbai office",
      "CPA (Wisconsin) and Indian Chartered Accountant",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/RonakShah%20.webp",
  },
  {
    name: "Dr Biju Toms",
    title: "Director - SCFA, Christ University",
    description: [
      "Seasoned management consultant & educator with expertise in strategy, transformation & talent development",
      "Driving organizational growth, leadership excellence & innovation across industries",
      "PhD | Consultant | Educator | Bridging Academia & Business for Impact",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/DrBijuToms.webp",
  },
  {
    name: "Suryanarayanan Sankaran",
    title: "Managing Partner (India), Weaver",
    description: [
      "Big Four–trained auditor with deep expertise across financial and non-financial services",
      "Former risk leader at a multinational bank, shaping governance and control frameworks",
      "Now driving Weaver India as Managing Partner, leading growth, capability-building, and strategic excellence",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/suryaNarayan.webp",
  },
  {
    name: "Ankit K Singhvi",
    title: "Director (India), Clark Schaefer Hackett ",
    description: [
      "Chartered Accountant & LLB with 14+ years across U.S. CPA firms, Finance GCCs, and tax fintech",
      "Leads CSH India’s growth, operations, and cross-border integration with a sharp blend of finance + legal expertise",
      "Former fintech contributor building tax tech & compliance products; passionate about innovation and process excellence",
      "International exposure through U.S. secondments; known for mentoring CA/CPA aspirants and founding key industry study circles",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/AnkitKSinghvi.webp",
  },
  {
    name: "Rajat Gupta ",
    title: "Executive Director, Grant Thornton Indus",
    description: [
      "CA, CPA with 20+ years of experience in Audit and Accounting Advisory domain.",
      "Leading Audit and Assurance Practice for Grant Thornton INDUS",
      "Experience with global innovation and transformation projects",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/RajatGupta%20.webp",
  },
  {
    name: "Badri Rao ",
    title: "Partner, KPMG GDC",
    description: [
      "26+ years of experience across global audit, risk, controls & remote delivery",
      "Leads tech-enabled audit transformation at KPMG, driving AI-led tools, solutions, and innovation",
      "CA (India), CPA (USA), CISA with deep expertise in external audits, internal audits, SOPs, risk assessment & mitigation",
      "20+ years in remote audit operations; manages large-scale teams serving US and global clients",
      "International experience with Deloitte in Chicago, Wilton & Dubai; currently leads 500+ professionals across insurance, healthcare & commercial audit services",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/BadriRao.webp",
  },
  {
    name: "Chung G Tham",
    title: "Financial Services Audit leader, EY GDS",
    description: [
      "Heads FS and WAM sectors for GDS Audit",
      "20+ years EY experience across global markets",
      "Expertise in WAM and IT with onshore/offshore teams worldwide",
      "Leads 4,000+ audit professionals across India, Philippines & Poland",
      "Drives growth through sector focus, quality & innovation",
      "Inclusiveness Council member; leads LGBTQ+ workstream",
      "Location Leader for Poland & Spain, steering regional growth",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/ChungGTham.webp",
  },
  {
    name: "Sanchit Gupta",
    title: "COO & Managing Director, Pierag Consulting",
    description: [
      "15+ years of experience in business strategy and operations",
      "Former EY professional with international experience in Houston, Texas",
      "Expert in designing scalable growth models and firm-wide operational frameworks",
      "Leads mobility planning, performance systems, and L&D initiatives at Pierag",
      "Strong focus on technology adoption and process automation to boost efficiency",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/SanchitGupta.webp",
  },
  {
    name: "Vishal Nair ",
    title: "Director Operations, Smith+Howard ",
    description: [
      "20+ years in Global Delivery, GCC transitions & commercial operations",
      "Leadership experience across Kroll, Nielsen, and Smith+Howard",
      "Builds & scales high-performing operational teams",
      "Leading Smith+Howard India as a strategic growth hub",
      "Driving Go-To-Market & Marketing teams to strengthen firm-wide growth",
      "Strong advocate for people-centric leadership & shared growth",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/VishalNair.webp",
  },
  {
    name: "Vinod V",
    title: "Director, Wipfli",
    description: [
      "CPA & IRS Enrolled Agent with 20 years in U.S. tax compliance",
      "Leads the Tax team at Wipfli India",
      "Driving expansion of the India tax practice in collaboration with U.S. leadership",
      "Shapes and executes the India growth strategy for tax",
      "Responsible for scaling operations and strengthening quality management",
      "Focused on industry integration and delivering higher-value tax work",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/Vinod_V.png",
  },
  {
    name: "Lavit Jain ",
    title: "Audit Managing Director (India), Crowe",
    description: [
      "17+ years of experience in External Audit",
      "CA & CPA with strong technical depth in auditing and accounting",
      "Leads large audit teams and drives India–US integration Client-facing experience across Manufacturing, Retail, Power & Utilities, Telecom & more",
      "Known for leadership, quality delivery, and building growth strategies",
      "Nominated for multiple awards during Big Four tenure",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/Lavit%20Jain.webp",
  },
  {
    name: "Vishal Agarwal",
    title: "Executive Managing Director, Citrin Cooperman",
    description: [
      "CA & CPA with 22+ years of experience",
      "Led offshore GCCs for multiple Big Four firms",
      "Drives growth and transformation for Citrin Cooperman India",
      "Focuses on innovation, standardization, and automation",
      "Known for building high-performing global capability centers",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/VishalAgarwal.webp",
  },
  {
    name: "Giridhar Rajagopal",
    title: "President & Country Manager, Sentient Solutions",
    description: [
      "25 Years of Offshore / GCC experience ",
      "Played a key role in one of India's first insurance BOT transactions, covering over 13k employees",
      "Created three CoE for Insurance / Accounting ",
      "Shared Services and AI / ML product lab Voted the top capstone project at the UC ",
      "Berkeley Haas School of Business [BEPM - Class of 2019]",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/GiridharRajagopal.webp",
  },
  // Repeated entries for testing scrolling
  {
    name: "Varun Bhasin",
    title: "Director, RSM US LLP",
    description: [
      "CPA-accredited Assurance Director at RSM US LLP",
      "13+ years of experience in public accounting",
      "Expertise in internal controls and US GAAP compliance",
      "Leads complex assurance engagements for U.S. commercial clients",
      "Known for strong analytical skills and dependable execution under tight deadlines",
      "Committed to elevating audit quality and financial reporting integrity",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/VarunBhasin.webp",
  },
  {
    name: "Prakash Sonthalia",
    title: "Managing Director, Deloitte",
    description: [
      "19+ years of public accounting experience",
      "Specialises in the investment management industry",
      "Audits both public and private entities including registered & non-registered investment companies, and fund-of-funds",
      "Deep expertise in US GAAP, SEC regulations, and 1940 Act compliance",
      "Strong track record in reviewing and validating complex financial statements",
      "Known for guiding clients through evolving regulatory and reporting requirements",
    ],
    imageUrl:
      "https://ik.imagekit.io/mileseducation/miles_website/mpact/speakersBio/PrakashSonthalia.webp",
  },
];
// ✅ Helper function to resolve image URLs
export const resolveStrapiImageUrl = (path: string | null) => {
  let STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_URL;
  if (!path) return "/default-image.jpg"; // Fallback image
  return path.startsWith("/uploads") ? `${STRAPI_URL}${path}` : path;
};

export const getKnowMoreLink = (
  template: string,
  id: number | string,
  event_type: string,
) => {
  // All online webinars now use the single generic detail page.
  if (event_type === "Online") {
    return `/events/details/${id}`;
  }

  switch (template) {
    case "USP":
      return `/events/events-us-pathway?id=${id}`;
    case "USP_TWO":
      return `/events/events-us-pathway?id=${id}`;
    case "CMA":
      return `/events/events-us-cma?id=${id}`;
    case "CPA":
      return `/events/events-us-cpa?id=${id}`;
    case "HEALTHCARE_DOCTORS":
      return `/events/events-doctors?id=${id}`;
    case "MBA_AI":
      return `/events/events-mba-ai?id=${id}`;
    case "CAIRA":
      return `/events/events-caira?id=${id}`;
    default:
      return "#";
  }
};

// ✅ Resolve display date/time for an event.
// Prefers the CMS-authored strings; falls back to formatting the raw ISO
// `date` in IST (e.g. "16 Nov, 2025" / "7:00 PM (IST)").
export const getEventDisplayDateTime = (
  dateString?: string | null,
  timeString?: string | null,
  isoDate?: string | null,
) => {
  const result = {
    date: dateString || "",
    time: timeString || "",
  };

  if ((!result.date || !result.time) && isoDate) {
    const d = new Date(isoDate);
    if (!isNaN(d.getTime())) {
      if (!result.date) {
        const tz = "Asia/Kolkata";
        const day = d.toLocaleDateString("en-GB", {
          timeZone: tz,
          day: "numeric",
        });
        const mon = d.toLocaleDateString("en-GB", {
          timeZone: tz,
          month: "short",
        });
        const yr = d.toLocaleDateString("en-GB", {
          timeZone: tz,
          year: "numeric",
        });
        result.date = `${day} ${mon}, ${yr}`;
      }
      if (!result.time) {
        result.time =
          d.toLocaleTimeString("en-US", {
            timeZone: "Asia/Kolkata",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          }) + " (IST)";
      }
    }
  }

  return {
    date: result.date || "N/A",
    time: result.time || "N/A",
  };
};

// ✅ Fetch Data from Strapi
// `init` overrides the fetch caching. Client callers keep the default
// (`no-cache`); server callers pass `{ next: { revalidate } }` so the route
// isn't forced into dynamic rendering.
export async function getEventsMainPageDataFromStrapi(
  // Same as RequestInit; spelled via `fetch` because ESLint's no-undef
  // doesn't know TypeScript's global DOM types.
  init: NonNullable<Parameters<typeof fetch>[1]> & {
    next?: { revalidate?: number; tags?: string[] };
  } = {
    cache: "no-cache",
  },
) {
  try {
    if (!process.env.NEXT_PUBLIC_STRAPI_URL) {
      console.warn("Strapi base URL is missing.");
      return null;
    }

    // Strapi 5 validates every `populate` key strictly — an unknown key fails
    // the whole request with `400 Invalid key <name>` — and `populate: "*"`
    // stops one level deep (it will not descend into a component's nested
    // component or a relation's media). Every branch is therefore spelled out.
    // Strict, curated populate — asks for exactly the relations/fields the page
    // renders. Strapi 5 rejects any unknown key with `400 Invalid key <name>`,
    // so if the CMS schema drops or renames a field this whole request fails.
    const strictPopulate = {
      event_main_banner_image: true, // Populate banner image
      event_main_gif: true, // Populate gif
      event_main_flag_bearer: {
        populate: { card_component: { populate: "*" } }, // nested component needs the extra level
      },
      event_main_alumni_in_india: {
        populate: { image: true, company_logo: true, domain: true },
      },
      event_main_alumni_in_us: {
        populate: { image: true, company_logo: true, domain: true },
      },
      events: {
        populate: {
          event_main_card_image: true,
          event_through_banner_image: true,
          city: true,
          experts: { populate: { image: true } }, // include speaker photo
          // Webinar meta (type, date, SF/MF/Zoom ids, title) all live under
          // sf_webinar_detail. The `course`/`domain` relations were removed
          // from this component, so we take only its scalar fields — the
          // course/category now comes from the event's `template`.
          sf_webinar_detail: true,
          // Platform/CPE record — replaces the removed miles_one_detail
          // and miles_masterclass_detail relations.
          caira_detail: true,
        },
      }, // Populate the fields used by the events list
      event_main_highlight: {
        populate: {
          sf_webinar_detail: true,
          caira_detail: true,
          city: true, // offline hero form needs city / orientation ids
        },
      }, // hero needs webinar ids/date from sf_webinar_detail; course from template
      seo: {
        populate: "*", // Populate all fields within seo
      },
    };

    // Lenient fallback — `populate: "*"` is a wildcard that NEVER 400s on an
    // unknown key. If a future schema change breaks the strict query, we retry
    // with this so the page still loads its core data (one level shallower:
    // e.g. speaker photos are skipped) instead of going fully blank.
    const lenientPopulate = {
      events: { populate: "*" },
      event_main_highlight: { populate: "*" },
      event_main_banner_image: true,
      event_main_alumni_in_india: { populate: "*" },
      event_main_alumni_in_us: { populate: "*" },
      seo: { populate: "*" },
    };

    const fetchWith = (populate: Record<string, unknown>) =>
      fetch(
        `${process.env.NEXT_PUBLIC_STRAPI_URL}/api/event-main-page?${qs.stringify(
          { populate },
          { encodeValuesOnly: true },
        )}`,
        {
          headers: {
            Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_BEARER_TOKEN}`,
          },
          ...init,
        },
      );

    let res = await fetchWith(strictPopulate);
    if (!res.ok) {
      console.warn(
        `event-main-page strict query failed (${res.status}); retrying with lenient populate`,
      );
      res = await fetchWith(lenientPopulate);
    }

    if (!res.ok) {
      const errorText = await res.text();
      console.error("Strapi API Response Status:", res.status);
      console.error("Strapi API Response Body:", errorText);
      throw new Error(
        `Failed to fetch data from Strapi. Status: ${res.status}`,
      );
    }

    const data = await res.json();

    return data;
  } catch (error: any) {
    console.error("Error in getStrapiMainPageData:", error.message);
    throw error;
  }
}

// ✅ Fetch Collection Details with Nested Population
export async function getEventDetailsByDocumentId(documentId: string) {
  try {
    // ✅ Define specific fields and nested relations to populate.
    // `fields` is validated before `populate` and both reject unknown keys with
    // `400 Invalid key <name>`, so only keys that exist on the Event schema may
    // appear here.
    // Strict, curated query. Strapi validates `fields` before `populate` and
    // both reject unknown keys with `400 Invalid key <name>`.
    const strictQuery = qs.stringify(
      {
        populate: {
          event_through_banner_image: true,
          event_main_card_image: true,
          what_will_you_learn: { populate: "*" }, // Fetch learn section + points
          // `populate: "*"` on these would also pull each row's `events`
          // back-relation — every event the expert/why-attend is attached to,
          // `about_the_event` blocks and all. Ask only for the media.
          why_attends: { populate: { icon: true } },
          event_through_statistics_strip: { populate: "*" },
          register_strip: { populate: "*" },
          experts: { populate: { image: true } },
          // `populate: "*"` would stop before the nested course relation, so
          // the upcoming-event card fields are listed explicitly.
          upcoming_events: {
            populate: {
              event_main_card_image: true,
              city: true,
              sf_webinar_detail: true,
              caira_detail: true,
            },
          },
          alumni_in_uses: {
            populate: { image: true, company_logo: true, domain: true },
          },
          alumni_in_indias: {
            populate: { image: true, company_logo: true, domain: true },
          },
          city: true,
          seo: { populate: "*" },
          // type/ids/date live under sf_webinar_detail (scalars only — the
          // course/domain relations were removed; category comes from template)
          sf_webinar_detail: true,
          // CPE/platform record — replaces the removed miles_one_detail
          caira_detail: true,
        },
        fields: [
          "event_date_string",
          "event_time_string",
          "about_the_event",
          "template",
          "webinar_name",
          "website_event_url",
          "form_api_link",
        ],
      },
      { encodeValuesOnly: true },
    );

    // Lenient fallback — wildcard populate + no `fields` restriction never 400s
    // on an unknown key, so a future schema change degrades (a level shallower)
    // instead of taking the whole detail page down.
    const lenientQuery = qs.stringify(
      { populate: "*" },
      { encodeValuesOnly: true },
    );

    const fetchWith = (query: string) =>
      fetch(
        `${process.env.NEXT_PUBLIC_STRAPI_URL}/api/events/${documentId}?${query}`,
        {
          headers: {
            Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_BEARER_TOKEN}`,
          },
          cache: "no-cache", // Always fetch the latest data
        },
      );

    let res = await fetchWith(strictQuery);
    if (!res.ok) {
      console.warn(
        `event details strict query failed (${res.status}); retrying with lenient populate`,
      );
      res = await fetchWith(lenientQuery);
    }

    if (!res.ok) {
      const errorText = await res.text();
      console.error("Strapi API Response Status:", res.status);
      console.error("Strapi API Response Body:", errorText);
      throw new Error(`Failed to fetch event details. Status: ${res.status}`);
    }

    const data = await res.json();
    return data;
  } catch (error: any) {
    console.error("Error in getEventDetailsByDocumentId:", error.message);
    throw error;
  }
}

// ✅ Strapi returns bigintegers (`sf_event_id`, `zoom_webinar_id`,
// `mf_event_id`, `orientation_id`) as strings to preserve precision past 2^53.
// Consumers are typed as numbers, so normalise on the way out.
const toNumericId = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// ✅ The id sent as `interested_webinar_id` on the lead payload. Prefer
// `sf_event_id` (populated on every synced sf-webinar-detail row); fall back to
// `mf_event_id` for any record SF has not synced.
const getRegistrationWebinarId = (sf: any): string | null =>
  sf?.sf_event_id ?? sf?.mf_event_id ?? null;

// ✅ Online vs Offline. `sf_webinar_detail.type_of_webinar` is the source of
// truth; `caira_detail.isOffline` covers events that have a platform record but
// no Salesforce sync yet. Events with neither belong to no list.
const getWebinarType = (event: any): string | undefined => {
  const type = event?.sf_webinar_detail?.type_of_webinar;
  if (type) return type;
  if (event?.caira_detail) {
    return event.caira_detail.isOffline
      ? EventTypeEnum.OFFLINE
      : EventTypeEnum.ONLINE;
  }
  // Legacy schema: `event_type` lived on the event itself.
  if (event?.event_type) return event.event_type;
  return undefined;
};

// ✅ A webinar stays visible through its go-live day and disappears the day
// after (compared on the IST calendar day). Events with no resolvable date are
// kept (we can't tell they're past).
const IST_TZ = "Asia/Kolkata";
const istCalendarDay = (d: Date): string =>
  d.toLocaleDateString("en-CA", { timeZone: IST_TZ }); // YYYY-MM-DD, sorts chronologically
const isWebinarActive = (event: any): boolean => {
  // The CMS display strings win (webinar_date can lag behind what the editors
  // set); then sf_webinar_detail.webinar_date; legacy: event.date (ISO).
  const display = parseDisplayDateTime(
    event?.event_date_string,
    event?.event_time_string,
  );
  const iso = event?.sf_webinar_detail?.webinar_date || event?.date;
  let day: string | null = null;
  if (display) {
    day = istCalendarDay(display);
  } else if (iso) {
    const d = new Date(iso);
    if (!isNaN(d.getTime())) day = istCalendarDay(d);
  } else if (event?.event_date_string) {
    const d = new Date(event.event_date_string);
    if (!isNaN(d.getTime()))
      day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  if (!day) return true;
  return day >= istCalendarDay(new Date());
};

export const fetchAndFormatEventData = async (
  init?: Parameters<typeof getEventsMainPageDataFromStrapi>[0],
) => {
  try {
    const data = await getEventsMainPageDataFromStrapi(init);

    // Guard the response shape — a null payload or a missing `data` wrapper
    // must degrade to the empty state, never throw on `eventsData.events`.
    if (!data || !data.data) {
      return {
        onlineEvents: [],
        offlineEvents: [],
        alumniInIndia: [],
        alumniInUS: [],
        eventsBannerData: null,
        seoData: null,
        eventMetaData: null,
        highlightedEventData: null,
      };
    }

    const eventsData = data.data;

    // Helper function to get day from date string. Tolerates the CMS's
    // "dd MMMM yyyy" and "MMMM d, yyyy" formats (and ISO), else "Unknown".
    const getDayFromDate = (dateString: string) => {
      try {
        let parsedDate = parse(dateString, "dd MMMM yyyy", new Date());
        if (!isValid(parsedDate)) parsedDate = new Date(dateString);
        if (!isValid(parsedDate)) return "Unknown";
        return format(parsedDate, "EEEE"); // Returns day name like "Saturday"
      } catch (error) {
        return "Unknown";
      }
    };

    // ✅ Prepare Online Events

    // Webinar meta (type, course, date, ids) lives under `sf_webinar_detail`,
    // the event's title is `webinar_name`, and the CPE/platform fields live
    // under `caira_detail`.
    const onlineEvents =
      eventsData.events
        ?.filter(
          (event: any) =>
            getWebinarType(event) === EventTypeEnum.ONLINE &&
            isWebinarActive(event),
        )
        .map((event: any) => {
          const sf = event.sf_webinar_detail;
          // Fall back to the legacy top-level fields when sf_webinar_detail is
          // absent (older CMS schema / unsynced events).
          const courseRel = sf?.course || event.course;
          const display = getEventDisplayDateTime(
            event.event_date_string,
            event.event_time_string,
            sf?.webinar_date || event.date,
          );
          const speaker = event.experts?.[0];
          const speakerImg =
            speaker?.image?.formats?.small?.url || speaker?.image?.url;
          return {
            id: event.documentId,
            title:
              event.webinar_name || sf?.title_on_website || event.name || "",
            date: display.date,
            time: display.time,
            // The CMS display strings win over webinar_date, which can lag
            // behind what the editors set (cards render their date from this).
            isoDate:
              parseDisplayDateTime(
                event.event_date_string,
                event.event_time_string,
              )?.toISOString() ||
              sf?.webinar_date ||
              event.date ||
              null,
            day: event.event_date_string
              ? getDayFromDate(event.event_date_string)
              : "N/A",
            category: event.template || "All",
            image: resolveStrapiImageUrl(
              event.event_main_card_image?.url ||
                event.event_through_banner_image?.formats?.thumbnail?.url,
            ),
            // Same banner shown on the detail page, used as the card visual.
            bannerImage: resolveStrapiImageUrl(
              event.event_through_banner_image?.formats?.large?.url ||
                event.event_through_banner_image?.url ||
                event.event_main_card_image?.url,
            ),
            template: event.template,
            courseName: courseRel?.Name || event.template || "N/A",
            courseId: courseRel?.course_id || 0,
            webinarId: toNumericId(
              getRegistrationWebinarId(sf) ?? event.mf_event_id,
            ),
            city: event.city?.city || "N/A",
            city_id: event.city?.city_id || 0,
            event_type: getWebinarType(event),
            orientation_id: toNumericId(event.city?.orientation_id),
            speaker: speaker?.Name || "",
            speakerDesignation: speaker?.Designation || "",
            speakerImage: speakerImg ? resolveStrapiImageUrl(speakerImg) : "",
            cpe: getEventCpeContentFromCms(
              { cairaDetail: event.caira_detail, sfWebinarDetail: sf },
              event.template,
            ),
          };
        }) || [];

    // ✅ Prepare Offline Events
    const offlineEvents =
      eventsData.events
        ?.filter(
          (event: any) =>
            getWebinarType(event) === EventTypeEnum.OFFLINE &&
            isWebinarActive(event),
        )
        .map((event: any) => {
          const sf = event.sf_webinar_detail;
          const courseRel = sf?.course || event.course;
          return {
            id: event.documentId,
            title:
              event.webinar_name || sf?.title_on_website || event.name || "",
            date: event.event_date_string || "N/A",
            time: event.event_time_string || "N/A",
            day: event.event_date_string
              ? getDayFromDate(event.event_date_string)
              : "N/A",
            category: event.template || "All",
            image: resolveStrapiImageUrl(
              event.event_main_card_image?.url ||
                event.event_through_banner_image?.formats?.thumbnail?.url,
            ),
            template: event.template,
            courseName: courseRel?.Name || event.template || "N/A",
            courseId: courseRel?.course_id || 0,
            webinarId: toNumericId(
              getRegistrationWebinarId(sf) ?? event.mf_event_id,
            ),
            city: event.city?.city || "N/A",
            // LiveEvents-Desk / -Mob read `city_id`; `cityId` kept as an alias.
            city_id: event.city?.city_id || 0,
            cityId: event.city?.city_id || 0,
            event_type: getWebinarType(event),
            orientation_id: toNumericId(event.city?.orientation_id),
          };
        }) || [];

    // ✅ Prepare Alumni in India Data
    const alumniInIndia = eventsData.event_main_alumni_in_india?.map(
      (alumni: any) => ({
        name: alumni.name,
        img: resolveStrapiImageUrl(alumni.image?.formats?.thumbnail?.url),
        alt: alumni.image?.alternativeText || "Alumni Image",
        description: alumni.content || "",
        // `content` is a plain text field on both alumni types, not blocks.
        des: alumni.content || "",
        compLogo: resolveStrapiImageUrl(
          alumni.company_logo?.formats?.thumbnail?.url,
        ),
        alliance: alumni.domain?.Name || "N/A",
        location: alumni.location || "N/A",
        linkedin: alumni.linkedin || "#",
        youtube: alumni.watch || "#",
      }),
    );

    // ✅ Prepare Alumni in US Data
    const alumniInUS = eventsData.event_main_alumni_in_us?.map(
      (alumni: any) => ({
        name: alumni.name,
        img: resolveStrapiImageUrl(alumni.image?.formats?.thumbnail?.url),
        alt: alumni.image?.alternativeText || "Alumni Image",
        description: alumni.content || "",
        des: alumni.content || "",
        compLogo: resolveStrapiImageUrl(
          alumni.company_logo?.formats?.thumbnail?.url,
        ),
        alliance: alumni.domain?.Name || "N/A",
        location: alumni.location || "N/A",
        linkdin: alumni.linkedin || "#",
        youtube: alumni.watch || "#",
      }),
    );

    // ✅ Prepare Event Banner Data
    const eventHighlight = eventsData.event_main_highlight;
    const sfHighlight = eventHighlight?.sf_webinar_detail;
    const highlightCourse = sfHighlight?.course || eventHighlight?.course;

    const highlightDisplay = getEventDisplayDateTime(
      eventHighlight?.event_date_string,
      eventHighlight?.event_time_string,
      sfHighlight?.webinar_date || eventHighlight?.date,
    );

    // First paragraph of the highlighted event's "about" content, used as the
    // hero sub-headline (falls back to empty string when unavailable).
    const heroDescription =
      (eventHighlight?.about_the_event || [])
        .find((block: any) => block?.type === "paragraph")
        ?.children?.map((child: any) => child?.text || "")
        .join("")
        .trim() || "";

    // The hero always renders. When the highlighted webinar has passed (or
    // there is no highlight), `expired: true` tells the banner to block the
    // form and show an "expired / new sessions coming soon" state instead.
    const highlightActive = Boolean(
      eventHighlight && isWebinarActive(eventHighlight),
    );
    const eventsBannerData = {
      expired: !highlightActive,
      banner: {
        bannerUrl: resolveStrapiImageUrl(
          eventsData.event_main_banner_image?.url,
        ),
        bannerAlt: eventsData.event_main_banner_image?.alternativeText,
      },
      hero: {
        title:
          eventHighlight?.webinar_name ||
          sfHighlight?.title_on_website ||
          eventHighlight?.name ||
          "",
        description: heroDescription,
        date: highlightDisplay.date,
        time: highlightDisplay.time,
      },
      form: {
        formLink: eventHighlight?.form_api_link,
        eventId:
          getRegistrationWebinarId(sfHighlight) ?? eventHighlight?.mf_event_id,
        zoomWebinarId:
          sfHighlight?.zoom_webinar_id ??
          eventHighlight?.zoom_webinar_id ??
          null,
        course: highlightCourse?.Name || eventHighlight?.template || "N/A",
      },
    };

    const seoData = {
      metaTitle: eventsData.seo?.metaTitle || "Events",
      metaDescription: eventsData.seo?.metaDescription || "Events",
      shareImage: {
        // `shared.seo` names this field `shareImage`.
        url: resolveStrapiImageUrl(eventsData.seo?.shareImage?.url),
        alt: eventsData.seo?.shareImage?.alternativeText || "Share Image",
      },
      keywords: eventsData.seo?.keywords || "Events",
    };

    const eventMetaData = {
      eventType: getWebinarType(eventHighlight),
      eventId:
        getRegistrationWebinarId(sfHighlight) ?? eventHighlight?.mf_event_id,
      zoomWebinarId:
        sfHighlight?.zoom_webinar_id ?? eventHighlight?.zoom_webinar_id ?? null,
      courseId: highlightCourse?.course_id,
      courseName: highlightCourse?.Name || eventHighlight?.template,
      eventName:
        eventHighlight?.webinar_name ||
        sfHighlight?.title_on_website ||
        eventHighlight?.name,
      eventDate: highlightDisplay.date,
      eventTime: highlightDisplay.time,
      city: eventHighlight?.city?.city || "N/A",
      city_id: eventHighlight?.city?.city_id || 0,
      orientation_id: toNumericId(eventHighlight?.city?.orientation_id),
      cpe: getEventCpeContentFromCms(
        {
          cairaDetail: eventHighlight?.caira_detail,
          sfWebinarDetail: sfHighlight,
        },
        eventHighlight?.template,
      ),
    };

    const highlightedEventData = {
      date: eventHighlight?.event_date_string ?? null,
      time: eventHighlight?.event_time_string ?? null,
    };

    return {
      onlineEvents,
      offlineEvents,
      alumniInIndia,
      alumniInUS,
      eventsBannerData,
      seoData,
      eventMetaData,
      highlightedEventData,
    };
  } catch (error: any) {
    console.error("Error fetching and formatting event data:", error.message);
    throw new Error(error.message || "Failed to fetch event data");
  }
};

export const fetchAndFormatEventDetails = async (id: string) => {
  if (!id) throw new Error("Event ID is required for fetching event details.");

  try {
    const data = await getEventDetailsByDocumentId(id);
    const eventData = data.data;

    if (!eventData) throw new Error("No event data found.");

    // Webinar meta (type, course, ids, date) lives under sf_webinar_detail and
    // the CPE/platform fields under caira_detail.
    const sf = eventData.sf_webinar_detail;
    const cairaDetail = eventData.caira_detail;
    // Fall back to legacy top-level fields when sf_webinar_detail is absent.
    const detailCourse = sf?.course || eventData.course;

    // ✅ Banner Data
    const bannerData = {
      bannerUrl: resolveStrapiImageUrl(
        eventData.event_through_banner_image?.formats?.large?.url ||
          eventData.event_through_banner_image?.url,
      ),
      bannerAlt:
        eventData.event_through_banner_image?.alternativeText ||
        "Event Banner Image",
    };

    const statisticsStrip = {
      learners: eventData.event_through_statistics_strip?.learners,
      duration: eventData.event_through_statistics_strip?.duration,
      found_it_useful: eventData.event_through_statistics_strip?.found_it,
    };

    const eventDateAndTime = getEventDisplayDateTime(
      eventData.event_date_string,
      eventData.event_time_string,
      sf?.webinar_date || eventData.date,
    );

    const bannerDeskData = {
      bannerTitle:
        eventData.webinar_name || sf?.title_on_website || eventData.name || "",
      bannerData,
      formLink: eventData.form_api_link || "test.com",
      statisticsStrip,
      eventDateAndTime,
    };

    // ✅ About Event Data
    const aboutEventData = {
      about_the_event: eventData.about_the_event || [],
    };

    // ✅ Why Attend Data
    const whyAttendData =
      eventData.why_attends?.map((item: any) => ({
        id: item.id,
        documentId: item.documentId,
        text: item.text,
        iconUrl: resolveStrapiImageUrl(
          item.icon?.formats?.small?.url ||
            item.icon?.url ||
            "/icons/default-icon.svg",
        ),
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        publishedAt: item.publishedAt,
        icon: {
          id: item.icon?.id,
          name: item.icon?.name,
          url: resolveStrapiImageUrl(item.icon?.url),
          alternativeText: item.icon?.alternativeText || "Icon Image",
        },
      })) || [];

    // ✅ Learn Data
    const learnData =
      eventData.what_will_you_learn?.learn_points?.flatMap(
        (point: any) =>
          point.children?.flatMap((child: any) =>
            child.children?.flatMap((grandChild: any) => grandChild.text || []),
          ) || [],
      ) || [];

    // ✅ Expert Data
    const expertData =
      eventData.experts?.map((expert: any) => ({
        id: expert.id,
        Name: expert.Name,
        Designation: expert.Designation,
        Content: expert.Content,
        image: {
          url: resolveStrapiImageUrl(
            expert.image?.formats?.large?.url ||
              expert.image?.url ||
              "/events/Experts.png",
          ),
          alternativeText: expert.image?.alternativeText || "Expert Image",
        },
      })) || [];

    // ✅ Alumni Data
    const combinedAlumniData = [
      ...(eventData.alumni_in_uses || []),
      ...(eventData.alumni_in_indias || []),
    ].map((alumni: any) => ({
      name: alumni.name || "N/A",
      img: resolveStrapiImageUrl(
        alumni.image?.formats?.thumbnail?.url ||
          alumni.image?.url ||
          "/events/placeholder.png",
      ),
      alt: alumni.image?.alternativeText || "Alumni Image",
      description: alumni.content || "No description available.",
      des: alumni.content || "No short description available.",
      compLogo: resolveStrapiImageUrl(
        alumni.company_logo?.formats?.thumbnail?.url ||
          alumni.company_logo?.url ||
          "/events/company-placeholder.png",
      ),
      alliance: alumni.domain?.Name || "Unknown Alliance",
      location: alumni.location || "Location not provided",
      linkdin: alumni.linkedin || "#",
    }));

    // ✅ Upcoming Events Data
    const upcomingEventsData = eventData.upcoming_events?.map((event: any) => {
      const upcomingSf = event.sf_webinar_detail;
      return {
        id: event.id,
        documentId: event.documentId,
        name: event.webinar_name || upcomingSf?.title_on_website || "",
        event_type: getWebinarType(event),
        city: event.city?.city || "N/A",
        city_id: event.city?.city_id || 0,
        orientation_id: toNumericId(event.city?.orientation_id),
        date: event.event_date_string || "N/A",
        time: event.event_time_string || "N/A",
        form_link: event.form_api_link || "",
        event_main_card_image: {
          url: resolveStrapiImageUrl(
            event.event_main_card_image?.formats?.medium?.url ||
              event.event_main_card_image?.url ||
              "/events/placeholder.png",
          ),
          alternativeText:
            event.event_main_card_image?.alternativeText || "Event Image",
        },
        template: event.template || "",
        courseName: upcomingSf?.course?.Name || event.template || "N/A",
        courseId: upcomingSf?.course?.course_id || 0,
        webinarId: toNumericId(getRegistrationWebinarId(upcomingSf)),
      };
    });

    const registerStripData = {
      ctaLink: eventData.register_strip?.cta_link || "",
      ctaText: eventData.register_strip?.cta_text || "Register Now", // Default value
      header: eventData.register_strip?.header || "Excited about the event?",
      subHeader:
        eventData.register_strip?.sub_header ||
        "Hurry and register now to secure your spot!",
    };

    const seoData = {
      metaTitle: eventData.seo?.metaTitle || "Events",
      metaDescription: eventData.seo?.metaDescription || "Events",
      shareImage: {
        // `shared.seo` names this field `shareImage`.
        url: resolveStrapiImageUrl(eventData.seo?.shareImage?.url),
        alt: eventData.seo?.shareImage?.alternativeText || "Share Image",
      },
      keywords: eventData.seo?.keywords || "Events",
    };

    const eventMetaData = {
      eventType: getWebinarType(eventData),
      eventId: getRegistrationWebinarId(sf) ?? eventData.mf_event_id,
      zoomWebinarId: sf?.zoom_webinar_id ?? eventData.zoom_webinar_id ?? null,
      sfEventId: sf?.sf_event_id ?? null,
      courseId: detailCourse?.course_id,
      courseName: detailCourse?.Name || eventData.template,
      eventName:
        eventData.webinar_name || sf?.title_on_website || eventData.name,
      city: eventData.city?.city || "N/A",
      city_id: eventData.city?.city_id || null,
      orientation_id: toNumericId(eventData.city?.orientation_id),
    };

    // NASBA/CPE panel, sourced from caira_detail with the static boilerplate as
    // the fallback for anything the CMS leaves blank. `null` when the event has
    // no caira_detail — the detail page hides the whole NASBA section then.
    const cpeData = cairaDetail
      ? getEventCpeContentFromCms(
          { cairaDetail, sfWebinarDetail: sf },
          eventData.template,
        )
      : null;
    // Ordered NASBA rows with per-field hide-if-null-from-CMS logic (empty when
    // there is no caira_detail — the section is hidden then anyway).
    const nasbaRows = cairaDetail
      ? getNasbaRows({ cairaDetail, sfWebinarDetail: sf })
      : [];

    return {
      bannerDeskData,
      aboutEventData,
      whyAttendData,
      learnData,
      expertData,
      combinedAlumniData,
      upcomingEventsData,
      registerStripData,
      seoData,
      eventMetaData,
      cpeData,
      nasbaRows,
      // Visible through its go-live day; the detail page shows the "no webinars"
      // state the day after.
      isActive: isWebinarActive(eventData),
      template: eventData.template || "",
    };
  } catch (error: any) {
    console.error("Error fetching event details:", error.message);
    throw new Error(error.message || "Failed to fetch event details.");
  }
};

// Old event links (ads, emails, socials) outlive their webinar. Once the event
// has passed, show the next upcoming online event with the same template from
// Strapi instead, so both the date and the registration point at a live webinar.
export const fetchEventDetailsOrUpcoming = async (id: string) => {
  const details = await fetchAndFormatEventDetails(id);
  if (details.isActive || !details.template) return details;

  const { onlineEvents } = await fetchAndFormatEventData();
  const upcoming = onlineEvents
    .filter((e: any) => e.template === details.template && e.id !== id && e.isoDate)
    .sort(
      (a: any, b: any) =>
        new Date(a.isoDate).getTime() - new Date(b.isoDate).getTime(),
    )[0];

  return upcoming ? fetchAndFormatEventDetails(upcoming.id) : details;
};

// ✅ Ensure date is always parsed correctly across all browsers
export const formatDates = (dateString: string): string => {
  if (!dateString) return "Invalid Date";

  let date = parseISO(dateString); // Parses ISO string (ensures correct UTC handling)

  if (!isValid(date)) return "Invalid Date";

  return format(date, "d MMMM, yyyy"); // Output: "8 February, 2025"
};

// ✅ Ensure time is formatted consistently
export const formatTime = (dateString: string): string => {
  if (!dateString) return "Invalid Time";

  let date = parseISO(dateString);

  if (!isValid(date)) return "Invalid Time";

  return format(date, "hh:mm a"); // Output: "10:30 AM"
};

// ✅ Securely Delete Cookies (Works across all browsers & subdomains)
const deleteCookie = (name: string) => {
  const domains = [window.location.hostname]; // Current domain
  const parts = window.location.hostname.split(".");
  if (parts.length > 2) {
    domains.push(parts.slice(-2).join(".")); // Parent domain (e.g., example.com)
  }
  domains.forEach((domain) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
  });
};

// ✅ Set Cookies (14-Day Expiration)
export const setCookie = (name: string, value: string, days = 14) => {
  // Default to 14 days
  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000,
  ).toUTCString();

  const isSecure = window.location.protocol === "https:";
  const sameSite = isSecure ? "None" : "Lax"; // Use Lax on localhost (HTTP), None only on HTTPS
  const secureFlag = isSecure ? "Secure" : "";

  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=${sameSite}; ${secureFlag}`;
};

// ✅ Get Cookies (Handles edge cases properly)
export const getCookie = (name: string) => {
  const cookies = document.cookie
    .split("; ")
    .map((cookie) => cookie.split("="));
  for (const [key, value] of cookies) {
    if (key === name && value !== undefined) return decodeURIComponent(value);
  }
  return null;
};

// ✅ Bulletproof Campaign ID Handler
export const handleCampaignID = () => {
  const urlParams = new URLSearchParams(window.location.search);
  let tkId = urlParams.get("tk");
  const existingCookie = getCookie("tkId");
  const directVisitFlag = localStorage.getItem("direct_visit");
  const lastVisitTimestamp = Number(
    localStorage.getItem("direct_visit_timestamp"),
  );
  const tkIdTimestamp = Number(localStorage.getItem("tkId_timestamp"));

  const fourteenDaysInMs = 14 * 24 * 60 * 60 * 1000;
  const oneDayInMs = 24 * 60 * 60 * 1000;

  // 🚀 Reset direct visit flag if 24 hours have passed
  if (lastVisitTimestamp && Date.now() - lastVisitTimestamp > oneDayInMs) {
    localStorage.removeItem("direct_visit");
    localStorage.removeItem("direct_visit_timestamp");

    if (
      existingCookie &&
      tkIdTimestamp &&
      Date.now() - tkIdTimestamp > fourteenDaysInMs
    ) {
      deleteCookie("tkId");
      localStorage.removeItem("tkId_timestamp");
    }
  }

  // 🚀 Remove old campaign cookie if it's older than 14 days
  if (
    existingCookie &&
    tkIdTimestamp &&
    Date.now() - tkIdTimestamp > fourteenDaysInMs
  ) {
    deleteCookie("tkId");
    localStorage.removeItem("tkId_timestamp");
  }

  // 🚀 Ensure `tkId_timestamp` is present if the cookie exists
  if (existingCookie && !tkIdTimestamp) {
    localStorage.setItem("tkId_timestamp", Date.now().toString());
  }

  // 🚀 New campaign ID from URL (ALWAYS OVERRIDES old data)
  if (tkId) {
    tkId = tkId.trim();
    if (
      /^[a-zA-Z0-9_-]+$/.test(tkId) &&
      tkId !== "null" &&
      tkId !== "undefined"
    ) {
      setCookie("tkId", tkId, 14); // Store for 14 days
      localStorage.setItem("tkId_timestamp", Date.now().toString());
      localStorage.removeItem("direct_visit");
      localStorage.removeItem("direct_visit_timestamp");
      return tkId;
    }
  }

  // 🚀 Returning visitor with valid campaign cookie
  if (existingCookie) {
    return directVisitFlag !== "true" ? existingCookie : null;
  }

  // 🚀 First-time direct visit
  if (
    !existingCookie ||
    (tkIdTimestamp && Date.now() - tkIdTimestamp > fourteenDaysInMs)
  ) {
    localStorage.setItem("direct_visit", "true");
    localStorage.setItem("direct_visit_timestamp", Date.now().toString());
    return null;
  }

  return null; // 🚀 Fallback to ensure `null` is returned in all cases
};

export async function insertDataInNetEnquiries(
  payload: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch("/api/globalForm/netEnquiry", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        return {
          error: new Error(
            "Failed to parse error response from internal API route",
          ),
        };
      }

      return {
        error: new Error(
          errorData?.message ||
            `Internal API error with status ${response.status}`,
        ),
      };
    }

    const responseData = await response.json();
    return { data: responseData?.result };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error : new Error("Unknown error occurred"),
    };
  }
}

export async function zapierWebhook(
  payload: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch("/api/goalForm/zapierWebhook", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        return {
          error: new Error(
            "Failed to parse error response from internal API route",
          ),
        };
      }

      return {
        error: new Error(
          errorData?.message ||
            `Internal API error with status ${response.status}`,
        ),
      };
    }

    const responseData = await response.json();
    return { data: responseData };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error : new Error("Unknown error occurred"),
    };
  }
}
export async function zapierWebhookGCC(
  payload: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch("/api/GCCForm/zapierWebhook", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        return {
          error: new Error(
            "Failed to parse error response from internal API route",
          ),
        };
      }

      return {
        error: new Error(
          errorData?.message ||
            `Internal API error with status ${response.status}`,
        ),
      };
    }

    const responseData = await response.json();
    return { data: responseData };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error : new Error("Unknown error occurred"),
    };
  }
}
export async function zapierNursing(
  payload: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch("/api/GCCForm/zapierNursing", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        return {
          error: new Error(
            "Failed to parse error response from internal API route",
          ),
        };
      }

      return {
        error: new Error(
          errorData?.message ||
            `Internal API error with status ${response.status}`,
        ),
      };
    }

    const responseData = await response.json();
    return { data: responseData };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error : new Error("Unknown error occurred"),
    };
  }
}
export async function zapierWebhookCairaAuditors(
  payload: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch("/api/CairaAuditorsForm/zapierWebhook", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        return {
          error: new Error(
            "Failed to parse error response from internal API route",
          ),
        };
      }

      return {
        error: new Error(
          errorData?.message ||
            `Internal API error with status ${response.status}`,
        ),
      };
    }

    const responseData = await response.json();
    return { data: responseData };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error : new Error("Unknown error occurred"),
    };
  }
}
export async function zapierWebhookMBBS(
  payload: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch("/api/mbbs/zapierWebhook", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        return {
          error: new Error(
            "Failed to parse error response from internal API route",
          ),
        };
      }

      return {
        error: new Error(
          errorData?.message ||
            `Internal API error with status ${response.status}`,
        ),
      };
    }

    const responseData = await response.json();
    return { data: responseData };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error : new Error("Unknown error occurred"),
    };
  }
}
export async function zapierWebhookHealthcareNursing(
  payload: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch("/api/nursing/zapierWebhook", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        return {
          error: new Error(
            "Failed to parse error response from internal API route",
          ),
        };
      }

      return {
        error: new Error(
          errorData?.message ||
            `Internal API error with status ${response.status}`,
        ),
      };
    }

    const responseData = await response.json();
    return { data: responseData };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error : new Error("Unknown error occurred"),
    };
  }
}

export async function zapierWebhookMilesFoundation(
  payload: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch("/api/MilesFoundationForm/zapierWebhook", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        return {
          error: new Error(
            "Failed to parse error response from internal API route",
          ),
        };
      }

      return {
        error: new Error(
          errorData?.message ||
            `Internal API error with status ${response.status}`,
        ),
      };
    }

    const responseData = await response.json();
    return { data: responseData };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error : new Error("Unknown error occurred"),
    };
  }
}

export async function zapierWebhookAiReadinessLead(
  payload: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch("/api/ai-readiness/zapierWebhookLead", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        return {
          error: new Error(
            "Failed to parse error response from internal API route",
          ),
        };
      }

      return {
        error: new Error(
          errorData?.message ||
            `Internal API error with status ${response.status}`,
        ),
      };
    }

    const responseData = await response.json();
    return { data: responseData };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error : new Error("Unknown error occurred"),
    };
  }
}

export async function zapierWebhookCairaInstitutions(
  payload: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch("/api/caira-institutions/zapierWebhook", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        return {
          error: new Error(
            "Failed to parse error response from internal API route",
          ),
        };
      }

      return {
        error: new Error(
          errorData?.message ||
            `Internal API error with status ${response.status}`,
        ),
      };
    }

    const responseData = await response.json();
    return { data: responseData };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error : new Error("Unknown error occurred"),
    };
  }
}

export async function zapierWebhookAiReadinessQuiz(
  payload: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch("/api/ai-readiness/zapierWebhookQuiz", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        return {
          error: new Error(
            "Failed to parse error response from internal API route",
          ),
        };
      }

      return {
        error: new Error(
          errorData?.message ||
            `Internal API error with status ${response.status}`,
        ),
      };
    }

    const responseData = await response.json();
    return { data: responseData };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error : new Error("Unknown error occurred"),
    };
  }
}
export async function zapierWebhookBecomeAIreadyQuiz(
  payload: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch("/api/become-ai-ready/zapierWebhookQuiz", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        return {
          error: new Error(
            "Failed to parse error response from internal API route",
          ),
        };
      }

      return {
        error: new Error(
          errorData?.message ||
            `Internal API error with status ${response.status}`,
        ),
      };
    }

    const responseData = await response.json();
    return { data: responseData };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error : new Error("Unknown error occurred"),
    };
  }
}

export async function insertDataForOfficevist(
  payload: any,
): Promise<{ error?: Error; data?: any }> {
  try {
    const response = await fetch("/api/globalForm/officeVisit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        return {
          error: new Error(
            "Failed to parse error response from internal API route",
          ),
        };
      }

      return {
        error: new Error(
          errorData?.message ||
            `Internal API error with status ${response.status}`,
        ),
      };
    }

    const responseData = await response.json();
    const result = responseData?.result;
    const parsedBody =
      typeof result?.body === "string" ? JSON.parse(result.body) : result?.body;
    return { data: { statusCode: result?.statusCode, body: parsedBody } };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error : new Error("Unknown error occurred"),
    };
  }
}

export const slots = [
  "11:00 AM",
  "12:00 PM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
  "4:00 PM",
  "5:00 PM",
  "6:00 PM",
  "7:00 PM",
];

export const sendBrochureEmail = async (
  email: string,
  pdfFileName: string,
  programName: string,
) => {
  try {
    const response = await fetch("/api/sendBrochure", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, pdfFileName, programName }),
    });

    if (!response.ok) {
      throw new Error("Failed to send brochure email");
    }
  } catch (error) {
    console.error("Error sending brochure email:", error);
  }
};

export const cpaTestimonials = [
  {
    name: "Mohit Gogia",
    course: "CPA",
    company: "EY",
    city: "Mumbai",
    content:
      "I recently completed all four CPA papers. Miles Education provides end-to-end guidance for CPA and CMA aspirants and offers structured study materials. Grateful for the journey, CPA MATLAB miles.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Mohit_Gogia.webp",
    youtubeLink: "jP74L4ifaaY?si=TPXYkqYXBMobEjS9",
  },
  {
    name: "Neelam Dhungel",
    course: "CPA",
    company: "EY",
    city: "Delhi",
    content:
      "I’m a CA who enrolled with Miles in 2020 and cleared my CPA in February 2023. The LMS, mobile access, and mock tests helped me stay consistent and revise effectively. Miles is the best for CPA prep.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Neelam_Dhungel.webp",
    youtubeLink: "https://youtu.be/HCCORwwQ5hU?si=Yycwm1qLF5u0l03r",
  },

  {
    name: "Jade Pereira",
    course: "CPA",
    company: "Morgan Stanley",
    city: "Mumbai",
    content:
      "I pursued the US CPA to expand my expertise while working full-time at Morgan Stanley. I heard great feedback about Miles’ curriculum. The flexibility to access Varun Jain’s videos anytime made it perfect for working professionals like me.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Jade_Pereira.webp",
    youtubeLink: "https://youtu.be/7iOdgnIH1WY?si=UFhGG1u8rvUuthLj",
  },
  {
    name: "Aarthi Poovalingam",
    course: "CPA",
    company: "KPMG",
    city: "Chennai",
    content:
      "CPA was a big win for me. Miles supported me through exam prep and licensing, and the material plus MCQs were enough. As the first graduate in my family, becoming a CPA made my parents truly proud.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Aarthi_Poovalingam.webp",
    youtubeLink: "https://youtube.com/shorts/ezN9nI3lpzo?si=Wgmq5S5Ppq3OOIdZ",
  },
  {
    name: "Manasa Sankaran",
    course: "CPA",
    company: "Grant Thornton",
    city: "Bangalore",
    content:
      "Completing the US CPA was a turning point for me. Miles’ supportive faculty, mentoring sessions, and detailed LMS helped me plan, practice, and stay on track. Their guidance made the journey structured and rewarding. CPA MATLAB Miles.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Manasa_Sankaran_CPA.webp",
    youtubeLink: "https://youtu.be/vVu468D32kw?si=xX6P9hiwtLw9s1Zi",
  },
  {
    name: "Meenakshi Kasinathan",
    course: "CPA",
    company: "KPMG",
    city: "Bangalore",
    content:
      "Varun Sir’s videos made every concept clear, and Miles’ books were the perfect revision guide. The LMS explanations and extensive MCQ practice helped me finally clear the US CPA with confidence. CPA MATLAB Miles.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Meenakshi_Kasinathan.webp",
    youtubeLink: "https://youtu.be/kUGcm9axf58?si=WUZjxMweK4JjyEsq",
  },
  {
    name: "Ayushi Shah",
    course: "CPA",
    company: "EY",
    city: "Ahmedabad",
    content:
      "Choosing CPA was easy once I saw how fast the US market was growing. Miles made the journey smooth; I cleared all four sections in one go. The concise books, practice questions, and one-to-one sessions helped me stay on track.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Ayushi_Shah.webp",
    youtubeLink: "https://youtube.com/shorts/xJ08j3JOppA?si=YHSiEIjtLeo5po7K",
  },
  {
    name: "Saundarya Mishra",
    course: "CPA",
    company: "PWC",
    city: "Gurugram",
    content:
      "My name is Saundarya Mishra. I work in risk consulting and started my CPA journey while working. Miles first reached me in college, and their guidance, material, and exam tips helped me stay consistent. For me, CPA means Miles",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Saundarya_Mishra.webp",
    youtubeLink: "https://youtu.be/Fj2EeVsz6R8?si=yFckfU1DszqZvU3J",
  },
  {
    name: "Alisha Virani",
    course: "CPA",
    company: "EY",
    city: "Hyderabad",
    content:
      "I’m Alisha Virani from Hyderabad. I joined Miles for CPA, and their LMS, webinars, and support made preparation easy and thorough. All exam material, MCQs, and simulations are covered, with doubts resolved promptly.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Alisha_Virani.webp",
    youtubeLink: "https://youtu.be/alQ4a1hpqCU?si=DAAFb4HANOw8TKaA",
  },

  {
    name: "Kankshit Gaglani",
    course: "CPA",
    company: "Siemens",
    city: "Mumbai",
    content:
      "I chose Miles for CPA because they offered end-to-end support, from US formalities to licensing, plus excellent coaching that made clearing all four exams achievable with consistent effort.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Kankshit_Gaglani.webp",
    youtubeLink: "https://youtube.com/shorts/yz3HyvUhK5U?si=6YXwTwyU099r42sl",
  },
  {
    name: "Latika Goyal",
    course: "CPA",
    company: "MNC",
    city: "Mumbai",
    content:
      "I’m Latika Goyal, a CA and CPA from Mumbai, working as a Senior Analyst in an MNC. Miles’ CPA program, with mocks and support, gave me the leverage and guidance to clear exams confidently.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Latika%20Goyal.webp",
    youtubeLink: "https://youtube.com/shorts/pB_zFFNyZt0?si=Ks4NkAWc3zYB2kx8",
  },
  {
    name: "Dimple Ramani",
    course: "CPA",
    company: "Sentient",
    city: "Hyderabad",
    content:
      "I’m Dimple Ramani, excited to work with Sentient in US Taxes. Miles guided me through every interview step. Their structured mentoring helped me confidently apply my skills and secure the opportunity successfully.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Dimple_Ramani.webp",
    youtubeLink: "https://youtube.com/shorts/7DNvOznXML0?si=A388w1hm9hsFcbRZ",
  },
  {
    name: "Megha K Nair",
    course: "CPA",
    company: "Sentient",
    city: "Kerala",
    content:
      "The interview process included an assessment, communication round, and personal interview. Miles provided interview-focused training and continuous support. I’m excited to begin my career in taxation.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Megha%20K%20Nair.webp",
    youtubeLink: "https://youtube.com/shorts/mPFaNZ0GwBI?si=6x7mgiAQB49R-u0z",
  },
  {
    name: "Chandana R",
    course: "CPA",
    company: "Sentient",
    city: "Bangalore",
    content:
      "Miles provided the right materials and profile-specific support. I’m excited to learn how taxation works in practice. My mother has been a huge support, and this success belongs equally to the Miles team.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Chandana_R.webp",
    youtubeLink: "https://youtube.com/shorts/FJNORFFyghs?si=_rCNPRyBT-oD2Mn4",
  },
  {
    name: "Dhanesh Khandhar",
    course: "CPA",
    company: "Integrity",
    city: "Ahmedabad",
    content:
      "I started with books, MCQs, and faculty videos. Miles’ material is clear and well-structured. Completing CPA helped me grow in the US industry and advance my career.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Dhanesh_Khandhar.webp",
    youtubeLink: "https://youtube.com/shorts/6i5gRyWr-E4?si=FsTG1YWNI1Ps6BAT",
  },
  {
    name: "Kavit Vora",
    course: "CPA",
    company: "Entigrity",
    city: "Ahmedabad",
    content:
      "Working with international clients inspired me to pursue the US CPA. I’m a qualified CA and US CPA, now a US Tax Senior. Miles’ clear webinars, recorded sessions, and simple study materials made clearing exams easier.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Kavit%20Vora.webp",
    youtubeLink: "https://youtube.com/shorts/Vz5k9l_mCao?si=ah8ODfs-Ocxh_kbY",
  },
  {
    name: "Auvsala Sai Praneeth",
    course: "CPA",
    company: "Sentient",
    city: "Hyderabad",
    content:
      "After multiple rejections, Miles supported me end to end and helped me get placed at Sentient Solutions within a month. Their interview training was crucial. I’m pursuing CPA, cleared REG, and now planning my US journey.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Auvsala_Sai_Praneeth.webp",
    youtubeLink: "https://youtube.com/shorts/cWeW-Y5zzKU?si=dCHufRgMrBFgtkIz",
  },
  {
    name: "Monica Mittal",
    course: "CPA",
    company: "Concero Global Solutions",
    city: "Ahmedabad",
    content:
      "I followed only Miles' material and Varun’s videos, which made concepts easy to grasp. As a CA with 15 years’ experience, Miles supported me with evaluation, planning, and discipline.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Monica%20Mittal.webp",
    youtubeLink: "https://youtube.com/shorts/c6Suj-w-N24?si=QxEU5B4ERd6JZ1YI",
  },
  {
    name: "Gowtham S",
    course: "CPA",
    company: "Sentient",
    city: "Chennai",
    content:
      "I benefited from Miles’ structured training, one-on-one doubt sessions, and interview preparation, including communication and professional skills. Miles’ guidance keeps me focused and excited to complete my CPA and grow in my career.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Gowtham_S.webp",
    youtubeLink: "https://youtube.com/shorts/E4p5fJvehxk?si=FTAeEb0dIVo0EcPS",
  },
  {
    name: "Aarthi Poovalingam",
    course: "CPA",
    company: "KPMG",
    city: "Chennai",
    content:
      "CPA was a big win for me. Miles supported me through exam prep and licensing, and the material plus MCQs were enough. As the first graduate in my family, becoming a CPA made my parents truly proud.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Aarthi_Poovalingam.webp",
    youtubeLink: "https://youtube.com/shorts/ezN9nI3lpzo?si=nFK-PpG7NcrzotgH",
  },
  {
    name: "Sai Geetha Kumar",
    course: "CPA",
    company: "Deloitte",
    city: "Bangalore",
    content:
      "The LMS closely matched the real CPA exam, and the mentors guided me with a clear, detailed plan. With live classes and continuous support, my CPA journey with Miles was truly rewarding. CPA MATLAB Miles.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Sai_Geetha_Kumar.webp",
    youtubeLink: "https://youtube.com/shorts/U88tEavnNKw?si=xmnWMDdfMMP4_Hvo",
  },

  {
    name: "Ricke Bose",
    course: "CMA",
    company: "EY",
    city: "Kochi",
    content:
      "Varun’s lectures, Miles’ study material, and the LMS question bank gave me the clarity and practice I needed. The exam felt manageable only because of Miles. For me, CMA means Miles.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Ricke%20Bose.webp",
    youtubeLink: "fXZNfnBZARA?si=EgsvVJRxvX0QHViy",
  },
  {
    name: "Christo Chazhoor Sajan",
    course: "CMA",
    company: "EY",
    city: "Kochi",
    content:
      "Miles’ structured CMA coaching, clear videos, notes, and mocks helped me stay disciplined and organized, understand concepts, and strategize for the exam. Miles’ structured CMA coaching, clear videos, notes, and mocks helped me stay disciplined and organized, understand concepts, and strategize for the exam.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Christo%20Chazhoor%20Sajan.webp",
    youtubeLink: "-btXjv1fl8I?si=j4Pvbc9tU5HIQYgK",
  },
  {
    name: "Mohit Kumar Singh",
    course: "CMA",
    company: "PWC",
    city: "Ahmedabad",
    content:
      "I cleared both CMA parts on the first attempt and now work at PwC. Miles integrated CMA into my college journey, and consistency, MCQ practice, and a positive mindset helped me succeed. I cleared both CMA parts on the first attempt and now work at PwC. Miles integrated CMA into my college journey, and consistency, MCQ practice, and a positive mindset helped me succeed.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Mohit%20Kumar%20Singh.webp",
    youtubeLink: "jgHWiGSU3wU?si=TogC72swXJHGJ8hz",
  },
  {
    name: "Aman Bhatnagar",
    course: "CMA",
    company: "Wipro",
    city: "Delhi",
    content:
      "I’m Aman, a CMA alum from Miles and now a CPA learner. Miles guided me on career roles and supported me throughout. Varun’s motivation and consistent practice strengthened my concepts.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Aman%20Bhatnagar.webp",
    youtubeLink: "MBk5fYhNjQc?si=OI_4Ij3saaJ4Au-5",
  },
  {
    name: "Tanvi Sanjiv Mangale",
    course: "CMA",
    company: "KPMG",
    city: "Mumbai",
    content:
      "From zero accounting knowledge to clearing CMA in my first attempt, Miles made everything understandable. Varun’s explanations and the mentors’ guidance helped me succeed while balancing college exams.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Tanvi%20Sanjiv%20Mangale.webp",
    youtubeLink: "qtf3htF5Oss?si=DhvitsAJOhPerIC1",
  },
  {
    name: "Prashant Sunil Garje",
    course: "CMA",
    company: "KPMG",
    city: "Mumbai",
    content:
      "Miles guided me through scholarships, provided concept-driven faculty, and a well-structured LMS for quick doubt resolution. Extensive practice material and clear video teaching helped me succeed.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Prashant%20Sunil%20Garje.webp",
    youtubeLink: "_KrImha0yZ4?si=iGqN41OtaVVWdQrb",
  },
  {
    name: "Abhivansh Srivastava",
    course: "CMA",
    company: "Deloitte",
    city: "Hyderabad",
    content:
      "I’ve completed my US CMA and work in Audit & Assurance in the US. Miles provided excellent faculty, LMS, and resources, while Varun’s engaging videos and mentoring made learning practical and effective.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Abhivansh%20Srivastava%20.webp",
    youtubeLink: "KyNvNq8-BR8?si=rV0JvjwZxLWdzuYy",
  },
  {
    name: "Aakanksha Jhawar",
    course: "CMA",
    company: "Deloitte",
    city: "Kochi",
    content:
      "I completed my CMA, and Miles made the journey smooth with flexible classes, supportive faculty, and an excellent LMS I used even during work breaks. I’m now in the final stages of my KPMG placement process.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Aakanksha%20Jhawar.webp",
    youtubeLink: "7n9VLfFyFRQ?si=yOlMD2ro5F9L4SuX",
  },
  {
    name: "Muhammed Noufal T N",
    course: "CMA",
    company: "KPMG",
    city: "Hyderabad",
    content:
      "Miles’ experienced faculty, comprehensive material, mnemonic techniques, and realistic mock tests guided my preparation. Watching lectures, reading notes, and practicing MCQs helped me succeed.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Muhammed%20Noufal%20T%20N.webp",
    youtubeLink: "vzctTWaifvs?si=HG68bMODneyfZAwO",
  },
  {
    name: "Rohit Eapen",
    course: "CMA",
    company: "EY",
    city: "Kochi",
    content:
      "My name is Rohit Matthews. I completed my CMA US with Miles. The notes and Varun sir’s recorded videos built a strong foundation, and consistent MCQ practice, nearly 250 a day, helped me clear both parts confidently.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Rohit%20Mathew%20Eapen.webp",
    youtubeLink: "pPlsjh_Ti-k?si=ZwxOHGoeLic3jenO",
  },
  {
    name: "Prince Gupta",
    course: "CPA + CMA",
    company: "EY",
    city: "Bangalore",
    content:
      "I cleared CMA in 2 months with Miles. Their LMS is bulletproof, and I even got referred to EY by a fellow Miles alumnus. With Miles, you study and join a community that moves your career forward. I am grateful to them.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Prince%20Gupta%20.webp",
    youtubeLink: "dg_C20SfZYQ?si=Eg2CsaafMmm9mf7h",
  },
  {
    name: "Vidya M Pillai",
    course: "CMA",
    company: "KPMG Global Services",
    city: "Bangalore",
    content:
      "As a Commerce graduate working at KPMG, I realized the need for a global qualification, and Miles became my guiding path. I trusted the process and cleared my CMA in just six months on my first attempt.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Vidya%20M%20Pillai.webp",
    youtubeLink: "ctE95geAGM0?si=nKaa4FMqzQi29tOh",
  },
  {
    name: "Shreya Lakshmi",
    course: "CMA",
    company: "EY",
    city: "Kochi",
    content:
      "Miles is truly the reason for my smile. Miles has always been there to clear every doubt with patience. My biggest tip for CMA aspirants: take it one step at a time, stay positive, and trust your preparation.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Shreya%20Lakshmi%20CMA.webp",
    youtubeLink: "b16NR1Xh_4M?si=TAFV1Qq2KqraGQ55",
  },
  {
    name: "Deeksha Suresh",
    course: "CMA",
    company: "Wipro",
    city: "Bangalore",
    content:
      "After completing my Commerce degree, I pursued a US CMA with Miles and found my true passion in finance and analysis. The faculty was exceptional, and the recorded sessions made studying flexible with my irregular schedule.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Deeksha%20Suresh.webp",
    youtubeLink: "JByPuYoURlU?si=kYCef3GmGMNckXZI",
  },
  {
    name: "Rahul Rajan",
    course: "CMA",
    company: "Bank of America Merrill Lynch",
    city: "Chennai",
    content:
      "As a Chartered Accountant, I chose the US CMA for its global recognition. Miles guided me from day one of my CMA journey to achieving an All-India 3rd rank and receiving the award in Denver, USA.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Rahul%20Rajan.webp",
    youtubeLink: "HPYwp98vSgk?si=ZAPL12ZuS79ZvHhi",
  },
  {
    name: "Apoorva Ramaseshan",
    course: "CMA",
    company: "The India Cements Ltd",
    city: "Chennai",
    content:
      "I completed my CMA while pursuing my B.Com, even before graduating. Since our college didn’t cover many concepts in depth, Miles bridged that gap perfectly. The team valued every student’s effort and provided constant motivation throughout the journey.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Apoorva%20Ramaseshan.png.webp",
    youtubeLink: "RJEQNvVC_ag?si=tSvaSz5TiT6JsB6R",
  },
  {
    name: "Syed Asif A",
    course: "CMA",
    company: "Bank of New York",
    city: "Chennai",
    content:
      "My CMA journey with Miles was solid. Varun’s videos, the question bank, mentoring support, and mock tests similar to the actual exam helped me understand concepts clearly. For CMA or CPA, I strongly recommend Miles.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Syed%20Asif%20.webp",
    youtubeLink: "6bH8GGeJ2Gk?si=odJ6f09I7gCxobEf",
  },
  // {
  //   name: "Niramaithi V",
  //   course: "CMA",
  //   company: "Wells Fargo International Bank",
  //   city: "Chennai",
  //   content:
  //     "I’m Niraimathi, a proud CMA alumna of Miles. I believe management accountants drive a company’s performance. Varun’s energetic teaching boosted my confidence and helped me stay consistent.",
  //   thumbnail:
  //     "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Niramaithi%20V.webp",
  //   youtubeLink: "5xlt7TuZGVA?si=y65viCB1wshfCorm",
  // },
  {
    name: "Nimit Negi",
    course: "CMA",
    company: "HCL",
    city: "Delhi",
    content:
      "I completed my CMA by studying three hours daily, revising concepts, watching LMS videos, and practising MCQs without fail. Seeing the result and sharing it with my mom was the happiest moment.",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Nimit%20Negi%20CMA.webp",
    youtubeLink: "tCNmOMODsPA?si=MRdAKiBY1AVBRYb4",
  },
  {
    name: "Damini Gupta",
    course: "CMA",
    company: "Tata Power",
    city: "Delhi",
    content:
      "I’m Damini, working at Tata Power Delhi. Varun’s energetic teaching and Miles’ material kept me motivated. Consistent practice and supportive mentors made a big difference. Miles is truly a one-stop solution!",
    thumbnail:
      "https://ik.imagekit.io/mileseducation/miles_website/Alumni/Damini%20Gupta.webp",
    youtubeLink: "eJn3gJL-9ps?si=99Gy3RITIUYhnUPA",
  },
];

// export const jobCards = [
//   {
//     course: "CPA",
//     company: "Aprio",
//     city: "Mumbai",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/Aprio.webp",
//     qualification: "CPA",
//     location: "Mumbai",
//     postion: "Senior Tax Associate",
//     experience: "3 - 5 years in US International Tax",
//     logo_first_letter: "A",
//     days: 5,
//     job_title: "Senior Tax Associate",
//     salary: "-",
//     key_responsibilities: [
//       "Prepare and review complex U.S. international tax compliance filings, including Forms 5471, 5472, 8865, 8858, 1118, 1120‑F, 8832, 8990, 8992, and 8993, and related statements and disclosures.",
//       "Analyze and apply U.S. international tax provisions, including Subpart F, GILTI, FDII, BEAT, foreign tax credits, withholding taxes, and treaty provisions.",
//       "Deliver strategic inbound and outbound U.S. and international tax consulting, including repatriation planning, holding company and financing structures, foreign exchange considerations, and support for internal reorganizations and post‑acquisition integrations.",
//       "Advise on the tax aspects of partnership ventures and provide structuring and diligence guidance for international aspects of M&A transactions.",
//       "Develop innovative international tax‑saving strategies through analysis of supply‑chain and transfer‑pricing considerations.",
//       "Draft and contribute to technical tax memoranda and client deliverables addressing U.S. international tax issues.",
//       "Identify planning opportunities and potential risks for multinational clients and communicate findings clearly to managers and partners.",
//       "Manage day‑to‑day execution of international tax engagements, including budgeting awareness, workflow coordination, and meeting deadlines.",
//       "Coordinate with U.S. domestic tax teams, transfer pricing professionals, and foreign firms to ensure consistent global tax outcomes.",
//       "Serve as a primary point of contact for routine client questions and data requests, escalating complex issues as appropriate.",
//     ],
//     note: "",
//     skills: ["US International Tax"],
//     requirements: ["CPA"],
//     about_company:
//       "Aprio is redefining what it means to be a global advisory and accounting firm. We help businesses and individuals Account for Anything™ — delivering clarity and confidence in a world full of unknowns. Aprio is the brand name under which Aprio, LLP, and Aprio Advisory Group, LLC deliver professional services to clients worldwide. Aprio Advisory Group, LLC provides advisory, tax, legal, wealth, and talent solutions to help build value, drive growth, manage risk, and protect wealth, while Aprio, LLP delivers audit and attest services with uncompromising quality and integrity. With more than 2,800 team members serving clients in 50+ countries and $5.5B in assets under management, Aprio is consistently recognized as a Top Workplace, celebrated for transformative growth on the Inc. 5000 and Inc. Power Partners lists, and led by Richard Kopelman, named to Accounting Today's Top 100 Most Influential People in Accounting.",
//   },
//   {
//     course: "CPA",
//     company: "Aprio",
//     city: "Mumbai",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/Aprio.webp",
//     qualification: "Commerce Graduate / CPA",
//     location: "Mumbai",
//     postion: "Tax Associate",
//     experience: "1 - 3 years in State & Income Tax",
//     logo_first_letter: "A",
//     days: 5,
//     job_title: "Tax Associate",
//     salary: "-",
//     key_responsibilities: [
//       "Interact closely with clients to provide tax planning, consulting, and compliance services",
//       "Work closely with seniors and managers on delivering innovative tax planning strategies and managing state income tax compliance jobs",
//       "Research to assist in the completion of nexus studies, revenue sourcing studies and more",
//       "Constantly learn and teach to keep up with technical topics",
//       "Collaborate as a team to build this group from the ground up, establishing standard templates, processes and procedures",
//     ],
//     note: "",
//     skills: [""],
//     requirements: ["Commerce Graduate / CPA"],
//     about_company:
//       "Aprio is redefining what it means to be a global advisory and accounting firm. … (same as above)",
//   },
//   {
//     course: "CPA",
//     company: "Elliott Davis India LLP",
//     city: "Bangalore",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/Elliott-Davis.webp",
//     qualification: "CPA/CA",
//     location: "Bangalore",
//     postion: "Senior Associate - Manager",
//     experience: "3‑12 years of experience in M&A Accounting & Finance Advisory",
//     logo_first_letter: "E",
//     days: 5,
//     job_title: "Senior Associate",
//     salary: "-",
//     key_responsibilities: [
//       "Provide timely, high quality client service that meets or exceeds client expectations",
//       "Address and rectify financial information gaps by implementing robust accounting processes, policies, and procedures to ensure the generation of accurate financial data",
//       "Assist with preparation of opening balance sheets and post‑close purchase price adjustments",
//       "Assist with conversion to accrual basis accounting and month‑end close processes to support going forward",
//       "Develop an understanding of the client's business and recognise key performance drivers, trends and developments and identify performance improvement opportunities and recommend creative solutions to complex issues and broaden our business relationships where possible",
//       "Ensure all deliverables are completed on time and meet the firm's quality standards",
//     ],
//     note: "",
//     skills: [""],
//     requirements: ["CPA/CA"],
//     about_company:
//       "Elliott Davis is a top 50 business solutions firm founded in 1920. Follow along here for our people, ideas, & events, and visit elliottdavis.com for more information. …",
//   },
//   {
//     course: "CPA",
//     company: "Elliott Davis India LLP",
//     city: "Bangalore",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/Elliott-Davis.webp",
//     qualification: "CPA/CA/CMA",
//     location: "Bangalore",
//     postion: "Manager - Accounting Advisory Services",
//     experience: "7+ years of experience in Client Accounting Services",
//     logo_first_letter: "E",
//     days: 5,
//     job_title: "Manager - Accounting Advisory Services",
//     salary: "-",
//     key_responsibilities: [
//       "Impact customers by providing an accurate and insightful overview of the organization’s performance, enabling informed decision making by management to drive business growth and optimize operational strategies.",
//       "Manage month‑end close process for multiple outsourced accounting customers with a focus on timely delivery of high‑quality financial statements.",
//       "Develop and maintain strong business relationships with individual clients.",
//       "Review financial statements to ensure accuracy and completeness. Analyze key financial and operational data to identify trends and identify key areas of client performance.",
//       "Assess current month end close processes and procedures and make recommendations for improvements.",
//       "Identify issues and recommend creative solutions to complex client issues.",
//       "Provide leadership and guidance to the internal team, including training and mentoring. Foster a culture of collaboration, accountability, and continuous improvement.",
//       "Identify and pursue opportunities to serve new customers and to provide additional services to your clients.",
//       "Monitor client engagement economics and participate in billing and collection process.",
//       "Leverage technology for increased effectiveness and efficiency.",
//       "Work with intelligent and innovative professionals who challenge you to grow and be your best self.",
//     ],
//     note: "",
//     skills: [""],
//     requirements: ["CPA/CA/CMA"],
//     about_company:
//       "Elliott Davis is a top 50 business solutions firm founded in 1920. Follow along here for our people, ideas, & events, and visit elliottdavis.com for more information. …",
//   },
//   {
//     course: "OTHERS",
//     company: "Elliott Davis India LLP",
//     city: "Bangalore",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/Elliott-Davis.webp",
//     qualification: "Commerce Graduate",
//     location: "Bangalore",
//     postion: "Tax Senior",
//     experience: "4+ years of experience in 1120",
//     logo_first_letter: "E",
//     days: 5,
//     job_title: "Tax Senior",
//     salary: "-",
//     key_responsibilities: [
//       "Apply tax knowledge to intermediate and complex, multi-state business tax returns",
//       "Perform ASC 740 (income tax accounting/tax provisions) computations and reporting",
//       "Ability to prepare and/or review complete set of financial statements",
//       "Work effectively in analyzing business transactions",
//       "Perform complete and efficient reviews of business tax returns",
//       "Gather data from customer, Assurance department, and third party sources",
//       "Perform tax research with view for alternatives to satisfy customer's objectives",
//       "Resolve all open items/issues encountered on assigned engagements",
//       "Actively monitor engagement economics by managing budgets and seeking efficiencies",
//       "Direct and control concurrent assignments to successful completion",
//       "Develop and lead associates through engagements, delegate assignments and tasks when appropriate",
//       "Provide honest, objective and constructive feedback in timely manner to associates",
//       "Collaborate to identify and sell opportunities among existing customers",
//       "Serve as a mentor and role model through active participation in firm committees, departmental matters, and events",
//       "Active in recruitment of new and experienced associates",
//     ],
//     note: "",
//     skills: [""],
//     requirements: ["Commerce Graduate"],
//     about_company:
//       "Elliott Davis is a top 50 business solutions firm founded in 1920. Follow along here for our people, ideas, & events, and visit elliottdavis.com for more information. …",
//   },
//   {
//     course: "CPA",
//     company: "Crete India",
//     city: "Hyderabad",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/Crete.webp",
//     qualification: "CPA/EA/CA",
//     location: "Hyderabad",
//     postion:
//       "Senior Associate / Assistant Manager / Manager – Tax (client prefers local candidates)",
//     experience: "6+ years in 1120/1120S/1065",
//     logo_first_letter: "C",
//     days: 5,
//     job_title:
//       "Senior Associate / Assistant Manager / Manager (Tax Compliance)",
//     salary: "-",
//     key_responsibilities: [
//       "Review federal and state tax returns for corporations (1120), S corporations (1120S), and partnerships (1065)",
//       "Review formatting Trial Balance & rolling them over",
//       "Analyze financial statements and workpapers for tax compliance and accuracy",
//       "Identify tax issues and propose solutions during the return preparation process",
//       "Perform technical research and apply tax law to specific client situations",
//       "Coordinate and communicate with clients and internal team members to gather missing information",
//       "Supervise and mentor tax preparers and junior staff; review their work and provide constructive feedback",
//       "Ensure timely delivery of quality tax returns in compliance with internal deadlines and client expectations",
//       "Assist in client onboarding, workflow management, and process improvements",
//       "Stay current on U.S. federal and state tax law changes and communicate implications to the team",
//     ],
//     note: "",
//     skills: [""],
//     requirements: ["CPA/EA/CA"],
//     about_company:
//       "Crete India is a technology-led accounting and professional services firm redefining how tax, advisory, and accounting solutions are delivered. …",
//   },
//   {
//     course: "OTHERS",
//     company: "Aprio",
//     city: "Mumbai",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/Aprio.webp",
//     qualification: "Commerce Graduate",
//     location: "Mumbai",
//     postion: "Senior Tax Associate",
//     experience: "3+ years in US Federal Tax",
//     logo_first_letter: "A",
//     days: 5,
//     job_title: "Senior Tax Associate",
//     salary: "-",
//     key_responsibilities: [
//       "Interact closely with clients to provide tax planning, consulting, and compliance services",
//       "Work closely with partners on delivering innovative tax planning strategies",
//       "Expect minimal to no travel required",
//     ],
//     note: "",
//     skills: [""],
//     requirements: ["Commerce Graduate"],
//     about_company:
//       "Aprio is redefining what it means to be a global advisory and accounting firm. …",
//   },
//   {
//     course: "OTHERS",
//     company: "Aprio",
//     city: "Mumbai",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/Aprio.webp",
//     qualification: "Commerce Graduate",
//     location: "Mumbai",
//     postion: "Tax Manager",
//     experience: "6+ years in US Federal Tax",
//     logo_first_letter: "A",
//     days: 5,
//     job_title: "Manager",
//     salary: "-",
//     key_responsibilities: [
//       "Manage and lead a team of tax professionals, delegating tasks and overseeing teamwork.",
//       "Develop and implement tax strategies while staying updated with changing tax laws and regulations.",
//       "Provide consulting services on mergers and acquisitions, tax planning, and corporate restructuring.",
//       "Conduct tax research to identify planning opportunities.",
//       "Prepare and review tax returns ensuring compliance with tax laws and regulations.",
//       "Communicate with clients, tax authorities, and regulatory bodies.",
//       "Manage client relationships and ensure client satisfaction.",
//       "Train and mentor team members.",
//     ],
//     note: "",
//     skills: [""],
//     requirements: ["Commerce Graduate"],
//     about_company:
//       "Aprio is redefining what it means to be a global advisory and accounting firm. …",
//   },
//   {
//     course: "CPA",
//     company: "Paperchase Accountancy India pvt. ltd",
//     city: "Trivandrum",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/paperchase_accountancy_india.webp",
//     qualification: "CPA/CMA",
//     location: "Trivandrum",
//     postion: "Multiple",
//     experience:
//       "2‑4 years in AP, AR, Finalization, Payroll, Audit, and UK/US accounting processes (preferred)",
//     logo_first_letter: "P",
//     days: 5,
//     job_title: "Multiple",
//     salary: "-",
//     key_responsibilities: "",
//     note: "",
//     skills: [""],
//     requirements: ["CPA/CMA"],
//     about_company:
//       "Paperchase Accountancy is a leading hospitality and consulting firm. At Paperchase Accountancy, our focus is not only on business, but on the incredible individuals who make up our team in India. With over 30 years of experience in serving hospitality clients worldwide, we have established ourselves as a leading consulting firm. Our expertise and commitment allow us to make precise, reliable and accurate monetary decisions to provide exceptional advisory services for our clients from India to the US, UK, Middle East and beyond. Our success is a testament to the dedication and expertise of our 1400+ employees based in India. They are the backbone of our organization, and we take great pride in their talent and diversity. We create an environment that fosters growth and development, empowering our employees to reach new heights.At Paperchase, we understand the power of innovative tailored solutions in F & A and Analytics services. Our commitment to excellence drives us to constantly adapt and stay ahead in the dynamic world of Financial Services by truly comprehending the unique needs of our clients.",
//   },
//   {
//     course: "OTHERS",
//     company: "RubinBrown (Client of Eisner Amper)",
//     city: "Mumbai",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/Rubin-Brown.webp",
//     qualification: "CA",
//     location: "Mumbai",
//     postion: "Manager – Professional Standards & Audit Quality",
//     experience: "10+ years in Professional Standards & Audit Quality Control",
//     logo_first_letter: "R",
//     days: 5,
//     job_title: "Manager – Professional Standards & Audit Quality",
//     salary: "-",
//     key_responsibilities: [
//       "Support the implementation, monitoring, and ongoing operations of the firm's System of Quality Management (SoQM) and related quality initiatives",
//       "Support the firm's inspection programs and monitoring activities, including engagement quality review process, file prep tools, internal review programs, deficiency tracking, and remediation action plans",
//       "Collaborate with stakeholders across PSG, Assurance leadership, and enabling functions to implement quality initiatives effectively",
//       "Support other PSG initiatives and the Firm’s commitment to the highest levels of quality",
//     ],
//     note: "",
//     skills: [""],
//     requirements: ["CA"],
//     about_company:
//       "RubinBrown is one of the nation's leading accounting and professional consulting firms with a commitment to building personal relationships and delivering totally satisfied clients. …",
//   },
//   {
//     course: "OTHERS",
//     company: "RubinBrown (Client of Eisner Amper)",
//     city: "Mumbai",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/Rubin-Brown.webp",
//     qualification: "Any Commerce Graduate",
//     location: "Mumbai",
//     postion: "Analyst / Senior Analyst – M&A (Advisory)",
//     experience: "2‑4 years in Audit or Consulting",
//     logo_first_letter: "R",
//     days: 5,
//     job_title: "Analyst / Senior Analyst",
//     salary: "-",
//     key_responsibilities: [
//       "Lead financial due diligence in M&A transactions (Quality of Earnings reports)",
//       "Analyze financial data to understand accounting policies, trends and risks affecting valuation",
//       "Coordinate across service lines and communicate with external stakeholders",
//       "Create tailored diligence reports and supervise junior team members",
//       "Assist in business development and growing the firm",
//     ],
//     note: "Shift 12:30 p.m.–9:30 p.m.",
//     skills: [""],
//     requirements: ["Any Commerce Graduate"],
//     about_company:
//       "RubinBrown is one of the nation's leading accounting and professional consulting firms with a commitment to building personal relationships and delivering totally satisfied clients. …",
//   },
//   {
//     course: "OTHERS",
//     company: "Armanino India",
//     city: "Hyderabad",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/armanino.webp",
//     qualification: "Any Commerce Graduate",
//     location: "Hyderabad",
//     postion: "Senior Functional Consultant",
//     experience:
//       "5+ years in ERP Finance consulting, with hands-on D365 Finance implementation",
//     logo_first_letter: "A",
//     days: 5,
//     job_title: "Senior Functional Consultant",
//     salary: "-",
//     key_responsibilities: [
//       "Lead and support end‑to‑end D365 Finance implementations, including requirements gathering, solution design, configuration, and testing.",
//       "Configure Finance modules such as GL, AP, AR, Fixed Assets, Banking, Budgeting, and related FSCM workflows.",
//       "Prepare functional design documents, testing scripts, and support UAT execution.",
//       "Partner with technical teams for integrations, customizations, and ISV solutions.",
//       "Support data migration activities—mapping, validation, and financial reconciliations.",
//       "Provide post‑go‑live support and resolve Level 2 & Level 3 finance functional issues.",
//       "Identify opportunities for process optimization, automation, and improved financial controls.",
//       "Mentor junior consultants and contribute to internal knowledge building and practice development.",
//     ],
//     note: "",
//     skills: [""],
//     requirements: ["Any Commerce Graduate"],
//     about_company:
//       "Armanino India LLP (AILP), situated in Ahmedabad, Gujarat, …",
//   },
//   {
//     course: "CPA",
//     company: "Arcesium LLC",
//     city: "Hyderabad",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/Arcesium.webp",
//     qualification: "CPA / CA",
//     location: "Hyderabad",
//     postion: "Manager",
//     experience:
//       "8–12 years of progressive experience in financial reporting, accounting, tax, and controllership across multi‑entity global organisations",
//     logo_first_letter: "A",
//     days: 5,
//     job_title: "Manager",
//     salary: "-",
//     key_responsibilities: [
//       "Lead end‑to‑end month‑end, quarterly, and year‑end close processes across multiple legal entities and jurisdictions, ensuring compliance with US GAAP, IFRS, and local statutory frameworks.",
//       "Prepare and validate consolidated financial statements, including intercompany eliminations, equity accounting, and multi‑entity reporting.",
//       "Manage statutory audits and act as the primary liaison with external auditors, addressing technical accounting and tax queries and ensuring timely audit completion.",
//       "Oversee direct and indirect tax compliance including Corporate Income Tax, VAT/GST, HMRC filings, and coordination with external tax advisors.",
//       "Handle cross‑border accounting matters including transfer pricing implications, foreign exchange accounting, intercompany transactions, and international tax considerations.",
//       "Support treasury operations including cash and liquidity management, banking operations, foreign currency hedging, debt/equity financing, and intercompany funding structures.",
//       "Ensure timely preparation and filing of statutory financial statements, tax returns, and regulatory reports across UK, Portugal, APAC, and other expansion regions.",
//       "Monitor changes in accounting standards and tax regulations; assess impact and implement required policy or system changes.",
//       "Strengthen internal controls and governance frameworks to mitigate financial reporting, tax, and treasury risks.",
//       "Drive automation and process improvement initiatives to enhance close cycle efficiency, reporting accuracy, and operational controls.",
//       "Partner with Finance, Legal, FP&A, Treasury, and Operations teams to provide accounting and tax guidance supporting strategic business decisions.",
//       "Proactively identify financial reporting and tax risks through scenario planning and technical analysis.",
//       "Manage and coordinate external consultants and ensure effective delivery of statutory and tax compliance activities.",
//     ],
//     note: "",
//     skills: [""],
//     requirements: ["CPA / CA"],
//     about_company:
//       "Arcesium is a global financial technology company delivering front-to-back technology solutions for some of the world's most sophisticated financial institutions…",
//   },
//   {
//     course: "CPA",
//     company: "Ernst & Young",
//     city: "Bengaluru",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/ey.webp",
//     qualification:
//       "Graduate with CPA 2 paper cleared (or) CA Inter with Articleship (or) Part Qualified ACCA (9/13) / CAIRA Preferred",
//     location: "Bengaluru | Chennai | Hyderabad",
//     postion: "Associate",
//     experience: "1–3 Years",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: "Associate",
//     salary: "-",
//     key_responsibilities: [
//       "Clearly and accurately document the execution of procedures as defined by engagement objectives, onshore team’s work step allocation, and planned approach",
//       "Demonstrate a basic understanding of relevant industries/sectors and awareness of technical issues or accounting risks",
//       "Stay updated on current business and economic developments relevant to client engagements",
//       "Identify potential issues or unusual relationships from financial statement analysis and escalate to GDS Senior/Manager",
//       "Collaborate with GDS and local team members to set goals, responsibilities, and deliverables",
//       "Ensure work is delivered in line with EY methodology, project plan, and quality standards",
//       "Maintain professionalism, demonstrate competence, and communicate clearly with GDS and local assurance teams",
//     ],
//     note: "Contract Duration: 4 – 6 months",
//     skills: [
//       "Strong knowledge of basic accounting and bookkeeping",
//       "Proficiency in written and verbal communication",
//       "Strong analytical and problem-solving abilities",
//       "Ability to work collaboratively in team environments",
//     ],
//     requirements: [
//       "Graduate with CPA 2 paper cleared (or) CA Inter with Articleship (or) Part Qualified ACCA (9/13) / CAIRA Preferred",
//     ],
//     about_company:
//       "EY (formerly Ernst & Young) is a global professional services giant, one of the Big Four, offering Assurance, Consulting, Strategy & Transactions, and Tax services, focused on helping clients build trust, grow sustainably, and solve complex problems using technology like AI, driven by their purpose of building a better working world. They operate as a vast network of member firms, employing hundreds of thousands globally, and are known for their deep sector expertise and commitment to innovation and responsible growth. ",
//   },
//   {
//     course: "CMA",
//     company: "KPMG ",
//     city: " Kolkata | Bangalore",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/kpmg.webp",
//     qualification:
//       "MBA Finance / Mcom (Full time) with Mandatory 60 percent and above throughout from 12th / CAIRA Preferred",
//     location: "Kolkata | Bangalore",
//     postion: "Associate -1 ",
//     experience: "Fresher",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: "Associate -1",
//     salary: "-",
//     key_responsibilities: [
//       "Demonstrate basic accounting and auditing knowledge",
//       "Understand audit procedures performed by GDC",
//       "Familiarize with audit documentation tool –eAudIT",
//       "Familiarize with audit tools/applications used (IDEA, WISDOM,CASEWARE, MS Office)",
//       "Efficient and effective performance of audit procedures within the time provided with Quality",
//       "Nominate in self-development activities in technical and project management areas",
//       "Demonstrate strong inter-personal skills",
//       "Participate in / lead team and group activities",
//       "Be an integral part of the GDC teams assigned to US audit engagements",
//       "Demonstrate basic accounting and auditing knowledge",
//       "Understand and learn audit procedures performed by GDC",
//       "Maintain effective and timely communication with the onshore and offshore team members.",
//       "Ensure the work is performed with high quality and completed within agreed turnaround time.",
//       "Maintain the chargeability of hours required to meet the targets set for the year",
//       "Be part of and nominate self in various firm initiatives and activities.",
//       "Demonstrate strong inter-personal skills",
//       "Participate in team and group activities",
//     ],
//     note: "",
//     skills: [""],
//     requirements: [
//       "MBA Finance / Mcom (Full time) with Mandatory 60 percent and above throughout from 12th / CAIRA Preferred",
//     ],
//     about_company:
//       'KPMG is a global network of professional services firms, one of the "Big Four," offering audit, tax, and advisory services worldwide, known for its industry-focused, tech-enabled solutions and a structure of independently owned member firms. Formed from the 1987 merger of Klynveld Main Goerdeler (KMG) and Peat Marwick International (PMI), its name reflects these origins (Klynveld Peat Marwick Goerdeler). KPMG provides services in over 140 countries, helping businesses with complex financial and strategic challenges, emphasizing integrity, excellence, and innovation.',
//   },
//   {
//     course: "CPA",
//     company: "Baker Tilly",
//     city: "Bangalore",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/bakertilly.webp",
//     qualification: "CPA / CA / CAIRA Preferred",
//     location: "Bangalore",
//     postion: "Assurance Associate",
//     experience: "6M-1 year of experience in Statutory Audit",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: "Assurance Associate",
//     salary: "-",
//     key_responsibilities: [
//       "Perform testing and planning to support an audit opinion, including preparing supporting work paper documentation, performing internal control and substantive testing, conducting analytical reviews of account balances, and preparing financial statements.",
//       "Research technical issues that arise during the course of engagements.",
//       "Create draft audit reports for management review.",
//       "Assist Seniors, Managers and Partners in preparing risk assessment, and developing audit plan programs.",
//       "Identify opportunities for continuous improvement and contribute towards implementing them.",
//     ],
//     note: "",
//     skills: [""],
//     requirements: ["CPA / CA / CAIRA Preferred"],
//     about_company: `Baker Tilly is a major global network of independent accounting and business advisory firms, offering assurance, tax, legal, and consulting services, known for supporting middle-market companies with a focus on personalized service, innovation (like AI and ESG), and deep local expertise within its worldwide structure, aiming to provide a "genuine coast-to-coast and global advantage". They combine international reach with local understanding, helping businesses grow globally, and are a significant player, ranking among the world's largest accounting networks.`,
//   },
//   {
//     course: "CPA",
//     company: "Grant Thornton",
//     city: "Bangalore",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/grant_thornton%20Indus.webp",
//     qualification: "CPA / CAIRA Preferred",
//     location: "Bangalore",
//     postion: "Associate - Controls Advisory",
//     experience: "Fresher",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: "Associate - Controls Advisory",
//     salary: "-",
//     key_responsibilities: [
//       "Performing SOX 404 Business Process Audits: Conducting Tests of Design and Tests of Operating effectiveness of controls on client engagements",
//       "Ensure quality delivery: Ensure the work delivered is complete and accurate through self-review process within the established turnaround times (or allotted budget)",
//       "Ensure compliance with engagement plans, internal quality & risk management procedures of the firm",
//       "Anticipate and identify engagement related risks and escalate issues as appropriate on a timely basis",
//       "Project management of engagements and responsible for attending the regular status calls with onshore team members based out of GTUS",
//     ],
//     note: "",
//     skills: [
//       "Elementary knowledge of accounting concepts and business cycles like Procure to Pay, Order to Cash, Financial statement close process, fixed assets management, etc.",
//       "Adept in Microsoft office tools (Word, Excel, Power point)",
//       "Excellent written and spoken communication skills",
//     ],
//     requirements: ["CPA / CAIRA Preferred"],
//     about_company: `Grant Thornton is a global network of independent assurance, tax, and advisory firms, serving privately held businesses, public entities, and public sectors with services like audit, consulting, risk management, and digital transformation, known for blending global scale with local insights and a client-focused, personal approach. With over 76,000 people across 156 markets, member firms like Grant Thornton Bharat in India and Grant Thornton LLP in the U.S. provide tailored solutions, aiming to build trust and help clients realize their ambitions.`,
//   },
//   {
//     course: "CPA",
//     company: "Crowe LLP",
//     city: "Noida",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/crowe_llppng.webp",
//     qualification:
//       "Graduate + CPA 2 papers cleared  / CA-inter / CAIRA Preferred",
//     location: "Noida",
//     postion: "Audit Associate",
//     experience: "Fresher",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: "Audit Associate",
//     salary: "-",
//     key_responsibilities: [
//       "Apply auditing and accounting principles in real client engagements.",
//       "Assist field auditors in preparing work papers, financial schedules, and analyses.",
//       "Perform analytical procedures, reconciliations, and financial statement reviews.",
//       "Collaborate with assurance teams to identify findings and support audit reporting.",
//       "Ensure accuracy, compliance, and timely completion of audit deliverables.",
//     ],
//     note: "",
//     skills: [""],
//     requirements: [
//       "Graduate + CPA 2 papers cleared  / CA-inter / CAIRA Preferred",
//     ],
//     about_company: "-",
//   },
//   {
//     course: "CPA",
//     company: "Plante Moran",
//     city: "Mumbai",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/plante_moran.webp",
//     qualification:
//       "Bachelor’s Degree in Accounting / CA / EA / CAIRA Preferred",
//     location: "Mumbai",
//     postion: "US Tax Staff / US Tax Senior",
//     experience: " 1-5 years in Public Accounting. ",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: "US Tax Staff / US Tax Senior",
//     salary: "-",
//     key_responsibilities: [
//       "Participation in client engagements from beginning to completion - including planning, executing, and completing tax projects",
//       "Preparation of returns for partnership, C Corporation, and S Corporation clients",
//       "Development of client-focused tax planning and transaction structuring concepts. Proactively interact with the client to gather information",
//       "Optional participation in internal learning and development committees and other firm leadership objectives",
//       "Supervise engagements and assume full responsibility for tax compliance",
//       "Participating as an integral member of client engagements teams from the beginning to end - including planning, executing, and completing tax projects",
//       "Reviewing or preparing tax returns for multiple business types including partnerships, Ccorporations, and S-corporations",
//       "Develop, coordinate, and supervise projects of tax staff",
//       "Performing tax research and preparing technical memorandum",
//       "Analyzing ideas and strategies to deliver tax savings opportunities to clients",
//       "Build and maintain client relationships and proactively interact to gather information, resolve taxrelated issues, and make recommendations for business and process improvements",
//     ],
//     note: "",
//     skills: [""],
//     requirements: [
//       "Bachelor’s Degree in Accounting / CA / EA / CAIRA Preferred",
//     ],
//     about_company: `Plante Moran is a large U.S. accounting, tax, consulting, and wealth management firm, founded in 1924, known for its distinctive "people-first" culture and commitment to client service over pure profit, operating across the U.S. and globally with services in audit, tax, advisory, and financial planning, prioritizing personal relationships and ethical practice.`,
//   },
//   {
//     course: "CMA",
//     company: "CohnReznick LLP",
//     city: "Chennai",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/cohnreznick.webp",
//     qualification: "Bcom / Mcom / CA Inter / CAIRA Preferred",
//     location: "Chennai",
//     postion: "Analyst (4-month contract)",
//     experience: "Fresher",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: "Analyst (4-month contract)",
//     salary: "-",
//     key_responsibilities: [
//       "Execute audit procedures for US-based clients in line with US GAAP and PCAOB standards",
//       "Conduct substantive testing, analytical reviews, and internal control assessments",
//       "Prepare audit documentation and workpapers as per firm and regulatory requirements",
//       "Collaborate with cross-functional teams to meet project timelines",
//       "Communicate audit findings and concerns effectively to team leads",
//       "Assist in preparing financial statements and related disclosures",
//       "Ensure confidentiality and integrity while handling sensitive financial data",
//     ],
//     note: "",
//     skills: [
//       "Strong accounting and auditing knowledge",
//       "Familiarity with US GAAP (preferred)",
//       "Excellent analytical and problem-solving skills",
//       "Proficiency in MS Excel and documentation",
//       "Good communication and teamwork abilities",
//     ],
//     requirements: ["Bcom / Mcom / CA Inter / CAIRA Preferred"],
//     about_company: `CohnReznick LLP is a major U.S. accounting, tax, and advisory firm, formed by a 2012 merger, offering audit, tax, risk, and business performance services, known for deep industry expertise (like real estate, cannabis, renewable energy) and being part of the global Nexia network, providing support to various organizations with a focus on client growth, risk management, and value optimization. They're recognized for a strong culture, diversity, and serving diverse clients from Fortune 1000s to non-profits, with nationwide US offices and global reach via Nexia.`,
//   },
//   {
//     course: "CPA",
//     company: "Armanino Advisory LLC",
//     city: "Ahmedabad/Hyderabad",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/armanino.webp",
//     qualification: "CPA/CA / CAIRA Preferred",
//     location: "Ahmedabad/Hyderabad",
//     postion: "Audit Associate - Financial Statement",
//     experience:
//       "1 years of experience in Public Accounting ,audit engagements.",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: "Audit Associate - Financial Statement",
//     salary: "-",
//     key_responsibilities: [
//       "Perform audits, reviews, compilations, and various engagements.",
//       "Analyze financial statement data and draw logical conclusions.",
//       "Exercise professional skepticism in the critical assessment of audit evidence.",
//       "Research and analyze pertinent clients, industry, and technical matters.",
//       "Identify, assess, and document controls and weaknesses in client accounting systems.",
//       "Thoroughly evaluate assigned areas of financial statements and identify potential points for improvement.",
//       "Prepare clear and accurate workpapers.",
//       "Exhibit strong analytical skills to evaluate financial data and relationships.",
//       "Update managers and seniors of job status and assurance issues in timely manner.",
//       "Develop understanding of client businesses related to assigned assurance areas.",
//       "Know and apply specialized knowledge, for example, the rules, regulations, and code of ethics of the American Institute of Certified Public * * * Accountants (“AICPA”), be familiar with pronouncements of the Financial Accounting Standards Board (“FASB”) and the AICPA, and applicable state regulations.",
//       "Understand and comply with the Firm’s quality control policy.",
//       "Organize, analyze, and communicate with both fellow staff and client personnel in a professional, productive, and efficient manner.",
//       "Work toward completion of CPA certification.",
//       "Actively seek out feedback to develop self-awareness, personal strengths, and address development areas.",
//       "Uphold the firm’s code of ethics and business conduct.",
//     ],
//     note: "",
//     skills: [""],
//     requirements: [" CPA/CA / CAIRA Preferred"],
//     about_company: `Armanino Advisory LLC (part of Armanino LLP) is a large, independent U.S. accounting and business consulting firm, known for its tech-forward approach, helping clients with audit, tax, business management, and technology solutions, especially for privately held companies, nonprofits, and public entities, focusing on innovation and creating value beyond just compliance, even being a certified B Corp. They offer integrated services from strategy to implementation, serving industries like tech, healthcare, consumer goods, and more, with a strong emphasis on cloud tools and digital transformation.`,
//   },
//   {
//     course: "CPA",
//     company: "WithumSmith+Brown PC",
//     city: "Bangalore",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/withum.webp",
//     qualification: "CPA/CA / CAIRA Preferred",
//     location: "Bangalore",
//     postion: "Staff Level 1 Accountant",
//     experience: "Fresher",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: "Staff Level 1 Accountant",
//     salary: "-",
//     key_responsibilities: {
//       audit: {
//         title: "Audit",
//         items: [
//           "Along with a team supervised by a Manager, you will:",
//           "Analyse client accounting records and financial statements.",
//           "Evaluate internal accounting control systems, audit risk, materiality, and compliance with generally accepted auditing standards.",
//           "Create work papers that support the auditing work done.",
//           "Work on portions of larger audit and accounting engagements.",
//           "Manage miscellaneous tasks as assigned.",
//         ],
//       },

//       tax: {
//         title: "Tax",
//         items: [
//           "Prepare individual, trust, partnership and corporate tax returns respectively according to specific niche (1065, 1065 FS, 1120C, 1120S, 1040 HNI) & to be reviewed by Seniors and Managers.",
//           "Research and resolve tax issues.",
//         ],
//       },

//       key_skills: {
//         title: "Key skills",
//         items: [
//           "Desire to grow into engagementsenior position.",
//           "Experience and knowledge of U.S. Tax Code is preferred.",
//           "Strong command of the English Language, both written and verbal communication.",
//           "Strong commitment to an entrepreneurial work ethic.",
//           "Competently analyzes and prioritizesinformation to make appropriate recommendations.",
//           "Ability to work on multiple engagements simultaneously and communicate as a team with the Domestic (US) engagement teams.",
//         ],
//       },
//     },
//     note: "",
//     skills: [
//       "Desire to grow into engagementsenior position.",
//       "Experience and knowledge of U.S. Tax Code is preferred.",
//       "Strong command of the English Language, both written and verbal communication.",
//       "Strong commitment to an entrepreneurial work ethic.",
//       "Competently analyzes and prioritizesinformation to make appropriate recommendations.",
//       "Ability to work on multiple engagements simultaneously and communicate as a team with the Domestic (US) engagement teams.",
//     ],
//     requirements: ["CPA/CA / CAIRA Preferred"],
//     about_company: "",
//   },
//   {
//     course: "CPA",
//     company: "Accenture",
//     city: "Gurgaon",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/accenture.webp",
//     qualification: "CPA/CA / CAIRA Preferred",
//     location: "Gurgaon",
//     postion: "Multiple",
//     experience:
//       "4–10 years of experience in Record to Report / Core Accounting",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: "Multiple",
//     salary: "-",
//     key_responsibilities: [
//       "Financial Reporting & Analysis ",
//       "Budgeting & Forecasting ",
//       "SOX (Sarbanes-Oxley) compliance and internal controls.",
//       "Treasury & Cash Flow Management ",
//       "Stakeholder Management and SEC Reporting",
//       "ERP & Process Improvement",
//       "Regulatory & Statutory Compliance",
//       "Financial Planning & Analysis (FP&A)",
//     ],
//     note: "Shift: US Shift",
//     skills: [""],
//     requirements: ["CPA/CA / CAIRA Preferred"],
//     about_company: `Accenture is a leading global professional services company, headquartered in Dublin, Ireland, offering Strategy, Consulting, Technology, Operations, and Song (Interactive) services, focusing on Digital, Cloud, and Security for businesses, governments, and organizations across over 120 countries. With hundreds of thousands of employees and unmatched tech/industry expertise, Accenture helps clients innovate and transform using human ingenuity and advanced technologies like AI and Data.`,
//   },
//   {
//     course: "CPA",
//     company: "SS&C GlobeOp",
//     city: "Mumbai",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/sss_globeop.webp",
//     qualification: "CPA / CMA / CAIRA Preferred",
//     location: "Mumbai",
//     postion: "Associate - Fund Accounting",
//     experience: "0-3 years in Fund Accounting",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: "Associate - Fund Accounting",
//     salary: "-",
//     key_responsibilities: [
//       " Analyze of Daily/Monthly PNL & NAV",
//       "Perform Reconciliation and resolve the breaks.",
//       "Prepare NAV Pack & Investor Allocation.",
//       "Timely escalation.",
//       "Prepare of Financial Statements as per USGAAP / IFRS.",
//       "Handling audit queries in timely manner.",
//     ],
//     note: "",
//     skills: [""],
//     requirements: ["CPA / CMA / CAIRA Preferred"],
//     about_company: `SS&C GlobeOp is a major division of SS&C Technologies, serving as the world's largest fund administrator, providing comprehensive outsourcing, fund administration, technology, and regulatory services for hedge funds, private equity, and managed accounts, handling trillions in assets with expertise in reporting, compliance, and operations, known for their Hedge Fund Index offering transparency into market performance.`,
//   },
//   {
//     course: "CPA",
//     company: "Genpact",
//     city: "Hyderabad",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/genpact.webp",
//     qualification: "CAIRA Preferred",
//     location: "Hyderabad",
//     postion: "Multiple (Associate to AVP)",
//     experience: "2-15 years in Record To Report",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: "Multiple (Associate to AVP)",
//     salary: "-",
//     key_responsibilities: [
//       "US/European Shift - 5:30pm onwards Immediate Joiners preferred",
//     ],
//     note: "",
//     skills: [""],
//     requirements: ["CAIRA Preferred"],
//     about_company: `Genpact - WikipediaGenpact is a global professional services firm (NYSE: G) offering advanced tech, digital, and operations services (AI, data, cloud) to large enterprises, helping transform business processes in areas like finance, supply chain, and customer experience, known for its roots in GE and its focus on combining process expertise with cutting-edge digital solutions for measurable outcomes`,
//   },
//   {
//     course: "CMA",
//     company: "HPE",
//     city: "Mumbai",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/hpe.webp",
//     qualification:
//       "CMA + Graduation passout year 2023/2024/2025 / CAIRA Preferred",
//     location: "Mumbai",
//     postion: "Finance Intern (1year)",
//     experience: "Fresher",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: "Finance Intern (1year)",
//     salary: "-",
//     key_responsibilities: [
//       "Month-end and quarter-end financial close activities including processing and analysis of Journal entries",
//       "Balance sheet analysis including accounts reconciliations. ",
//       "Fixed Asset accounting and analysis",
//       "Treasury/cash accounting and reconciliations",
//       "Exposure and hands-on working knowledge of SAP S4 HANA",
//       "Exposure to International accounting practices and policies",
//       "Royalty and supply chain accounting",
//       "Review of P&L and Balance Sheet",
//     ],
//     note: "Internship Onboarding Date : Jan’26",
//     skills: [""],
//     requirements: [
//       "CMA + Graduation passout year 2023/2024/2025 / CAIRA Preferred",
//     ],
//     about_company: `Hewlett Packard Enterprise (HPE) is a global IT company focused on enterprise solutions, providing servers (ProLiant), storage, networking (Aruba), and hybrid cloud services via its GreenLake platform, helping businesses use AI, edge computing, and cloud to manage data and operations for digital transformation, serving sectors from healthcare to finance.  `,
//   },
//   {
//     course: "CMA",
//     company: "Befree",
//     city: "Vadodara | Chennai | Bangalore",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/befree.webp",
//     qualification: "CMA/MBA/Mcom / CAIRA Preferred",
//     location: "Vadodara | Chennai | Bangalore",
//     postion: " UK Paraplanning / Australian Paraplanning",
//     experience: "Fresher",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: " UK Paraplanning / Australian Paraplanning",
//     salary: "-",
//     key_responsibilities: {
//       tax: {
//         title: "A. Preparing and maintaining the client file :",
//         description:
//           "Generally the Financial Planner should collect the qualitative information (client attitudes,life goals etc) leaving the paraplanner to collect all the quantitative detailed information required to compile a financial plan. The Paraplanner may attend client meetings. Specific activities include:",
//         items: [
//           "Check all compliance paperwork is present",
//           "Confirm client risk profile is determined",
//           "Discuss client objectives with planner",
//           "Identify and obtain the information necessary to compile financial cash flow forecast",
//           "Compile draft net worth statement; income and expenditure statements and financial cash flow forecast and discuss with planner.",
//         ],
//       },
//       audit: {
//         title: "B. Preparing recommendations:",
//         description: "Identify areas for planning",
//         items: [
//           "Undertake research both independently and with the Financial Planner to identify suitable solutions to meet the client’s needs",
//           "Prepare information/comparisons for analysis by planner",
//           "Liaise with client’s providers.",
//           "Consider current and future allocation of assets for investment strategy with regard to client risk profile",
//           "Generate quotes and illustration online from respective providers",
//           "Generate quotes and illustration Online from respective providers",
//           "Prepare draft recommendation reports to be discussed/signed off by planner.",
//         ],
//       },
//       option_c: {
//         title: "C. Implementing recommendations:",
//         description:
//           "Prepare suitability letters in accordance with the agreed recommendations",
//         items: [
//           "Complete application / proposals forms",
//           "Ensure all compliance paperwork is in order",
//           "Make changes to clients’ investments as instructed",
//           "Implement the chosen investment strategies",
//           "Learn and execute the re-registration processes",
//         ],
//       },
//       option_d: {
//         title: "D. Review:",
//         description:
//           "Act as main point of contact and deal effectively with queries from clients and other parties through effective communication",
//         items: [
//           "Undertake Establish and build strong relationships with clients and attend client meetings with planner if required",
//           "Preparing client valuations",
//           "Organise future planning meetings with client",
//           "Review investment portfolio, asset allocation, risk profile etc",
//           "Send planning figures to client for update and revision in preparation for next meeting",
//           "Initiate review meetings with clients",
//           "Liaise with providers for fund valuations",
//         ],
//       },
//     },
//     note: [
//       "Shift Timing: 10:30AM To 7:30PM- UK Paraplanning",
//       "7:30AM To 4:30PM - Australian Paraplanning",
//     ],
//     skills: [""],
//     requirements: [
//       "Bachelor’s Degree in Accounting / CA / EA / CAIRA Preferred",
//     ],
//     about_company: `Befree (befree Global) is a worldwide leader in Finance & Accounting Business Process Outsourcing (BPO), providing tech-driven, scalable solutions for accountants, law firms, and businesses in Australia, UK, US, and India, offering services from bookkeeping and tax prep to payroll and virtual admin, helping firms boost efficiency and cut costs. Founded in 2006, they operate globally with strong hubs in India and support clients across various markets. `,
//   },
//   {
//     course: "CMA",
//     company: "Knowcraft Analytics",
//     city: "Ahmedabad | Indore",
//     company_logo:
//       "https://ik.imagekit.io/mileseducation/miles_website/jobs/knowcraft_analytics.webp",
//     qualification: "CMA / CAIRA Preferred",
//     location: "Ahmedabad | Indore",
//     postion: " Associate - US Accounting, Advance Associate - US Accounting",
//     experience: "0-2years in Accounting",
//     logo_first_letter: "-",
//     days: 5,
//     job_title: " Associate - US Accounting, Advance Associate - US Accounting",
//     salary: "-",
//     key_responsibilities: [""],
//     note: "",
//     skills: [""],
//     requirements: ["CMA / CAIRA Preferred"],
//     about_company: `Knowcraft Analytics is an Indian professional services firm, established 2012, providing offshore accounting, valuation, tax, and advisory services (including investment banking support) primarily for US and Canadian financial firms, known for its expert team (CAs, CPAs, etc.), ISO 27001 certification, and focus on quality, cost-efficiency, and strategic partnerships, with an associated learning platform for analytics skills.`,
//   },
// ];
